import { useCallback, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import type { SimulationRendererProps, RosterOrganism, BodyTypeSchema, GeomDef, MeshGeomDef, MeshData } from '../types/simulation';
import { FrozenTreeStore, FROZEN_RECORD_BYTES } from './frozenTrees';
import { SunShadows } from './csmShadows';
import { PalmShadowCasters } from './palmRecipe';
import type { PalmRecipesMessage } from './palmRecipe';
import { TerrainView } from './terrainView.ts';
import type { GroundInfo } from './terrainView.ts';
import { GroundClip } from './groundClip';
import { GroundAssembler, isGroundFrame } from './groundWire';
import { CanopyAssembler, isCanopyFrame } from './canopyWire';
import { CanopyClip } from './canopyClip';
import { RockView } from './rocks';
import { WindField, WindMessage } from './wind';
import { Sky, twilightGlowGain } from './sky.ts';
import type { WeatherMessage } from './sky.ts';
import { air } from './air';
import type { AirMapMessage } from './air';
import { planet, curvedDisc, horizonDistance, EARTH_RADIUS_M, RADIUS_MIN_M, RADIUS_MAX_M } from './planet';
import { origin, parseOriginMode } from './origin';
import { CloudCells } from './cloudCells.ts';
import type { CloudCellsMessage, CellMode } from './cloudCells.ts';
import { CloudVolume, SHADE_DEFAULT, FAR_DEFAULT, STEEP_DEFAULT, DETAIL_DEFAULT } from './cloudVolume.ts';
import { CloudShadow } from './cloudShadow';
import type { CloudWinMessage, VolumeQuality } from './cloudVolume.ts';
import { VolumeAssembler, isVolumeFrame } from './cloudVolumeWire.ts';
import { AmbientAudio, isAudioFrame } from './ambientAudio';
import { WorldTileAssembler, isWorldFrame, worldTiles } from './worldTiles';
import { GrassView } from './grass';
import { EcoAssembler, EcoStore, isEcoFrame } from './ecoWire';
import { EcoView } from './ecoPlants';
import { WaterView } from './waterView';
import type { SeaState } from './waterSpectrum';
import { TideState } from './waterState';
import type { WaterStateMessage } from './waterState';
import { tideOffset } from './tide';
import { CanopyShade, CANOPY_SHADE_DECL, canopyShadeApply } from './canopyShade';
import type { CanopyLightMessage } from './canopyShade';
import type { RocksMessage } from './rocks';
import { EXPOSURE, exposureGain, installNightToneMapping, scotopicShift } from './exposure';
import { StarField } from './stars';
import { StarProbe } from './starProbe';
import { SkyLight } from './skyLight';
import { cloudOptics, LIQUID } from './cloudOptics';
import type { StarsMessage } from './stars';
import { Starlight } from './starlight';
import { GalaxyGlow } from './galaxy';
import type { GlowMessage } from './galaxy';
import { MoonView } from './moon';
import { Space } from './space';
import { Atmosphere } from './atmospherePhysics';
import { eyeLight } from './eyeLight';
import { FREE_ABOVE_M } from './bodies';
import type { BodiesMsg } from './bodies';
import type { Drive } from './flight';
import { Lattice } from './lattice';
import { fmtDist, fmtSpeed, fmtTime, latticeInputs, navInfo } from './nav';
import { SunGlare, makeSunDiscMaterial } from './sunGlare';
import { installPerfProfiler, layerGroup } from './perfProfile';
import { TunePanel } from './tunePanel';

const MAX_FIXTURES = 4096;
const MAX_SITES    = 512;
const RECORD_BYTES = 43;  // 23 base + 16 quaternion + 4 geom_scale
const PARTICLE_RECORD_BYTES = 27;     // slot(4)+owner(4)+xyz(12)+radius(4)+rgb(3) — slot widened u16->u32
const PARTICLE_SUBHEADER_BYTES = 13;  // flags(1)+a(4)+b(4)+state_hash(4): same layout as the frozen section
const PARTICLE_RK_COOLDOWN  = 30;     // min frames between keyframe re-requests (retries a lost keyframe)
const MAX_FROZEN            = 2621440; // frozen (LOD unit) cells cap; MUST match ecology/frozen_field.MAX_FROZEN
const FROZEN_SUBHEADER_BYTES = 13;    // flags(1)+added_count(4)+removed_count(4)+cid_set_hash(4)
// FROZEN_RECORD_BYTES (36) lives in frozenTrees.ts next to the parser that reads it.
const FROZEN_RK_COOLDOWN     = 30;    // min frames between frozen keyframe re-requests

// CRC32 (IEEE, reflected) — byte-identical to Python's zlib.crc32, so the client can
// verify its particle state against the server's embedded hash.
const _CRC32_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
    t[n] = c >>> 0;
  }
  return t;
})();
function crc32(bytes: Uint8Array): number {
  let c = 0xFFFFFFFF;
  for (let i = 0; i < bytes.length; i++) c = (_CRC32_TABLE[(c ^ bytes[i]) & 0xFF] ^ (c >>> 8)) >>> 0;
  return (c ^ 0xFFFFFFFF) >>> 0;
}
// crc32 of a cid as 4 little-endian bytes — matches Python zlib.crc32(struct.pack("<I", cid)),
// the unit the frozen cid-set hash is accumulated from on both ends.
const _cidBuf = new Uint8Array(4);
function crc32Cid(cid: number): number {
  _cidBuf[0] = cid & 0xFF; _cidBuf[1] = (cid >>> 8) & 0xFF;
  _cidBuf[2] = (cid >>> 16) & 0xFF; _cidBuf[3] = (cid >>> 24) & 0xFF;
  return crc32(_cidBuf);
}
// Render-cost controls (see the quality ladder and the debug overlay in the render effect).
const PIXEL_RATIO_CAP   = 1.5;    // never render more than this many pixels per CSS pixel
const FRAME_BUDGET_MS   = 10;     // max time spent decoding queued frames in one animation frame
const MAX_QUEUED_FRAMES = 600;    // 20 s of frames at 30 fps: past this the client is hopelessly behind, so drop and resync from a keyframe (must outlast first-frame shader compiles, which stall the loop for seconds)
const SLOW_FRAME_MS     = 22;     // smoothed frame time above this steps quality down (about 45 fps)
const FAST_FRAME_MS     = 17.5;   // below this (vsync-limited 60 fps) for a while steps quality back up
const PICK_INTERVAL_MS  = 100;    // crosshair hover raycast rate
const SHADOW_REFRESH_MS = 500;    // shadow map is also refreshed this often even if nothing seems to have moved
// Beach island (worlds whose terrain dips below the sea level; see waterView.ts and tide.ts).
const TERR_EVERY_MS     = 100;    // least time between two ground-window position messages
const TERR_STEP_M       = 0.25;   // ... and the least movement that is worth one
const SEA_DRY_MARGIN    = 0.2;    // grass grows above the high-tide mark plus this many metres
const WET_DRY_TAU_S     = 20;     // seconds the wet-sand band takes to fall most of the way to a lower tide
const FOG_AIR_DENSITY   = 0;      // three's exp2 fog density in air: none. The air is air.ts (the weather model's extinction along the ray, replacing this fog's chunks); this density is the underwater medium's only.
const FOG_UNDER_DENSITY = 0.075;  // and with the eye under the sea: about 20 m of visibility
const UNDER_FADE_M      = 0.3;    // metres of depth over which the underwater look fades in at the surface
// Quality ladder: resolution scale (on top of the pixel-ratio cap) and the share of grass drawn.
const QUALITY_LEVELS = [
  // fine: reach of the ground's fine bump. skyK: the sky dome's drawing size, relative to its base scale.
  // sea: the sea's cost level (0 full ray-marched surface and FFT every frame, 1 lighter, 2 flat plane and a small FFT grid).
  { res: 1.00, grass: 1.00, sky: 2, fine: 1.00, skyK: 1.00, sea: 0 },
  { res: 0.85, grass: 1.00, sky: 2, fine: 1.00, skyK: 1.00, sea: 0 },
  { res: 0.75, grass: 0.85, sky: 1, fine: 0.75, skyK: 1.00, sea: 1 },
  { res: 0.65, grass: 0.70, sky: 1, fine: 0.60, skyK: 1.00, sea: 1 },
  { res: 0.60, grass: 0.50, sky: 0, fine: 0.45, skyK: 0.90, sea: 2 },
  { res: 0.50, grass: 0.35, sky: 0, fine: 0.35, skyK: 0.80, sea: 2 },
];
const _dummy = new THREE.Object3D();

// Props: live substrate particles whose owner_id carries a flag in its high bits. They are real physics objects,
// unlike plant cells, which are not drawn.
//  - Fruit (ecology FRUIT_OWNER_FLAG, bit 31): hangs pinned in a crown while it ripens, then falls and rolls.
//    Small fruit is unripe: colour blends from green to the wire colour as the radius grows.
//  - Frond debris (FROND_OWNER_FLAG, bit 30): ONE particle for a whole frond the wind tore off a palm (the
//    frond's cells left the frozen field). Its radius is length / 12, so the frond card is drawn 12 radii long,
//    lying flat just above the ground, turning along its direction of travel and fluttering while it moves.
const MAX_FRUIT = 8192;
const MAX_FROND_DEBRIS = 2048;
const FRUIT_OWNER_FLAG = 0x80000000;
const PROP_OWNER_MIN = 0x40000000;   // owners at or above this are drawn props (fronds, then fruit)
const FRUIT_MIN_RADIUS = 0.03;       // radius at which a fruit is fully green
const FRUIT_RIPE_RADIUS = 0.10;      // radius at which it shows its wire (husk) colour
const FRUIT_GREEN: [number, number, number] = [0.38, 0.56, 0.24];
const FROND_LENGTH_PER_RADIUS = 12;
type FruitRec = [number, number, number, number, number, number, number, number];   // sim x, y, z, radius, r, g, b (0..255), owner_id
const _fruitColor = new THREE.Color();

/** A unit-length pinnate frond in the x-z plane (rachis along +x, -0.5..0.5), leaflets sweeping toward the tip and
 *  drooping a little, so it reads as a palm leaf from above. Double-sided; scale it by the frond's length. */
function makeFrondGeometry(): THREE.BufferGeometry {
  const pos: number[] = [];
  const N = 14;
  const tri = (a: number[], b: number[], c: number[]) => { pos.push(...a, ...b, ...c); };
  tri([-0.5, 0, -0.006], [0.5, 0, -0.006], [0.5, 0, 0.006]);
  tri([-0.5, 0, -0.006], [0.5, 0, 0.006], [-0.5, 0, 0.006]);
  for (let i = 0; i < N; i++) {
    const u = (i + 0.5) / N;
    const x = -0.5 + u;
    const w = 0.02 + 0.17 * Math.pow(Math.sin(Math.PI * Math.min(0.97, 0.08 + 0.9 * u)), 0.8);
    const tipX = x + 0.07;
    for (const side of [-1, 1]) {
      tri([x - 0.012, 0, 0], [x + 0.012, 0, 0], [tipX, -0.045 * w / 0.19, side * w]);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.computeVertexNormals();
  return g;
}

// Per-frond motion state for the card's heading and flutter: slot -> last position and drawn yaw / flutter.
const _frondState = new Map<number, { x: number; y: number; yaw: number; wob: number }>();

/** Rebuild the fruit and frond instances from the live prop set. Called only when a frame changed it. */
function flushProps(fruit: THREE.InstancedMesh, frond: THREE.InstancedMesh, live: Map<number, FruitRec>): void {
  let n = 0;
  let nf = 0;
  const tsec = performance.now() * 0.001;
  for (const [slot, f] of live) {
    if (f[7] >= FRUIT_OWNER_FLAG) {
      if (n >= MAX_FRUIT) continue;
      _dummy.position.set(f[0], f[2], -f[1]);                       // sim(x,y,z) -> three(x,z,-y)
      _dummy.rotation.set(0, 0, 0);
      _dummy.scale.set(f[3], f[3] * 1.15, f[3]);                    // a coconut is a little taller than wide
      _dummy.updateMatrix();
      fruit.setMatrixAt(n, _dummy.matrix);
      const t = Math.min(1, Math.max(0, (f[3] - FRUIT_MIN_RADIUS) / (FRUIT_RIPE_RADIUS - FRUIT_MIN_RADIUS)));
      _fruitColor.setRGB(
        FRUIT_GREEN[0] + (f[4] / 255 - FRUIT_GREEN[0]) * t,
        FRUIT_GREEN[1] + (f[5] / 255 - FRUIT_GREEN[1]) * t,
        FRUIT_GREEN[2] + (f[6] / 255 - FRUIT_GREEN[2]) * t,
      );
      fruit.setColorAt(n, _fruitColor);
      n++;
    } else {
      if (nf >= MAX_FROND_DEBRIS) continue;
      let st = _frondState.get(slot);
      if (!st) { st = { x: f[0], y: f[1], yaw: slot * 2.399963, wob: 0 }; _frondState.set(slot, st); }
      const dx = f[0] - st.x, dy = f[1] - st.y;
      const d = Math.hypot(dx, dy);
      if (d > 0.004) {
        // Turn the long axis toward the direction of travel (sim (dx, dy) = three (dx, -dy); about Y that is atan2(dy, dx)).
        const target = Math.atan2(dy, dx);
        let diff = target - st.yaw;
        diff = ((diff + Math.PI / 2) % Math.PI + Math.PI) % Math.PI - Math.PI / 2;     // a frond is symmetric: take the short way to either end
        st.yaw += diff * 0.15;
      }
      st.wob += (Math.min(0.45, d * 8) - st.wob) * 0.25;                               // flutter dies away as it comes to rest
      st.x = f[0]; st.y = f[1];
      const len = f[3] * FROND_LENGTH_PER_RADIUS;
      _dummy.position.set(f[0], f[2] - 0.8 * f[3], -f[1]);
      _dummy.rotation.set(st.wob * Math.sin(tsec * 9 + slot), st.yaw, st.wob * Math.cos(tsec * 7 + slot * 1.7), 'YXZ');
      _dummy.scale.set(len, len, len);
      _dummy.updateMatrix();
      frond.setMatrixAt(nf, _dummy.matrix);
      _fruitColor.setRGB(f[4] / 255, f[5] / 255, f[6] / 255);
      frond.setColorAt(nf, _fruitColor);
      nf++;
    }
  }
  if (_frondState.size > live.size + 32) {
    for (const k of Array.from(_frondState.keys())) if (!live.has(k)) _frondState.delete(k);
  }
  fruit.count = n;
  fruit.instanceMatrix.needsUpdate = true;
  if (fruit.instanceColor) fruit.instanceColor.needsUpdate = true;
  frond.count = nf;
  frond.instanceMatrix.needsUpdate = true;
  if (frond.instanceColor) frond.instanceColor.needsUpdate = true;
}
const _color = new THREE.Color();
const _skyColor = new THREE.Color();
const _underCol = new THREE.Color();                 // the sea's colour seen from inside: sky pulled toward teal
const _underTint = new THREE.Color(0.12, 0.55, 0.65);
const _fogMix = new THREE.Color();
const _glitSunK = [0, 0, 0], _glitMoonK = [0, 0, 0];   // sea glitter strengths (see waterView.ts)
const _glitMoonDir = new THREE.Vector3();
const _drawSize = new THREE.Vector2();
new THREE.Vector3();
const _fwd   = new THREE.Vector3();
const _navV  = new THREE.Vector3();
const _earDir = new THREE.Vector3();   // the camera's forward direction, for the ambient sound's heading

// Touch controls (phones and tablets, landscape). See the touch block in the render effect.
const TOUCH_WALK_SPEED  = 8;      // full-deflection walking speed, world units / second (desktop walk is 20; there is no touch sprint)
const TOUCH_ACCEL_TAU   = 0.3;    // seconds for walking speed to ease up toward the stick (a ramp, not an instant start)
const TOUCH_STOP_TAU    = 0.1;    // seconds for it to ease down when the stick is released or eased off (short, so stopping is quick)
const TOUCH_ZOOM_MAX    = 4;      // pinch zoom limit, as magnification of the view (1 = normal)
const ZOOM_EASE_S       = 0.08;   // seconds of smoothing on the zoom, so a pinch does not step the view
const TOUCH_TAP_MS      = 250;    // a touch shorter than this, and nearly still, is a tap
const TOUCH_DOUBLE_TAP_MS = 320;  // two taps within this reset the zoom
const TOUCH_TAP_SLOP    = 12;     // px a finger may wander and still count as a tap
const TOUCH_ZONE_LEFT   = 1 / 3;  // touch zones, as fractions of the view width: left of this walks,
const TOUCH_ZONE_RIGHT  = 2 / 3;  // right of this turns, and in between is pinch zoom
const TOUCH_DEADZONE    = 0.12;   // stick deflection (0 to 1) ignored around the centre
const TOUCH_STICK_R     = 56;     // px of thumb travel for full deflection
const TOUCH_LOOK_SENS   = 0.0045; // radians of turn per pixel of drag on the right half
const GYRO_EASE_S       = 0.05;   // seconds of smoothing on tilt, so sensor noise does not shake the view

// True on devices whose primary input is a finger (the layout and controls below assume that).
function detectTouch(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(pointer: coarse)').matches && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
}
const wrapPi = (a: number) => Math.atan2(Math.sin(a), Math.cos(a));
const clampPitch = (p: number) => Math.max(-Math.PI / 2 + 0.01, Math.min(Math.PI / 2 - 0.01, p));

// The world's day length and start hour, from the "spin" block of the config (found wherever the config
// nests it). Without it there is no clock.
interface SpinInfo { ticksPerDay: number; startHour: number; latitudeDeg: number }
function findSpin(obj: unknown, depth = 0): SpinInfo | null {
  if (!obj || typeof obj !== 'object' || depth > 6) return null;
  if (Array.isArray(obj) && obj.length > 64) return null;
  const o = obj as Record<string, any>;
  const s = o.spin;
  if (s && typeof s === 'object' && typeof s.ticks_per_day === 'number' && s.ticks_per_day > 0) {
    return { ticksPerDay: s.ticks_per_day, startHour: typeof s.start_hour === 'number' ? s.start_hour : 0, latitudeDeg: typeof s.latitude_deg === 'number' ? s.latitude_deg : 40 };   // 40 is the server's default patch latitude
  }
  for (const k of Object.keys(o)) {
    const r = findSpin(o[k], depth + 1);
    if (r) return r;
  }
  return null;
}
const hhmm = (h: number) => {
  const hh = Math.floor(h);
  const mm = Math.min(59, Math.floor((h - hh) * 60));
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};
// 24-hour HH:MM at a sim tick: the start hour plus the fraction of the day elapsed. This is the world's spin clock (its sidereal
// day), which is NOT local solar time: the world also orbits, so the sun comes round about ticksPerDay / ticksPerYear of a day later
// each day, and this clock drifts away from the sun (by 24 hours over a year). Used only until the first sun message arrives.
function clockText(tick: number, spin: SpinInfo): string {
  const h = (((spin.startHour + 24 * (tick % spin.ticksPerDay) / spin.ticksPerDay) % 24) + 24) % 24;
  return hhmm(h);
}
// Local solar time from the sun's direction in the local frame (x east, y north, z up): 12:00 when the sun is on the meridian, then
// 24 hours of hour angle round the spin axis. The spin axis in the local frame is fixed by the patch latitude alone: it lies in
// the north-up plane, (0, cos lat, sin lat) (latitude is positive north and north leans toward the axis; the world's east is axis x up, so the axis has no east part
// and its angle to up is 90 degrees minus the latitude), whatever the axial tilt is. Null when the geometry is degenerate (latitude +-90).
function solarClockText(dir: ArrayLike<number>, spin: SpinInfo): string | null {
  const lat = spin.latitudeDeg * Math.PI / 180;
  const ay = Math.cos(lat), az = Math.sin(lat);                   // the spin axis, local
  const uy = -az * ay, uz = 1 - az * az;                  // up with the axis taken out
  const un = Math.hypot(uy, uz);
  if (un < 1e-6) return null;
  const sd = dir[1] * ay + dir[2] * az;                           // sun . axis
  const sx = dir[0], sy = dir[1] - sd * ay, sz = dir[2] - sd * az;
  const west = -sx, along = (sy * uy + sz * uz) / un;             // the sun's hour angle, from the meridian (west positive)
  if (Math.hypot(west, along) < 1e-9) return null;
  const h = ((12 + Math.atan2(west, along) * 12 / Math.PI) % 24 + 24) % 24;
  return hhmm(h);
}

// Coordinate transform: sim(x,y,z) → Three.js(x,z,-y)  =  rotation -90° around X
// Used to convert both positions and quaternions from sim space to Three.js space.
const COORD_QUAT     = new THREE.Quaternion(-Math.SQRT1_2, 0, 0, Math.SQRT1_2); // R_x(-90°)
const COORD_QUAT_INV = new THREE.Quaternion( Math.SQRT1_2, 0, 0, Math.SQRT1_2); // R_x(+90°)

function simQuatToThree(qw: number, qx: number, qy: number, qz: number): THREE.Quaternion {
  const q = new THREE.Quaternion(qx, qy, qz, qw); // THREE uses (x,y,z,w)
  return COORD_QUAT.clone().multiply(q).multiply(COORD_QUAT_INV);
}

// Same conversion into an existing quaternion (COORD * q * COORD^-1), for the per-frame path,
// which otherwise allocated three quaternions per body segment per frame.
function simQuatToThreeInto(out: THREE.Quaternion, qw: number, qx: number, qy: number, qz: number): THREE.Quaternion {
  return out.set(qx, qy, qz, qw).premultiply(COORD_QUAT).multiply(COORD_QUAT_INV);
}

function b64ToFloat32(b64: string): Float32Array {
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return new Float32Array(buf.buffer);
}

function b64ToInt32(b64: string): Int32Array {
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return new Int32Array(buf.buffer);
}

// Build a Three.js BufferGeometry from a MuJoCo geom definition.
// Geometry is defined in the body's Three.js local frame (after coord transform).
// Axes after coord transform: local-x=sim-x, local-y=sim-z, local-z=-sim-y
function makeGeomGeometry(geom: GeomDef, meshes?: Record<string, MeshData>): THREE.BufferGeometry {
  switch (geom.type) {
    case 'sphere': {
      const [s0] = geom.size;
      return new THREE.SphereGeometry(s0, 12, 8);
    }
    case 'capsule': {
      // MuJoCo capsule: axis along body-local z → Three.js local y after coord transform
      // size[0]=radius, size[1]=half-length of cylindrical part
      const [s0, s1] = geom.size;
      return new THREE.CapsuleGeometry(s0, s1 * 2, 4, 12);
    }
    case 'cylinder': {
      // Same axis mapping as capsule
      const [s0, s1] = geom.size;
      return new THREE.CylinderGeometry(s0, s0, s1 * 2, 12);
    }
    case 'box': {
      // size[0,1,2] = half-extents along sim x,y,z
      // → Three.js local: x=s0, y=s2, z=s1 (after coord transform)
      const [s0, s1, s2] = geom.size;
      return new THREE.BoxGeometry(s0 * 2, s2 * 2, s1 * 2);
    }
    case 'ellipsoid':
      return new THREE.SphereGeometry(1, 12, 8);
    case 'mesh': {
      const g = geom as MeshGeomDef;
      const meshData = meshes?.[g.mesh_key];
      if (!meshData) return new THREE.SphereGeometry(0.05, 6, 4); // fallback if schema incomplete
      const verts = b64ToFloat32(meshData.verts_b64);
      const faces  = b64ToInt32(meshData.faces_b64);
      // Transform vertices from sim Z-up (x,y,z) → Three.js Y-up (x,z,-y)
      for (let i = 0; i < verts.length; i += 3) {
        const vy = verts[i + 1], vz = verts[i + 2];
        verts[i + 1] = vz;
        verts[i + 2] = -vy;
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(verts, 3));
      geo.setIndex(new THREE.BufferAttribute(new Uint32Array(faces.buffer), 1));
      geo.computeVertexNormals();
      return geo;
    }
    default:
      return new THREE.SphereGeometry(0.05, 6, 4);
  }
}

function makeGeomMesh(geom: GeomDef, meshes?: Record<string, MeshData>): THREE.Mesh {
  const geo = makeGeomGeometry(geom, meshes);
  const [, , , a] = geom.rgba;
  const mat = new THREE.MeshStandardMaterial({
    color: new THREE.Color(0.90, 0.48, 0.15),
    roughness: 0.65,
    metalness: 0.05,
    transparent: a < 0.99,
    opacity: a,
  });
  const mesh = new THREE.Mesh(geo, mat);

  // Local position: apply coord transform to geom_pos (sim body-local)
  const [px, py, pz] = geom.pos;
  mesh.position.set(px, pz, -py);

  // Local orientation: apply coord transform to geom_quat
  const [qw, qx, qy, qz] = geom.quat;
  mesh.quaternion.copy(simQuatToThree(qw, qx, qy, qz));

  // Ellipsoid: scale the unit sphere by semi-axes
  if (geom.type === 'ellipsoid') {
    const [s0, s1, s2] = geom.size;
    mesh.scale.set(s0, s2, s1); // sim-x→three-x, sim-z→three-y, sim-y→three-z
  }

  return mesh;
}

// Build one Three.js Group for a single body segment, containing all its geom meshes.
function buildSegmentGroup(segment: { name?: string; geoms: GeomDef[] }, meshes?: Record<string, MeshData>): THREE.Group {
  const group = new THREE.Group();
  for (const geom of segment.geoms) {
    if (geom.type === 'mesh') {
      const key = (geom as MeshGeomDef).mesh_key;
      const md = meshes?.[key];
      console.log(`[sim] mesh geom key=${key} found=${!!md} verts=${md ? b64ToFloat32(md.verts_b64).length / 3 : 0}`);
    }
    group.add(makeGeomMesh(geom, meshes));
  }
  return group;
}

export default function DefaultSimRenderer({
  broadcastUrl,
  config,
  selectedEntityId,
  hoveredEntityId,
  snapToEntityId,
  onOrganismClick,
  onOrganismHover,
}: SimulationRendererProps) {
  const [broadcastOffline, setBroadcastOffline] = useState(false);
  const [webglUnavailable, setWebglUnavailable] = useState(false);
  const [isLocked, setIsLocked] = useState(false);  // pointer-lock (FPS) state, for the crosshair/hint overlay
  const [isFreeRoam, setIsFreeRoam] = useState(false);  // free roam (fly) on: the camera leaves the ground, for the hint overlay
  const [flyScale, setFlyScale] = useState(1);             // free-roam speed scale (the wheel), for the overlay
  const [isGravity, setIsGravity] = useState(false);        // the thrusters are on out in space (true: the observer has a velocity under the bodies' pulls, the keys push) or the keys move the camera directly (false), for the overlay
  const [isNav, setIsNav] = useState(false);                // the navigation overlay (N): readout and Lagrange points
  const [isLattice, setIsLattice] = useState(false);        // the gravitational lattice (L)
  const navHudRef = useRef<HTMLPreElement>(null);
  const navMarksRef = useRef<HTMLDivElement>(null);
  const [inSpaceHud, setInSpaceHud] = useState(false);      // the observer is out in space (free regime), for the overlay
  const panelHiddenRef = useRef(false);               // H hides the page chrome, header and left pane (the viewer's own overlays stay)
  const [isTouchDevice] = useState(detectTouch);      // finger-first device: touch controls on, keyboard and pointer lock off
  const [isPortrait, setIsPortrait] = useState(false); // touch devices only: portrait shows a "rotate to landscape" cover
  const touchHintRef = useRef<HTMLDivElement>(null);  // control hint on touch devices, hidden at the first touch
  const clockRef = useRef<HTMLSpanElement>(null);     // the 24-hour sim clock beside the tick counter
  const spinRef = useRef<SpinInfo | null>(null);      // day length and start hour from the config (null = no clock)

  const mountRef   = useRef<HTMLDivElement>(null);
  const tickDisplayRef = useRef<HTMLSpanElement>(null);
  const tickRateRef    = useRef<HTMLSpanElement>(null);
  const fpsRef         = useRef<HTMLSpanElement>(null);          // the frame rate readout beside TPS
  const wsRef      = useRef<WebSocket | null>(null);
  const rosterRef  = useRef<RosterOrganism[]>([]);
  const siteRegistryRef = useRef<string[]>([]);
  // Frames wait here until the render loop decodes them, each exactly once and in order. (It used
  // to keep only the newest buffer and re-decode it on every animation frame, which repeated the
  // work 2 to 5 times at 60 to 144 Hz against a 30 fps stream, and lost a delta whenever two
  // frames landed between animation frames, which the hash audit then answered with a keyframe.)
  const frameQueueRef = useRef<{ buf: ArrayBuffer; view: DataView }[]>([]);
  const rosterByIdxRef = useRef(new Map<number, RosterOrganism>());
  const shadowDirtyRef = useRef(true);                                 // a message changed what casts shadows
  const instanceIdToOrgIdxRef = useRef(new Map<number, number>());

  // Latest sun state from the world server (via broadcast 'sun' message).
  // Angles are radians; irradiance triples are linear RGB where 1.0 is top-of-atmosphere
  // flux at the reference distance. `directRgb` is the atmosphere-filtered beam normal
  // to the ray; `skyRgb` is diffuse light on an open horizontal surface.
  const simTpsRef = useRef(100);                         // the sim's actual tick rate, from the stats message
  const sunMsgCountRef = useRef(0);                      // sun messages received, for the debug overlay
  const sunRef = useRef<{
    direction: [number, number, number];
    elevation: number;
    angularDiameter: number;
    directRgb: [number, number, number];
    skyRgb: [number, number, number];
    moonOpenRgb?: [number, number, number];              // set with the atmosphere's tables: `moon.rgb` is then the moon's beam through the air and no cloud (the client dims it by the cloud it draws)
    clearRgb?: [number, number, number];                 // set with the atmosphere's tables: the beam with the atmosphere only (no cloud), the sun's own strength at the eye; directRgb is the same until the cloud is taken through it
    atm?: { open: number[]; floor: number[]; mh: number; sc: number; tbh: number };   // set with the atmosphere's tables (eyeLight.ts): the pieces of the cloud filter, which the client applies to the optical depths it works through the volume (skyLight.ts)
    isDay: boolean;
    flux?: number;                                       // the star's flux outside the atmosphere (the sky dome lights its clouds with it)
    tick?: number;                                       // sim tick this state is for
    receivedAt?: number;                                 // performance.now() when the message arrived
    // For placing the catalog's stars and the moon. Absent from an older server.
    worldPos?: [number, number, number];                 // the world's universe-space position
    frame?: number[];                                    // 3x3 row-major: east, north, up in universe space
    moon?: { direction: [number, number, number]; angularDiameter: number; phase: number; rgb: [number, number, number] } | null;
  } | null>(null);

  // MJCF body schema state
  const bodySchemaRef    = useRef<Record<string, BodyTypeSchema>>({});
  // entity_id → array of Three.js Groups (one per fixture slot / body segment)
  const entityGroupsRef  = useRef<Map<string, THREE.Group[]>>(new Map());
  // fixture slot → { entityId, segIdx } for schema-rendered slots
  const slotToEntityRef  = useRef<Map<number, { entityId: string; segIdx: number }>>(new Map());
  // Reference to the scene for adding/removing groups (set during scene setup)
  const sceneRef         = useRef<THREE.Scene | null>(null);
  const fixturesRef      = useRef<THREE.InstancedMesh | null>(null); // sphere fallback
  const fruitRef         = useRef<THREE.InstancedMesh | null>(null); // fruit (live particles flagged in owner_id)
  const frondRef         = useRef<THREE.InstancedMesh | null>(null); // frond debris (live particles flagged in owner_id bit 30)
  const fruitLiveRef     = useRef<Map<number, FruitRec>>(new Map());  // particle slot -> prop record (fruit and frond debris)
  const terrainRef       = useRef<TerrainView | null>(null);         // the ground on screen: the rings of the server's height windows (terrainView.ts)
  const groundInfoRef    = useRef<GroundInfo | null>(null);          // last ground_info (sea level, tide amplitude), for a view built after it arrived
  const groundClipRef    = useRef<GroundClip | null>(null);          // the height windows the server streams (groundClip.ts); outlives the views
  if (groundClipRef.current === null) groundClipRef.current = new GroundClip();
  const canopyClipRef    = useRef<CanopyClip | null>(null);          // the far canopy rasters the server streams (canopyClip.ts); outlives the views
  if (canopyClipRef.current === null) canopyClipRef.current = new CanopyClip();
  const terrSentRef      = useRef({ t: -1e9, x: NaN, y: NaN, h: NaN });   // where the server was last told we stand, for the ground windows (tile metres; h above the ground)
  const grassRef         = useRef<GrassView | null>(null);           // cosmetic wind-blown grass
  const ecoStoreRef      = useRef<EcoStore | null>(null);            // the ecology's plant windows the server streams (ecoWire.ts); outlives the views
  if (ecoStoreRef.current === null) ecoStoreRef.current = new EcoStore();
  const ecoViewRef       = useRef<EcoView | null>(null);             // the plants they make, drawn (ecoPlants.ts)
  const canopyShadeRef   = useRef<CanopyShade | null>(null);         // plant-canopy shading map for ground and grass
  const canopyLightMsgRef = useRef<CanopyLightMessage | null>(null);  // last canopy_light message, for a view built after it arrived
  const palmMsgRef       = useRef<PalmRecipesMessage | null>(null);  // latest palm recipes from the broadcast server (the casters may not exist yet)
  const palmCastersRef   = useRef<PalmShadowCasters | null>(null);  // palm leaflets, drawn only into the sun's shadow maps
  const windMsgRef       = useRef<WindMessage | null>(null);         // latest server wind message (the field may not exist yet)
  const windRef          = useRef<WindField | null>(null);           // shared wind (trees, live tips, grass)
  const cloudCellsMsgRef = useRef<CloudCellsMessage | null>(null);   // latest cloud_cells message (the cycle's clouds as tracked objects)
  const cloudCellsRef    = useRef<CloudCells | null>(null);          // the cells' renderer (cloudCells.ts): puff clusters or raymarched volumes
  const cloudWinMsgsRef  = useRef<Map<number, CloudWinMessage>>(new Map());   // the latest cloud_win message of each level (the cloud in nested windows round the eye: 0..3 the rings, 4 the world), kept for a rebuilt scene
  const cloudVolRef      = useRef<CloudVolume | null>(null);         // its renderer (cloudVolume.ts); while it draws, the dome's own low layer stands down
  const weatherMsgRef    = useRef<WeatherMessage | null>(null);      // latest server weather message (cloud, haze), for a sky built after it arrived
  const atmosphereRef    = useRef<Atmosphere | null>(null);          // the world's air as the server solved it (atmospherePhysics.ts): the light at the eye and the sky are read from it where the observer is
  const skyRef           = useRef<Sky | null>(null);                 // the sky: one full-screen pass over the sky-view table (sky.ts)
  const waterRef         = useRef<WaterView | null>(null);           // the sea (absent or hidden when the terrain has no ground below sea level)
  const tideRef          = useRef<TideState>(new TideState());       // the server's tide and sea state, eased (waterState.ts); outlives the views
  const rocksRef         = useRef<RockView | null>(null);            // static boulders (absent = none)
  const rocksMsgRef      = useRef<RocksMessage | null>(null);        // last rocks message, for a view built after it arrived
  const starFieldRef     = useRef<StarField | null>(null);           // the night sky's catalog stars
  const starlightRef     = useRef<Starlight | null>(null);          // the stars message's starlight floor, by spin angle
  const starsMsgRef      = useRef<StarsMessage | null>(null);        // last stars message, for a view built after it arrived
  const bodiesMsgRef     = useRef<BodiesMsg | null>(null);           // the server's bodies (masses, circles): the observer's dynamics and the Lagrange points
  const galaxyRef        = useRef<GalaxyGlow | null>(null);          // the Milky Way glow: the light of the stars too faint to be points
  const glowMsgRef       = useRef<GlowMessage | null>(null);         // last glow message, for a view built after it arrived
  const frozenRef        = useRef<FrozenTreeStore | null>(null);     // frozen LOD-unit cells (wood segments + needle sprigs)
  // Persistent particle instance state, so sleeping cells cost nothing per frame:
  // the wire stream is keyframe + deltas, and cells not mentioned keep their instance.
  const particleHasKeyframeRef = useRef(false);                       // ignore deltas until first keyframe
  const particleKfRef = useRef<{ shadow: Map<number, number>; fruit: Map<number, FruitRec>; hash: number; next: number } | null>(null);   // keyframe being received in slices
  const particleRawRef = useRef<Map<number, number>>(new Map());      // slot -> crc32 of its record (for the running hash)
  const particleHashRef = useRef(0);                                  // running XOR of crc32(record) over our cells
  const particleCrcFrameRef = useRef(0);                              // frames since connect, for request cadence
  const lastRkFrameRef = useRef(-PARTICLE_RK_COOLDOWN);               // last keyframe-request frame
  const locSentRef = useRef({ t: -1e9, x: NaN, y: NaN, z: NaN });    // the viewer's position as last told to the server (ms, world local metres)
  const ambientRef = useRef<AmbientAudio | null>(null);              // the ambient sound player (ambientAudio.ts); made at the first key press or click, since a browser only starts audio from a gesture
  const [soundOn, setSoundOn] = useState(false);
  const [soundWhy, setSoundWhy] = useState('');                         // why the server could not make the sound (empty: no complaint)
  const toggleSound = useCallback(() => {
    if (!ambientRef.current) ambientRef.current = new AmbientAudio(() => wsRef.current, why => { setSoundOn(false); setSoundWhy(why); });
    const a = ambientRef.current;
    setSoundWhy('');
    if (a.enabled) { a.disable(); setSoundOn(false); return; }
    a.enable().then(() => setSoundOn(true)).catch(e => { console.warn('[sim] sound could not start:', e); setSoundOn(false); setSoundWhy(e instanceof Error ? e.message : String(e)); });
  }, []);
  // Frozen (LOD unit) instance state: static geometry, append/keyframe stream.
  const frozenHasKeyframeRef = useRef(false);
  const frozenHashRef = useRef(0);                                    // running XOR of crc32(cid) over our frozen set
  const frozenCrcFrameRef = useRef(0);                               // frozen frames since connect, for request cadence
  const frozenKfKeepRef = useRef<Set<number> | null>(null);          // cids seen so far in a keyframe being received in slices
  const frozenKfNextRef = useRef(0);                                  // next expected keyframe slice index
  const lastFrozenRkFrameRef = useRef(-FROZEN_RK_COOLDOWN);           // last frozen keyframe-request frame

  const selectedEntityIdRef = useRef(selectedEntityId);
  const hoveredEntityIdRef  = useRef(hoveredEntityId);
  const snapToEntityIdRef   = useRef(snapToEntityId);
  const onOrganismClickRef  = useRef(onOrganismClick);
  const onOrganismHoverRef  = useRef(onOrganismHover);
  const entityPositionsRef  = useRef<Map<string, THREE.Vector3>>(new Map());
  const raycasterTargetsRef = useRef<THREE.Mesh[]>([]);

  useEffect(() => { selectedEntityIdRef.current = selectedEntityId; }, [selectedEntityId]);
  useEffect(() => { hoveredEntityIdRef.current  = hoveredEntityId;  }, [hoveredEntityId]);
  useEffect(() => { snapToEntityIdRef.current   = snapToEntityId;   }, [snapToEntityId]);
  useEffect(() => { onOrganismClickRef.current  = onOrganismClick;  }, [onOrganismClick]);
  useEffect(() => { onOrganismHoverRef.current  = onOrganismHover;  }, [onOrganismHover]);
  useEffect(() => { spinRef.current = findSpin(config); }, [config]);

  // Landscape only on touch devices: a cover asks for rotation while the device is in portrait.
  useEffect(() => {
    if (!isTouchDevice) return;
    const mq = window.matchMedia('(orientation: portrait)');
    const update = () => setIsPortrait(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, [isTouchDevice]);

  // Rebuild entity groups whenever roster or schemas change.
  const rebuildEntityGroups = useCallback(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    const roster = rosterRef.current;
    const schemas = bodySchemaRef.current;
    const entityGroups = entityGroupsRef.current;
    const slotToEntity = slotToEntityRef.current;

    // Remove groups for entities no longer in roster (or dead)
    const rosterIds = new Set(roster.filter(r => r.state !== 'dead').map(r => r.entity_id));
    for (const [entityId, groups] of entityGroups) {
      if (!rosterIds.has(entityId)) {
        for (const g of groups) scene.remove(g);
        entityGroups.delete(entityId);
        // Clean up slot mapping
        for (const [slot, info] of slotToEntity) {
          if (info.entityId === entityId) slotToEntity.delete(slot);
        }
      }
    }

    // Create groups for new entities that have a schema
    for (const org of roster.filter(r => r.state !== 'dead')) {
      if (entityGroups.has(org.entity_id)) continue;
      const bodyType = org.body_type;
      if (!bodyType || !schemas[bodyType]) continue;

      const schema = schemas[bodyType];
      const groups: THREE.Group[] = [];
      for (let segIdx = 0; segIdx < schema.bodies.length && segIdx < org.fixture_slots.length; segIdx++) {
        const group = buildSegmentGroup(schema.bodies[segIdx], schema.meshes);
        group.traverse(obj => { if (obj instanceof THREE.Mesh) obj.userData.entityId = org.entity_id; });
        scene.add(group);
        groups.push(group);
        slotToEntity.set(org.fixture_slots[segIdx], { entityId: org.entity_id, segIdx });
      }
      if (groups.length > 0) entityGroups.set(org.entity_id, groups);
    }

    // Rebuild flat mesh list for raycasting
    const targets: THREE.Mesh[] = [];
    for (const groups of entityGroups.values()) {
      for (const g of groups) {
        g.traverse(obj => { if (obj instanceof THREE.Mesh) targets.push(obj); });
      }
    }
    raycasterTargetsRef.current = targets;
  }, []);

  useEffect(() => {
    const scene = sceneRef.current;
    const foodItems = config?.world?.static_objects?.food;
    if (!scene || !foodItems?.length) return;

    const geo = new THREE.SphereGeometry(0.4, 8, 6);
    const mat = new THREE.MeshStandardMaterial({
      color: 0x5aaa3a,
      emissive: new THREE.Color(0.2, 0.5, 0.05),
      emissiveIntensity: 1.2,
      roughness: 0.7,
      metalness: 0.0,
    });
    const meshes = foodItems.map(item => {
      const [fx, fy, fz = 0] = item.position;
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(fx, fz, -fy); // sim(x,y,z) → three(x,z,-y)
      scene.add(mesh);
      return mesh;
    });

    return () => {
      for (const m of meshes) scene.remove(m);
      geo.dispose();
      mat.dispose();
    };
  }, [config]);

  const connectBroadcast = useCallback(() => {
    wsRef.current?.close();
    // Fresh connection: discard particle state and wait for the server's keyframe.
    particleHasKeyframeRef.current = false;
    particleKfRef.current = null;
    particleRawRef.current.clear();
    particleHashRef.current = 0;
    particleCrcFrameRef.current = 0;
    fruitLiveRef.current.clear();
    if (fruitRef.current) { fruitRef.current.count = 0; }
    if (frondRef.current) { frondRef.current.count = 0; }
    _frondState.clear();
    lastRkFrameRef.current = -PARTICLE_RK_COOLDOWN;
    locSentRef.current = { t: -1e9, x: NaN, y: NaN, z: NaN };    // a fresh connection tells the server where we are again
    frozenHasKeyframeRef.current = false;
    frozenHashRef.current = 0;
    frozenCrcFrameRef.current = 0;
    frozenKfKeepRef.current = null;
    frozenKfNextRef.current = 0;
    lastFrozenRkFrameRef.current = -FROZEN_RK_COOLDOWN;
    frozenRef.current?.clear();
    groundClipRef.current?.reset();       // a fresh connection streams the ground windows again from nothing
    canopyClipRef.current?.reset();       // and the canopy windows
    terrSentRef.current = { t: -1e9, x: NaN, y: NaN, h: NaN };
    tideRef.current.reset();
    frameQueueRef.current.length = 0;
    const ws = new WebSocket(broadcastUrl);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    // The cloud volume: binary parts that the assembler puts together (cloudVolumeWire.ts); the whole message ends up here.
    const onCloudVolume = (msg: CloudWinMessage) => {
      cloudWinMsgsRef.current.set(Math.trunc(msg.level), msg);
      const cv = cloudVolRef.current;
      if (cv?.enabled) void cv.ingest(msg).then(() => skyRef.current?.setVolumeActive(cv.active)).catch(e => console.warn('[sim] cloud_win rejected:', e));
    };
    const volAsm = new VolumeAssembler(onCloudVolume, e => console.warn('[sim] cloud_volume parts rejected:', e));
    // The world painter's tiles (worldTiles.ts, worldCloud.ts): binary parts like the cloud's, sent only to a client that says it reads them. A new connection starts from none.
    worldTiles.reset();
    const worldAsm = new WorldTileAssembler(t => worldTiles.ingest(t), e => console.warn('[sim] world tile rejected:', e));
    // The ground's height windows (groundWire.ts): binary parts the assembler puts together; each whole window goes into the clipmap's store.
    const groundAsm = new GroundAssembler(w => {
      try { groundClipRef.current?.accept(w); shadowDirtyRef.current = true; } catch (e) { console.warn('[sim] ground window rejected:', e); }
    }, e => console.warn('[sim] ground window parts rejected:', e));
    // The ecology's far canopy windows (canopyWire.ts): binary parts like the ground's, one raster a ring, drawn on the ground by terrainView.ts.
    const canopyAsm = new CanopyAssembler(w => {
      try { canopyClipRef.current?.accept(w); } catch (e) { console.warn('[sim] canopy window rejected:', e); }
    }, e => console.warn('[sim] canopy window parts rejected:', e));
    // The ecology's plant windows (ecoWire.ts): binary parts like the ground's; a new connection starts from none.
    ecoStoreRef.current?.clear();
    const ecoAsm = new EcoAssembler(w => ecoStoreRef.current?.accept(w), e => console.warn('[sim] ecology window rejected:', e));
    ws.onopen = () => { try { ws.send(JSON.stringify({ type: 'world_tiles', on: true })); } catch { /* socket not ready */ } ambientRef.current?.onSocketOpen(); };
    ws.onmessage = (evt) => {
      if (typeof evt.data === 'string') {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === 'roster') {
            rosterRef.current = msg.organisms ?? [];
            rosterByIdxRef.current = new Map(rosterRef.current.map(r => [r.idx, r]));
            shadowDirtyRef.current = true;
            console.log('[sim] roster', msg.organisms);
            rebuildEntityGroups();
          } else if (msg.type === 'body_schema') {
            bodySchemaRef.current = msg.schemas ?? {};
            shadowDirtyRef.current = true;
            const schemas = msg.schemas ?? {};
            for (const [bt, s] of Object.entries(schemas) as [string, any][]) {
              const meshCount = Object.keys(s.meshes ?? {}).length;
              const geomCounts = (s.bodies ?? []).map((b: any) => `${b.name}:${(b.geoms ?? []).length}geoms`);
              console.log(`[sim] body_schema ${bt}: ${meshCount} meshes, bodies=[${geomCounts.join(', ')}]`);
            }
            rebuildEntityGroups();
          } else if (msg.type === 'site_registry') {
            siteRegistryRef.current = msg.sites ?? [];
          } else if (msg.type === 'ground_info') {
            console.log('[sim] ground_info', msg);
            groundInfoRef.current = msg as GroundInfo;
            shadowDirtyRef.current = true;
            try { terrainRef.current?.setInfo(msg as GroundInfo); } catch (e) { console.error('[sim] ground_info rejected:', e); }      // (this handler swallows what it throws)
          } else if (msg.type === 'palm_recipes') {
            palmMsgRef.current = msg as PalmRecipesMessage;
            if (palmCastersRef.current && palmCastersRef.current.load(msg as PalmRecipesMessage) < 0) console.warn('[sim] palm_recipes from an unknown library version:', msg.lib, msg.v);
            shadowDirtyRef.current = true;
          } else if (msg.type === 'water') {
            tideRef.current.setState(msg as WaterStateMessage);
          } else if (msg.type === 'wind') {
            windMsgRef.current = msg as WindMessage;
            windRef.current?.applyServer(msg as WindMessage);
          } else if (msg.type === 'weather') {
            weatherMsgRef.current = msg as WeatherMessage;
            skyRef.current?.setWeather(msg as WeatherMessage);
            air.setWeather(msg as WeatherMessage);
          } else if (msg.type === 'air_map') {
            try { air.ingestMap(msg as AirMapMessage); } catch (e) { console.warn('[sim] air_map rejected:', e); }
          } else if (msg.type === 'cloud_cells') {
            cloudCellsMsgRef.current = msg as CloudCellsMessage;
            cloudCellsRef.current?.ingest(msg as CloudCellsMessage);
          } else if (msg.type === 'canopy_light') {
            canopyLightMsgRef.current = msg as CanopyLightMessage;
            void canopyShadeRef.current?.load(msg as CanopyLightMessage);
          } else if (msg.type === 'rocks') {
            rocksMsgRef.current = msg as RocksMessage;
            shadowDirtyRef.current = true;
            rocksRef.current?.load(msg as RocksMessage);
          } else if (msg.type === 'atmosphere') {
            try {
              const a = Atmosphere.fromMessage(msg);
              if (!a) console.warn('[sim] atmosphere message of a version this client does not read:', msg.version);
              cloudOptics.set(msg.cloud_optics);          // the world's cloud tables ride with its atmosphere (a block that does not parse refuses the atmosphere)
              atmosphereRef.current = a;
              skyRef.current?.setAtmosphere(a);
            } catch (e) { console.warn('[sim] atmosphere rejected:', e); cloudOptics.clear(); atmosphereRef.current = null; skyRef.current?.setAtmosphere(null); }
          } else if (msg.type === 'bodies') {
            bodiesMsgRef.current = msg as BodiesMsg;
          } else if (msg.type === 'stars') {
            starsMsgRef.current = msg as StarsMessage;
            starlightRef.current = Starlight.from((msg as StarsMessage).starlight);
            void starFieldRef.current?.load(msg as StarsMessage);
          } else if (msg.type === 'glow') {
            glowMsgRef.current = msg as GlowMessage;
            void galaxyRef.current?.load(msg as GlowMessage);
          } else if (msg.type === 'sun') {
            sunMsgCountRef.current++;
            sunRef.current = {
              direction:       msg.direction,
              elevation:       msg.elevation,
              angularDiameter: msg.angular_diameter,
              directRgb:       msg.direct_rgb,
              skyRgb:          msg.sky_rgb,
              isDay:           msg.is_day,
              flux:            typeof msg.flux === 'number' ? msg.flux : undefined,
              tick:            typeof msg.tick === 'number' ? msg.tick : undefined,
              receivedAt:      performance.now(),
              worldPos:        msg.world_pos,
              frame:           Array.isArray(msg.frame) ? ([] as number[]).concat(...(msg.frame as number[][])) : undefined,
              moon:            msg.moon ? {
                direction: msg.moon.direction, angularDiameter: msg.moon.angular_diameter,
                phase: msg.moon.phase, rgb: msg.moon.rgb,
              } : null,
            };
          } else if (msg.type === 'audio_unavailable') {
            ambientRef.current?.unavailable(typeof msg.why === 'string' ? msg.why : 'the server cannot make the sound');
          } else if (msg.type === 'stats') {
            const hz = msg.actual_tick_rate as number;
            simTpsRef.current = hz > 0 ? hz : 0;
            if (tickRateRef.current) tickRateRef.current.textContent = hz > 0 ? hz.toFixed(1) : '—';
          }
        } catch { /* ignore malformed */ }
        return;
      }
      const buf = evt.data as ArrayBuffer;
      if (isAudioFrame(buf)) { ambientRef.current?.ingest(buf); return; }                  // a block of the ambient sound
      if (isVolumeFrame(buf)) { volAsm.accept(buf); return; }                      // a part of the cloud volume, not a physics frame
      if (isWorldFrame(buf)) { worldAsm.accept(buf); return; }                     // a part of a world painter tile
      if (isGroundFrame(buf)) { groundAsm.accept(buf); return; }                   // a part of a ground height window
      if (isCanopyFrame(buf)) { canopyAsm.accept(buf); return; }                    // a part of a far canopy window
      if (isEcoFrame(buf)) { ecoAsm.accept(buf); return; }                         // a part of an ecology plant window
      if (buf.byteLength < 18) return;
      const q = frameQueueRef.current;
      q.push({ buf, view: new DataView(buf) });
      if (q.length > MAX_QUEUED_FRAMES) {
        // Hopelessly behind (a long stall): keep only the newest frame and drop the keyframe state,
        // which makes the decoder ask the server for fresh keyframes instead of applying deltas
        // that no longer chain.
        q.splice(0, q.length - 1);
        particleHasKeyframeRef.current = false;
        particleKfRef.current = null;     // a keyframe in progress lost slices; the decoders ask for new ones
        frozenHasKeyframeRef.current = false;
        frozenKfKeepRef.current = null;   // a keyframe in progress lost slices; the decoder asks for a new one
      }
    };

    ws.onerror = () => setBroadcastOffline(true);
    ws.onclose = () => { wsRef.current = null; };
  }, [broadcastUrl, rebuildEntityGroups]);

  useEffect(() => {
    setBroadcastOffline(false);
    connectBroadcast();
    return () => { wsRef.current?.close(); wsRef.current = null; };
  }, [connectBroadcast]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    // ── Renderer ────────────────────────────────────────────────────────────
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true });
    } catch {
      setWebglUnavailable(true);
      return;
    }
    // Pixel ratio is capped (a 2x or 3x display would shade 4 to 9 times the pixels of a 1x one, through
    // a heavy ground shader) and then scaled by the quality ladder below.
    // The HI-RES button (touch) swaps basePixelRatio for the screen's native ratio and back.
    const nativePixelRatio = window.devicePixelRatio || 1;
    let basePixelRatio = Math.min(nativePixelRatio, PIXEL_RATIO_CAP);
    renderer.setPixelRatio(basePixelRatio);
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.domElement.style.touchAction = 'none';
    // ACES by day; at night a toe-free curve, so the ground the sky lights is not clipped to black.
    renderer.toneMapping = installNightToneMapping() ? THREE.CustomToneMapping : THREE.ACESFilmicToneMapping;
    // The planet (planet.ts): everything drawn curves away round the eye, by the radius in km in ?radius= (the Earth's by default; ?radius=flat draws the flat world as before). Before the air, whose fog chunks
    // read what this leaves in the vertex stage, and before the first material compiles.
    if (!planet.install()) console.warn('[sim] planet: three\'s vertex chunks are not what planet.ts expects; the world is drawn flat');
    {
      const rq = new URLSearchParams(window.location.search).get('radius');
      if (rq !== null) planet.setRadius(rq === 'flat' || rq === '0' ? 0 : parseFloat(rq) * 1000);
    }
    // The floating origin (origin.ts): the shaders count positions from a point near the eye, so far from the island (the world is 512 km across) nothing is drawn with the coarse steps of a large float.
    // ?origin=off keeps it at the world's origin (the old behaviour), ?origin=force uses it everywhere (to compare with the old picture near the island: there should be no difference).
    origin.setMode(parseOriginMode(new URLSearchParams(window.location.search).get('origin')));
    // ?air=off: no aerial perspective at all (three's fog chunks stay as they are, the sea, pools and clouds read no air), to tell whether a problem is the air's
    if (new URLSearchParams(window.location.search).get('air') === 'off') air.enabled = false;
    else if (!air.install()) console.warn('[sim] air: three\'s fog chunks are not what air.ts expects; the land is drawn without aerial perspective');
    renderer.toneMappingExposure = 1.3;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    // The shadow map is refreshed on demand (see the frame loop), not redrawn every frame: hills and
    // boulders are static and only the quadruped and the sun move.
    renderer.shadowMap.autoUpdate = false;
    renderer.shadowMap.needsUpdate = true;
    renderer.info.autoReset = false;       // reset once per frame, just before the main render, so the counts are the scene's
    mount.appendChild(renderer.domElement);

    // World extents (for ground plane + shadow frustum). Fall back to 128 if config absent.
    const dims = (config as any)?.universe?.grid_dimensions as number[] | undefined;
    const worldW = dims?.[0] ?? 128;
    const worldH = dims?.[1] ?? 128;
    const worldMax = Math.max(worldW, worldH);
    const cx = worldW / 2;
    const cz = -worldH / 2;  // sim y maps to three -z

    // ── Scene ───────────────────────────────────────────────────────────────
    const scene = new THREE.Scene();
    // Sky and fog colours are derived each frame from the sun message's skylight (see
    // the sun block in animate()); these are only the pre-first-message defaults.
    scene.background = new THREE.Color(0x05070f);
    scene.fog = new THREE.FogExp2(0x05070f, FOG_AIR_DENSITY);   // (the object stays, at density 0 in air: the underwater look raises its density, and the sea and pools read it)
    sceneRef.current = scene;

    // ── Camera ──────────────────────────────────────────────────────────────
    // Coordinate mapping: sim(x, y, z) → Three.js(x, z, -y). FPS camera: it stands
    // on the ground plane at a fixed eye height and is driven by pointer-lock look +
    // WASD walk (below). Starts near one edge of the patch looking across it.
    const CAMERA_NEAR = 0.1;    // the near plane at the base far plane (worldMax * 20); both follow the eye's height (see the frame loop)
    const camera = new THREE.PerspectiveCamera(75, mount.clientWidth / mount.clientHeight, CAMERA_NEAR, worldMax * 20);

    // ── FPS controls: pointer-lock mouselook + WASD walk ─────────────────────
    // Tunables.
    const EYE_HEIGHT       = 2.0;    // camera height above the ground plane (three-y)
    const MOVE_SPEED       = 20;     // walk speed, world units / second
    const SPRINT_MULT      = 2.5;    // hold Shift to sprint
    // Free roam (V): the camera comes off the ground and flies, along the view direction (W/S/A/D, the stick on touch), Space up and C down.
    // The wheel scales the speed. It cannot enter the ground; there is no ceiling.
    const FLY_SPEED        = 40;     // free-roam speed at scale 1, world units / second
    const FLY_K_MIN        = 0.05;   // the wheel's speed scale: slowest ...
    const FLY_K_MAX        = 100;    // ... and fastest (x1 is 40 / s; x100 crosses the world in seconds)
    const FLY_ANGULAR      = 0.35;   // free roam: the speed grows with the height above the surface so the ground moves past at this many radians a second at most (m/s per m of height);
                                     // low down the speed is FLY_SPEED, as before. The wheel scales both.
    const REACH_PER_HEIGHT = 300;    // the camera's far plane is at least this many times the eye's height above the horizon plane: from there the edge of the flat sea is under 0.2 degrees below the horizon, where the air (air.ts) has taken all its colour
    const NEAR_PER_GROUND_HEIGHT = 0.05;   // the near plane is at most this share of the eye's height above the ground (a 2 m eye: the base 0.1 m; 100 m up: 3.2 m)
    const NEAR_CAP_M       = worldMax;   // the near plane follows the far plane but never passes this: the sky shell (dome, stars, sun, moon) is camera-locked at SKY_R * 0.5 and beyond
    const DEPTH_GAP_RATE   = 0.02;   // the ground apron and the horizon cutter sit this share of the eye's height (above 50 m) under the sea, so the depth buffer's coarse far steps cannot put them in front of it
    const FLY_CLEARANCE    = 0.5;    // closest the eye may come to the terrain surface in free roam
    const LAND_TAU_S       = 0.6;    // leaving free roam, the eye settles back to walking height over about this long
    const LOOK_SENSITIVITY = 0.002;  // radians of view rotation per pixel of mouse motion
    const PICK_PX          = 30;     // screen-space fallback pick radius for center selection
    const isTouch          = detectTouch();

    camera.position.set(cx, EYE_HEIGHT, cz);  // the middle of the patch: the spawn (the ground windows follow the eye from here)
    camera.rotation.order = 'YXZ';
    let yaw   = 0;    // look along -z, into the patch
    let pitch = 0;    // level
    let roll  = 0;    // about the view axis: zero on the tile; out in space the camera's attitude is its own (space.flight), no direction is up
    camera.rotation.set(pitch, yaw, 0, 'YXZ');

    let locked = false;
    const keys = new Set<string>();
    let freeRoam = false;     // free roam (fly) on
    let flyK = 1;             // free-roam speed scale (the wheel)
    let inSpaceShown = false;   // what the overlay was last told about the regime
    let gravityMode = false;  // out in space (G): the thrusters are on, the observer is a body with a velocity under the bodies' pulls and the keys push it; off: the keys move the camera directly
    let navOn = false;        // the navigation overlay (N)
    let latticeOn = false;    // the gravitational lattice (L)
    let navAt = 0;            // when the overlay's readout was last written (ms)
    let landing = false;      // just left free roam: the eye is easing back down to walking height
    let syncFlyBtn = () => { /* replaced by the touch menu, when there is one */ };
    const setFreeRoam = (on: boolean) => {
      if (on === freeRoam) return;
      freeRoam = on;
      landing = !on;   // leaving: ease down to the ground rather than snapping to it
      setIsFreeRoam(on);
      syncFlyBtn();
    };
    let prevHoveredFromCanvas: string | undefined;
    const canvas = renderer.domElement;

    // ── Lighting — sun-driven (day/night, shadows) ───────────────────────────
    // Light levels come from the world server in physical-ish linear units spanning
    // ~9 decades (noon ~1, a full moon ~1e-6, a moonless night ~1e-9). A single exposure gain, a pure
    // function of the current luminance (exposure.ts), scales tone-mapping exposure and the sky colour,
    // compressing that range into something displayable. The gain only partly compensates, so night
    // still reads darker than day. The constants that set how dark a moonless night gets live in
    // exposure.ts (EXPOSURE).
    const BASE_EXPOSURE = 1.3;    // tone-mapping exposure at full daylight (gain = 1)
    const SKY_EXPOSURE  = 6.0;    // skylight -> sky colour: 1 - exp(-SKY_EXPOSURE * gain * sky)
    const LIGHT_GAIN    = 2.4;    // directional light intensity per unit of direct_rgb
    const AMBIENT_GAIN  = 4.8;    // ambient light intensity per unit of sky_rgb
    const MOON_GAIN     = AMBIENT_GAIN;   // the moon's beam, when it is the light that casts: the server puts the moon's horizontal irradiance in sky_rgb, which AMBIENT_GAIN lights level ground with, so the same gain on the beam gives level ground the same light (LIGHT_GAIN would halve it)
    // The eye adapts to the light a scene is lit with: under a sun a little warm and a sky that carries the cloud's warm scatter, the mean
    // light reads as white, and the ground's own colours show. SCENE_WB is how much of the cast of the light on a horizontal surface
    // (sun on it plus sky) is taken out of the sun and ambient light colours (luminance kept), from a sun a few degrees up (0.02 to 0.22
    // in sin elevation); at sunset and by night it is 0, so the colour of a low sun stays.
    const SCENE_WB      = 0.9;
    let sceneWbInfo = 'scene light: not yet';
    let exposure = 1;             // the current gain, for the debug overlay
    // Whether the canvas encodes its output as sRGB itself, which the star and moon shaders must mimic
    // because they write display values directly (they bypass tone mapping, like the sun disc).
    const srgbOut = ((renderer as any).outputColorSpace ?? '') === (THREE as any).SRGBColorSpace
      || (renderer as any).outputEncoding === (THREE as any).sRGBEncoding;

    // Ambient stands in for skylight; colour and intensity come from the sun message.
    const ambient = new THREE.AmbientLight(0xffffff, 0);
    scene.add(ambient);
    // Skylight with a direction (skyLight.ts, field): the same ambient light as a light probe, spherical harmonics up to the second band, so a face turned up, down or toward
    // a clear patch or a cloud bank is lit by the part of the sky it sees, and one that looks down by the ground. It stays in the scene at intensity 0 when the flat ambient
    // is in use (the probe's presence is part of every material's shader, as the light count is), and the flat ambient is 0 while it is on.
    const ambProbe = new THREE.LightProbe(); ambProbe.intensity = 0;
    scene.add(ambProbe);

    // The sun's light: four cascaded shadow maps (three's CSM), each a directional light over a slice of the camera's view, so the
    // ground at your feet has centimetre texels and the far island still has a map. The helper keeps the beam's direction, colour
    // and intensity in step with the sun message (setSun, in the frame loop), sets every lit material up for the cascades,
    // and redraws each map only when it must. Intensity is set to 0 (not removed) at night so the light count, and with it every
    // material's shader, stays fixed.
    const sunShadows = new SunShadows(scene, camera, { splitsM: [8, 30, 100, 300], mapSize: 2048, dirtyMinMs: [33, 33, 100, 250] });     // the two near maps redraw at most 30 times a second: every server frame moves the body, and a redraw is 3 to 6 ms of CPU

    // Shadow refresh. Current three lets each light decide whether its own shadow map is redrawn, so
    // the sun's (big, static between sun and body moves) can stay on demand while the flashlight's
    // redraws every frame it is lit. Older three has only the global switch, where a lit flashlight
    // means the sun's map is redrawn every frame too (the original behaviour).
    // (The cascaded sun needs per-light control; SunShadows has already put each cascade on demand.)
    const perLightShadows = 'autoUpdate' in (sunShadows.lights[0].shadow as any) && 'needsUpdate' in (sunShadows.lights[0].shadow as any);
    if (perLightShadows) renderer.shadowMap.autoUpdate = true;

    // ── Flashlight (F) ───────────────────────────────────────────────────────
    // A spotlight held just below and right of the eye, aimed at the point under the crosshair. Purely
    // visual: it is drawn by the client and the simulation, and the quadruped's sensors, do not see it.
    // It is a real light in the world's units, so the eye-adaptation model decides how bright it looks:
    // FLASH_GLOW is how bright it reads at full night adaptation (raise for a stronger beam), and in
    // daylight the same beam is invisible, as it should be. It does not feed adaptation itself.
    // Lives in the scene only while on (a spot light with a shadow adds per-pixel cost to every lit
    // material, and switching it in the first time recompiles those materials once: a short hitch).
    const FLASH_GLOW      = 60;       // display brightness of the beam at night, per metre squared, about
    const FLASH_ANGLE     = 0.30;     // cone half-angle, radians (about 17 degrees)
    const FLASH_PENUMBRA  = 0.65;     // fraction of the cone that is soft edge
    const FLASH_RANGE     = 40;       // shadow frustum depth, metres
    const FLASH_FADE_S    = 0.15;     // seconds to fade in and out
    const FLASH_INTENSITY = FLASH_GLOW / (BASE_EXPOSURE * EXPOSURE.A_MAX);
    const flash = new THREE.SpotLight(0xfff0d8, 0, 0, FLASH_ANGLE, FLASH_PENUMBRA, 2);
    flash.castShadow = true;
    flash.shadow.mapSize.set(1024, 1024);
    flash.shadow.camera.near = 0.1;
    flash.shadow.camera.far = FLASH_RANGE;
    flash.shadow.bias = -0.0004;
    flash.shadow.normalBias = 0.02;
    let flashWanted = false, flashLevel = 0, flashInScene = false;
    let sunBeamLum = 1;                 // luminance of the sun's direct beam on the ground, set by the sun block
    // The sky has two lights, the sun and the moon, and one set of shadow cascades: they follow whichever beam is the stronger on level ground (with hysteresis), and hand over
    // through a short dip in the light (a quarter of a second), so the shadows are never drawn for a light that is hardly there. While the moon holds them its direct light moves
    // out of the ambient (the eye's light has the moon's horizontal irradiance in it) and into the beam, so level ground keeps the light it had.
    let moonLead = false, moonShown = false, handK = 1;      // which is the stronger (the moon?), which the cascades follow, and how far the light is up after a hand over (0..1)
    let moonLift = 0;                   // 0..1: how far the plants' filter is lifted because the beam is the moon's (the canopy light volume is the sun's)
    const _moonDirThree = new THREE.Vector3(), _moonDirArr = [0, 0, 0], _moonSub = [0, 0, 0], _moonRgb = [0, 0, 0], _moonBtr = [0, 0, 0];   // _moonRgb: the moon's beam as used this frame (through the volume, or the server's)
    let moonVol = false;                // the moon's beam this frame is worked through the volume (else the air's alone)
    let moonInfo = 'moon: -';
    // The sun's beam and the sky's light at the eye, worked through the cloud volume that is drawn (skyLight.ts) instead of the server's cloud cells: the cells and the
    // volume agree on how much cloud there is but not on which rays to the sun they block, so the disc, the cast shadows and the ambient light did not follow the cloud
    // on screen. The beam and the sky's light are those of the tables at the eye (eyeLight.ts), the cloud is the volume's. Costs a few tenths of a millisecond every 100 ms on the CPU and
    // nothing on the GPU.
    const skyLight = new SkyLight();
    const lightTune = {
      amb: (new URLSearchParams(window.location.search).get('ambient') === 'flat' ? 'flat' : 'dir') as 'dir' | 'flat',   // the ambient light: with a direction (needs the volume's light), or the one flat colour
      ground: 0.25,                     // the albedo of the ground that lights the faces looking down
      amt: 1,                           // how far the directional light goes from the flat one (0 = flat)
      cloud: 1,                         // how far the clouds' ambient follows it (0 = their own flat ambient and height cue)
      moon: new URLSearchParams(window.location.search).get('moonlight') !== 'off',   // the moon takes the shadow cascades and its glow and bounce are in the field, when it is the stronger
    };
    const _ambE = [0, 0, 0], _ambH = [0, 0, 0], _ambSh = new Float32Array(27), _ambRel = new Float32Array(27);
    let ambDir = false;                 // the ambient light this frame is the directional one
    const lightD: [number, number, number] = [0, 0, 0], lightS: [number, number, number] = [0, 0, 0];   // the volume's light, in the units of direct_rgb and sky_rgb
    const _slEye = [0, 0, 0], _slSun = [0, 0, 0];
    let volLight = false;               // the light this frame is the volume's (else the tables' alone, or the server's before they have come)
    const _flashFwd = new THREE.Vector3();

    // ── Visible sun body ─────────────────────────────────────────────────────
    // The sun is drawn at infinity. The simulation models it with parallel rays (one
    // direction for the whole patch, matching the shadows and the light field), so a
    // finite-distance sun would show parallax the physics does not have. Its apparent
    // size comes from the server's angular diameter, which is where orbit radius shows
    // up. The orb is locked to the camera position each frame at SKY_R, well inside the
    // far plane and outside the patch. It is unlit and exempt from tone mapping: its
    // colour is the beam's hue (normalised), not a radiometric value.
    const SKY_R = worldMax * 10;
    // Drawn size of the sun disc relative to its true angular size. A 75 degree view on a monitor
    // shrinks the sky, so the true half degree reads as a few pixels; 1 is exact.
    // Live-tunable from the Clouds panel (tunePanel.ts): the disc's drawn size, the glare's strength, and what decides how much of the disc shows through cloud.
    const tune = { sunSize: 2.0, glare: 1.0, seen: 'both' as 'both' | 'probe' | 'server' | 'always' | 'never' };
    const discDbg = { server: null as number | null, probe: 1, final: 1, note: '' };
    const sunOrbGeo = new THREE.SphereGeometry(1, 32, 16);
    // Additive: the disc only ever adds light to the sky behind it, so through thick cloud it fades into the
    // bright patch the glare makes instead of showing as a dark opaque dot in front of the clouds.
    const sunOrbMat = makeSunDiscMaterial();
    const sunOrb = new THREE.Mesh(sunOrbGeo, sunOrbMat);
    sunOrb.frustumCulled = false;
    scene.add(sunOrb);
    // A soft glow around the disc so it reads as bright rather than a flat coin (see sunGlare.ts).
    const sunGlare = new SunGlare();
    scene.add(sunGlare.mesh);
    const glareColor = new THREE.Color(1, 1, 1);
    // Invisible horizon. Writes depth but no colour, so anything beyond the world (the
    // sun, at SKY_R) is clipped where the ground would cut it off. Without it the disc
    // sinks past the edge of the finite ground plane and stays visible below the
    // horizon. Sits below the lowest terrain and follows the camera in x/z.
    const HORIZON_Y = -6;
    // (A disc of rings, in the XZ plane: it bends away under the eye with the sea and the land, so the horizon it cuts at is the planet's, planet.ts.)
    const horizonGeo = curvedDisc(SKY_R * 1.5);
    const horizonMat = new THREE.MeshBasicMaterial({ colorWrite: false, depthWrite: true });
    const horizonMesh = new THREE.Mesh(horizonGeo, horizonMat);
    horizonMesh.renderOrder = -10;
    horizonMesh.frustumCulled = false;
    scene.add(horizonMesh);
    const _white = new THREE.Color(1, 1, 1);
    const lastDisc = new THREE.Color(1, 0.98, 0.92);   // held while the beam is 0 (limb still visible)

    // ── Stars and moon ───────────────────────────────────────────────────────
    // Real catalog stars, placed from the world's universe position and frame (see stars.ts), and the
    // moon as a lit sphere. Both are camera-locked at the sky radius like the sun, and both are hidden
    // below the horizon by the horizon disc above. The catalog arrives once per connection.
    // The sky (sky.ts) is one full-screen pass drawn behind everything: for each pixel's direction the colour the air sends to the eye, from the sky-view table for the observer where it is
    // (on the ground, ten kilometres up and in orbit alike). The cloud is the cloud volume's. scene.background stays as the clear colour (and as the underwater colour), and the fog
    // follows the sky's colour just above the horizon.
    const sky = new Sky();
    skyLight.setShape((sinEl, phi, out) => sky.view.shapeAt(sinEl, phi, out));       // the clear sky's brightness by direction (the sky-view table, read in place), for the directional ambient
    sky.setOutputSrgb(((renderer as any).outputColorSpace ?? '') === (THREE as any).SRGBColorSpace);
    scene.add(sky.mesh);
    skyRef.current = sky;
    if (atmosphereRef.current) sky.setAtmosphere(atmosphereRef.current);   // (likewise)
    if (weatherMsgRef.current) sky.setWeather(weatherMsgRef.current);   // a message that arrived before the scene existed
    // The cycle's low cloud as objects (cloudCells.ts), drawn in the scene and depth-tested against it; the clear sky is the sky pass's.
    // ?cells=off|puffs|volume|hybrid picks the renderer to start with (default off: the cloud volume draws the low cloud and its rain; the cells stay as the model's objects), ?cellsteps=8..64 the volume march; window.cloudCells.setMode(...) and key 7
    // switch it while the scene runs, so the two can be compared with prof.frame() / prof.groups().
    const cells = new CloudCells(sky);
    scene.add(cells.group);
    cloudCellsRef.current = cells;
    const qs = new URLSearchParams(window.location.search);
    let cellsMode: CellMode = (['off', 'puffs', 'volume', 'hybrid'] as CellMode[]).includes(qs.get('cells') as CellMode) ? (qs.get('cells') as CellMode) : 'off';
    cells.setMode(cellsMode);
    // The server's cloud volume (cloudVolume.ts): the low cloud as one volume everybody sees. While it draws, the dome's own low layer stands down.
    // ?volume=off keeps the dome's layer (and ignores the message); window.cloudVolume.on()/off() switch it while the scene runs.
    const cloudVol = new CloudVolume(sky);
    sky.volumeProbe = cloudVol;                                   // the probe sees the volume: the sun's disc, the moon and the stars are hidden by it (sky.ts volumeProbe)
    scene.add(cloudVol.mesh);
    cloudVolRef.current = cloudVol;
    cloudVol.setEnabled(qs.get('volume') !== 'off');
    // The march ends each ray at the ground (cloudVolume.ts, setGround): the clouds are not drawn inside the terrain, and the contact is clean. ?groundclip=off marches as before (to compare);
    // window.cloudVolume.setGroundClip(false/true) while it runs.
    cloudVol.setGround(groundClipRef.current);
    if (qs.get('groundclip') === 'off') cloudVol.setGroundClip(false);
    // Each pixel's cloud is drawn as depth slices, so what stands inside it (the plants) is veiled by the cloud in front of it only (cloudVolume.ts, setLayers). ?cloudlayers=1 draws one depth, as before;
    // up to 8 (the default is 4). window.cloudVolume.setLayers(n) while it runs.
    const layersQ = parseInt(qs.get('cloudlayers') ?? '', 10);
    if (Number.isFinite(layersQ)) cloudVol.setLayers(layersQ);
    // The cloud's shadow on everything the sun lights (cloudShadow.ts): the beam marched through this volume into rings of transmittance round the eye, read by every lit material (csmShadows.ts).
    // ?cloudshadow=off leaves it out (to compare); window.cloudShadow.setEnabled(false/true) while it runs.
    const cloudShadow = new CloudShadow(cloudVol, groundClipRef.current!, sunShadows.cloudUniforms);
    if (qs.get('cloudshadow') === 'off') cloudShadow.setEnabled(false);
    (window as any).cloudShadow = {
      shadow: cloudShadow,
      setEnabled: (on: boolean) => { cloudShadow.setEnabled(on); return cloudShadow.describe(); },
      setStepK: (k: number) => { cloudShadow.setStepK(k); return cloudShadow.describe(); },
      describe: () => cloudShadow.describe(),
    };
    // The volume's march is drawn into a target of this share of the screen's size, each way (cloudVolume.ts, SMALL_DEFAULT = 0.5: a quarter of the pixels) and stretched back by
    // a cheap quad that the scene still cuts out; the quality ladder shrinks it from here with the dome's view scale. ?cloudscale=1 draws it at full size, as before the small target
    // existed, to compare; any value from 0.25 to 1. window.cloudVolume.setScale(...) changes it while the scene runs.
    const cloudScaleParam = parseFloat(qs.get('cloudscale') ?? '');
    if (Number.isFinite(cloudScaleParam)) cloudVol.setScale(cloudScaleParam);
    const cloudBaseScale = cloudVol.scaleValue;
    // The march draws one pixel in four of that target a frame and builds the rest from the last frames (cloudVolume.ts, the temporal march): about a quarter of the cost, the same picture.
    // ?cloudtemporal=off marches the whole target every frame, to compare; window.cloudVolume.setTemporal(false/true) while it runs.
    if (qs.get('cloudtemporal') === 'off') cloudVol.setTemporal(false);
    // The sky probe (the sky as seen from the eye, which the sea's reflection, the stars, the moon and the sun's disc read) is redrawn this often, in seconds: it follows the cloud,
    // which is slow, and a quicker probe only shows the march's own per-frame noise. ?probe=0.25 for a quicker one, ?probe=0 for every frame.
    const probeQ = parseFloat(qs.get('probe') ?? '');
    const PROBE_PERIOD_S = Number.isFinite(probeQ) ? Math.max(0, probeQ) : 1.0;
    // The volume's far field (cloudVolume.ts, FAR_DEFAULT): ?far=off draws the low cloud to its horizon with no thinning; ?far=lod,fade,haze,maxD,maxA sets the five (any may be left empty:
    // ?far=,,,15000 shortens the cloud's horizon to 15 km). window.cloudVolume.setFar({...}) changes them while the scene runs.
    const farQ = qs.get('far');
    if (farQ === 'off') cloudVol.setFar({ lod: 0, fade: 1, haze: 1 });
    else if (farQ) { const [fl, ff, fh, fm, fa] = farQ.split(',').map(parseFloat); const o: Record<string, number> = {}; if (Number.isFinite(fl)) o.lod = fl; if (Number.isFinite(ff)) o.fade = ff; if (Number.isFinite(fh)) o.haze = fh; if (Number.isFinite(fm)) o.maxD = fm; if (Number.isFinite(fa)) o.maxA = fa; cloudVol.setFar(o); }
    // The volume's steepness (cloudVolume.ts, STEEP_DEFAULT): ?steep=1 draws the extinction as the solver made it; below 1 the edges are sharper. window.cloudVolume.setSteep(...) changes it while the scene runs.
    const steepQ = parseFloat(qs.get('steep') ?? '');
    if (Number.isFinite(steepQ)) cloudVol.setSteep(steepQ);
    // The fine structure below the volume's voxel (cloudVolume.ts, DETAIL_DEFAULT): ?detail=0 turns it off, ?detail=0.8 makes it stronger. window.cloudVolume.setDetail({...}) changes it while the scene runs.
    const detailQ = parseFloat(qs.get('detail') ?? '');
    if (Number.isFinite(detailQ)) cloudVol.setDetail(detailQ <= 0 ? { on: false } : { on: true, k: detailQ });
    // The height cue (cloudVolume.ts, GRAD_DEFAULT): ?grad=off turns it off; ?grad=base,curve,amb sets the three (the beam's share left at a cloud's base, the exponent on the sky's openness, the ambient's share).
    const gradQ = qs.get('grad');
    if (gradQ === 'off') cloudVol.setGrad({ base: 1 });
    else if (gradQ) { const [gb, gs, ga] = gradQ.split(',').map(parseFloat); const o: Record<string, number> = {}; if (Number.isFinite(gb)) o.base = gb; if (Number.isFinite(gs)) o.curve = gs; if (Number.isFinite(ga)) o.amb = ga; cloudVol.setGrad(o); }
    // The lobe shading (cloudVolume.ts, SHADE_DEFAULT): ?shade=0 turns it off, ?shade=2 doubles it. window.cloudVolume.setShade({...}) changes it (and the tap sizes) while the scene runs.
    const shadeQ = parseFloat(qs.get('shade') ?? '');
    if (Number.isFinite(shadeQ)) cloudVol.setShade({ shade: shadeQ });
    // The march's step: ?step=0.3 takes steps of 0.3 of a voxel (the Clouds panel has this and the other cost knobs too).
    const stepQ = parseFloat(qs.get('step') ?? '');
    if (Number.isFinite(stepQ)) cloudVol.setCost({ fine: stepQ });
    // The cross-fade between consecutive volumes (cloudVolume.ts): ?blend=off swaps each volume in at once, as before; window.cloudVolume.setBlend(false/true) while it runs.
    if (qs.get('blend') === 'off') cloudVol.setBlend(false);
    // A rebuilt scene takes the windows it already has (the coarse ones first).
    if (cloudVol.enabled) for (const lv of [...cloudWinMsgsRef.current.keys()].sort((a, b) => b - a)) void cloudVol.ingest(cloudWinMsgsRef.current.get(lv)!).then(() => sky.setVolumeActive(cloudVol.active)).catch(e => console.warn('[sim] cloud_win rejected:', e));
    (window as any).air = air;                                                                   // air.strength, air.enabled, air.mapT
    (window as any).cloudVolume = {
      volume: cloudVol,
      on: () => { cloudVol.setEnabled(true); sky.setVolumeActive(cloudVol.active); return cloudVol.describe(); },
      off: () => { cloudVol.setEnabled(false); sky.setVolumeActive(false); return cloudVol.describe(); },
      setQuality: (q: VolumeQuality) => { cloudVol.setQuality(q); return cloudVol.describe(); },
      setScale: (s: number) => { cloudVol.setScale(s); return cloudVol.describe(); },
      setTemporal: (on: boolean) => { cloudVol.setTemporal(on); return cloudVol.describe(); },
      setCost: (c: { taps?: number | null; erStep?: number | null; fine?: number | null; maxSteps?: number }) => cloudVol.setCost(c),
      setFar: (f: { lod?: number; fade?: number; haze?: number; maxD?: number }) => cloudVol.setFar(f),
      setBlend: (on: boolean) => cloudVol.setBlend(on),
      setGroundClip: (on: boolean) => cloudVol.setGroundClip(on),
      setLayers: (n: number) => cloudVol.setLayers(n),
      setSteep: (s: number) => cloudVol.setSteep(s),
      setDetail: (d: { on?: boolean; k?: number; from?: number; full?: number }) => cloudVol.setDetail(d),
      setGrad: (g: { base?: number; curve?: number; amb?: number }) => cloudVol.setGrad(g),
      setShade: (f: { shade?: number; tap?: number; ratio?: number; from?: number; full?: number }) => cloudVol.setShade(f),
      describe: () => cloudVol.describe(),
      /** The light the scene is exposed with: raw against eased (lum, adapt, sky_rgb), and the last big steps of the raw light. A flip of every cloud at once
       *  should match an entry in `events` (and a jump of adaptRaw). Filled by the frame loop; call it while it happens or just after. */
      light: () => ({ ...lightDbg, events: lightEvents.slice(), nowS: Math.round(performance.now()) / 1000 }),
    };
    const cellSteps = parseInt(qs.get('cellsteps') ?? '', 10);
    if (Number.isFinite(cellSteps)) cells.setVolumeSteps(cellSteps);
    if (cloudCellsMsgRef.current) cells.ingest(cloudCellsMsgRef.current);
    (window as any).cloudCells = {
      cells,
      setMode: (m: CellMode) => { cellsMode = m; cells.setMode(m); return cells.describe(); },
      getMode: () => cellsMode,
      setSteps: (n: number) => { cells.setVolumeSteps(n); return cells.describe(); },
      setRainGain: (g: number) => { cells.setRainGain(g); return g; },
      describe: () => cells.describe(),
    };
    const wind = new WindField();            // shared by stars' shimmer, grass, trees and live tips; advanced in the frame loop
    windRef.current = wind;
    if (windMsgRef.current) wind.applyServer(windMsgRef.current);   // a message that arrived before the scene existed
    const starField = new StarField(SKY_R * 0.98, wind);
    scene.add(starField.object);
    starFieldRef.current = starField;
    if (starsMsgRef.current) void starField.load(starsMsgRef.current);
    const galaxyGlow = new GalaxyGlow(SKY_R * 0.99);   // the stars too faint to be points, summed into a sky map (galaxy.ts)
    scene.add(galaxyGlow.mesh);
    galaxyRef.current = galaxyGlow;
    if (glowMsgRef.current) void galaxyGlow.load(glowMsgRef.current);
    const starProbe = new StarProbe(starField, galaxyGlow);   // the stars and the Milky Way in a small cube, for the sea and the pools to reflect (starProbe.ts)
    const moonView = new MoonView(SKY_R);   // the moon's glare; the moon itself, like every body too far to be geometry, is the far shell's (farShell.ts)
    scene.add(moonView.halo);
    // The observer among the bodies (space.ts): the registry built from the sun message, where the observer is (on the tile, or free in space, at any height), and the far shell that paints the
    // bodies that are not geometry. Everything in the frame loop that depends on height reads it.
    const space = new Space(cx, cz);
    scene.add(space.shell.mesh);
    scene.add(space.cloud.mesh);        // the planet's cloud, after the sea and the cloud volume's composite (worldCloud.ts)
    const lattice = new Lattice();      // the gravitational lattice (lattice.ts): drawn straight to the picture, out in space only, L toggles it
    scene.add(lattice.group);
    const spaceHidden = new Map<THREE.Object3D, boolean>();      // the objects the near field's hiding turned off this frame, with their state before (restored at the start of the next)
    const _sunDirThree = new THREE.Vector3();

    // ── Ground plane (receives shadows) ──────────────────────────────────────
    const groundGeo = new THREE.PlaneGeometry(worldMax * 3, worldMax * 3, 24, 24);       // (cut into squares: the vertex stage bends it, planet.ts)
    const groundMat = new THREE.MeshStandardMaterial({
      color: 0x3a3226, roughness: 0.95, metalness: 0.0,
    });
    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.rotation.x = -Math.PI / 2;  // lie flat on the XZ plane at y=0 (sim z=0)
    ground.position.set(cx, 0, cz);
    ground.receiveShadow = true;
    // Drawn after the terrain and the sand chunks (order 1), which cover most of what it would: their depth then rejects its fragments
    // before the shader (shadow lookups and canopy shade) runs. Opaque draws sort by order, then material, and this plane's centre is far from the camera.
    ground.renderOrder = 2;
    scene.add(ground);

    // ── Floor grid ───────────────────────────────────────────────────────────
    const gridHelper = new THREE.GridHelper(512, 64, 0x2a2a2a, 0x1a1a1a);
    gridHelper.position.set(64, 0.02, -64);
    scene.add(gridHelper);

    // ── Terrain: hills replace the flat plane when the server sends a heightfield ──
    const canopyShade = new CanopyShade(worldW, worldH);
    canopyShadeRef.current = canopyShade;
    if (canopyLightMsgRef.current) void canopyShade.load(canopyLightMsgRef.current);
    // The flat fallback plane (a world with no terrain, and the apron beyond the walls) gets the
    // same canopy shading as the terrain surface.
    groundMat.onBeforeCompile = (sh) => {
      Object.assign(sh.uniforms, canopyShade.uniforms);
      sh.vertexShader = sh.vertexShader
        .replace('#include <common>', '#include <common>\nvarying vec3 vCanopyPos;')
        .replace('#include <begin_vertex>', '#include <begin_vertex>\nvCanopyPos = (modelMatrix * vec4(transformed, 1.0)).xyz;');
      sh.fragmentShader = sh.fragmentShader
        .replace('#include <common>', '#include <common>\nvarying vec3 vCanopyPos;\n' + CANOPY_SHADE_DECL)
        .replace('#include <lights_fragment_end>', '#include <lights_fragment_end>\n' + canopyShadeApply('vCanopyPos'));
    };
    const clip = groundClipRef.current!;
    const terrain = new TerrainView(scene, ground, gridHelper, clip, canopyShade, canopyClipRef.current!);
    terrainRef.current = terrain;
    if (groundInfoRef.current) { try { terrain.setInfo(groundInfoRef.current); } catch (e) { console.error('[sim] ground_info rejected:', e); } }

    // ── Grass: cosmetic, wind-blown, stands on the ground clipmap ──
    const grass = new GrassView(scene, wind, clip.uniforms, { bounds: [worldW, worldH], shade: canopyShade });
    grassRef.current = grass;

    // ── The ecology's plants: instanced trees, shrubs, ferns and grass on the ground clipmap, from the server's plant windows (ecoPlants.ts). ?eco=off leaves them out, ?oldplants=off the per-plant trees. ──
    const ecoQs = new URLSearchParams(window.location.search);
    const eco = new EcoView(scene, wind, clip, origin, ecoStoreRef.current!);
    ecoViewRef.current = eco;
    if (ecoQs.get('eco') === 'off') eco.setEnabled(false);
    if (ecoQs.get('canopy') === 'off') canopyClipRef.current!.setEnabled(false);          // ?canopy=off leaves the far canopy off the ground

    // ── Sea: drawn over the sea floor where the ground dips below the sea level ──
    const water = new WaterView(scene, clip.uniforms);
    water.setWind(wind);                  // gusts roughen the short waves where they cross the water
    waterRef.current = water;
    const tideState = tideRef.current;
    const _rainDiscs = new Float32Array(24);
    let seaSnapped = false;        // the sea state takes the wind's value at once the first time, then follows it slowly
    let wetY = 0;                  // three-y of the wet-sand level: follows the tide up at once, down slowly

    // ── Rocks: static boulders, placed by the server on the same terrain the physics uses ──
    const rocks = new RockView(scene);
    rocksRef.current = rocks;
    if (rocksMsgRef.current) rocks.load(rocksMsgRef.current);

    // ── Sphere fallback InstancedMesh (for fixtures without body schema) ─────
    const fixGeo = new THREE.SphereGeometry(1, 8, 6);
    // NOTE: no `vertexColors: true`. Per-instance colour comes from instanceColor
    // (setColorAt), which the shader applies via USE_INSTANCING_COLOR on its own.
    // Enabling vertexColors here turns on USE_COLOR, which multiplies by the
    // geometry's per-vertex `color` attribute — absent on this sphere, so it reads
    // as (0,0,0) and zeroes every instance to black.
    const fixMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color(1.0, 1.0, 1.0),
      roughness: 0.65,
      metalness: 0.05,
    });
    const fixtures = new THREE.InstancedMesh(fixGeo, fixMat, MAX_FIXTURES);
    fixtures.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    fixtures.frustumCulled = false;
    fixtures.castShadow = true;
    fixtures.count = 0;
    scene.add(fixtures);
    fixturesRef.current = fixtures;

    // ── Managed-field particles (live plant cells) ───────────────────────────
    // Not drawn. Growing tips are the short-lived frontier of a tree and add nothing to the
    // rendered view; a cell appears once it freezes (see the frozen section below). The wire
    // records are still parsed so the running hash audit keeps the stream honest.
    // The exception is props: particles flagged in owner_id are drawn as instanced fruit spheres (bit 31) and
    // frond cards (bit 30), see flushProps; the instance sets are rebuilt only on frames that change them.
    const fruitMat = new THREE.MeshStandardMaterial({ color: new THREE.Color(1, 1, 1), roughness: 0.8, metalness: 0.0 });
    const fruitMesh = new THREE.InstancedMesh(new THREE.SphereGeometry(1, 10, 8), fruitMat, MAX_FRUIT);
    fruitMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    fruitMesh.frustumCulled = false;
    fruitMesh.castShadow = true;
    fruitMesh.count = 0;
    scene.add(fruitMesh);
    fruitRef.current = fruitMesh;
    const frondMat = new THREE.MeshStandardMaterial({ color: new THREE.Color(1, 1, 1), roughness: 0.9, metalness: 0.0, side: THREE.DoubleSide });
    const frondMesh = new THREE.InstancedMesh(makeFrondGeometry(), frondMat, MAX_FROND_DEBRIS);
    frondMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    frondMesh.frustumCulled = false;
    frondMesh.castShadow = false;
    frondMesh.count = 0;
    scene.add(frondMesh);
    frondRef.current = frondMesh;

    // ── Frozen (LOD unit) cells: tapered wood segments + needle sprigs ────────
    // Demoted plant structure is static, so instance data is written once when a record
    // arrives and never touched per frame (only a per-tree girth texel eases with age).
    // See frozenTrees.ts. No shadow casting, same reasoning as the particles above.
    // Trees sway in the shared wind field created with the grass above.
    const frozen = new FrozenTreeStore(scene, MAX_FROZEN, wind, canopyShade);
    frozenRef.current = frozen;
    if (ecoQs.get('oldplants') === 'off') frozen.setEnabled(false);       // the per-plant trees off, to see the ecology's plants alone
    // Palm crowns in the sun's shadow: leaflets expanded from the broadcast server's per-frond recipes, never drawn in the main pass.
    const palmCasters = new PalmShadowCasters(scene);
    palmCastersRef.current = palmCasters;
    if (palmMsgRef.current) palmCasters.load(palmMsgRef.current);     // a message that arrived before the scene existed

    // ── Site points ──────────────────────────────────────────────────────────
    const sitePosArr = new Float32Array(MAX_SITES * 3);
    const siteColArr = new Float32Array(MAX_SITES * 3);
    const siteGeo = new THREE.BufferGeometry();
    siteGeo.setAttribute('position', new THREE.BufferAttribute(sitePosArr, 3));
    siteGeo.setAttribute('color',    new THREE.BufferAttribute(siteColArr, 3));
    siteGeo.setDrawRange(0, 0);
    scene.add(new THREE.Points(siteGeo, new THREE.PointsMaterial({
      size: 5, sizeAttenuation: false, vertexColors: true,
    })));

    // ── Quality ladder, debug overlay and toggles ────────────────────────────
    // A frame-time controller (in the frame loop) steps down the ladder when frames run slow and
    // back up when they have been fast for a while; each step lowers the render resolution and
    // the share of grass drawn. The overlay (backtick, or ?simdebug in the URL) shows what the
    // frame costs and lets you switch parts off to find the expensive one: 1 grass, 2 trees,
    // 3 sun shadows, 4 boulders, 5 auto quality on/off, [ and ] step quality by hand.
    const show = { grass: true, trees: true, shadows: true, rocks: true };
    // The adaptive controller is on by default: a frame time over SLOW_FRAME_MS steps down a level, a long calm
    // stretch steps back up, so a fast GPU sits at level 0 and a slow one settles where it can hold about 45 fps.
    // ?quality=0..5 pins a level (0 = full) with the controller off, ?quality=auto forces it on;
    // [ ] and 5 (overlay open) still work at runtime.
    let autoQuality = true;
    let qLevel = 0;
    const applyGround = () => {
      const lv = QUALITY_LEVELS[qLevel];
      terrain.setDetail(lv.fine);     // reach of the ground's fine bump
    };
    const applyQuality = () => {
      const lv = QUALITY_LEVELS[qLevel];
      renderer.setPixelRatio(basePixelRatio * lv.res);
      grassRef.current?.setQuality(lv.grass);
      ecoViewRef.current?.setQuality(lv.grass);
      cloudVolRef.current?.setQuality(lv.sky as VolumeQuality);     // the volume's tiers follow the sky's: 2 window and detail, 1 detail, 0 plain
      cloudVolRef.current?.setScale(cloudBaseScale * lv.skyK);      // and its drawing size follows the dome's
      water.setQuality(lv.sea);
      applyGround();
    };
    // ?quality=0..5 forces that level from the start (0 = full resolution, 5 = half) and turns the controller off;
    // ?quality=auto or no parameter leaves the controller on.
    const qParam = new URLSearchParams(window.location.search).get('quality');
    if (qParam === 'auto') {
      autoQuality = true;
    } else if (qParam !== null) {
      const n = parseInt(qParam, 10);
      if (Number.isFinite(n)) { autoQuality = false; qLevel = Math.min(QUALITY_LEVELS.length - 1, Math.max(0, n)); applyQuality(); }
    }
    // ── Console profiler (perfProfile.ts) ────────────────────────────────────
    // `prof` on window: await prof.frame() for where a frame goes, prof.groups() for what each subsystem costs, prof.fill(),
    // prof.scene(), prof.sky(), prof.help(). The frame loop calls perf.begin / mark / end, which do nothing until a measurement
    // runs. A group switches a subsystem off and back on while the scene runs, so one already off with keys 1 to 6 shows no
    // difference. "on" puts a subsystem back to the state the keys left it in.
    // The tree store's materials (private to it), for the profiler's variants only.
    const treeMats = () => ({ sprigs: ((frozen as any).sprigMats ?? []) as THREE.MeshStandardMaterial[], wood: (frozen as any).woodMat as THREE.MeshStandardMaterial | undefined });

    // ── The Clouds panel: live controls for the look, the layers and the sun's disc. Backslash opens it (or its tab at the right edge). Changed values are kept in the
    // browser and put back on reload; Copy gives them as JSON; Reset puts every control back. ──
    const tunePanel = new TunePanel({ storageKey: 'nism.cloudPanel.v1', title: 'Clouds', onOpen: () => { if (document.pointerLockElement) document.exitPointerLock(); } });
    {
      const P = tunePanel, cv0 = cloudVol.describe();
      const so = (g: { on(): void; off(): void }) => (v: boolean) => { if (v) g.on(); else g.off(); };
      P.section('Look', 'Live. A dot marks a value you changed.');
      P.slider('steep', 'steepness', { min: 0.4, max: 1.5, step: 0.05, def: STEEP_DEFAULT, value: cv0.steep, onChange: v => cloudVol.setSteep(v), title: '1 draws the extinction as the solver made it; below 1 the edges are sharper and the cloud a little fatter' });
      P.slider('detail', 'fine detail', { min: 0, max: 1, step: 0.05, def: DETAIL_DEFAULT.k, value: cv0.detail.on ? cv0.detail.k : 0, onChange: v => { cloudVol.setDetail(v <= 0 ? { on: false } : { on: true, k: v }); }, title: 'Structure below the volume\'s voxel on cloud near the camera: the edge eroded by a noise fixed to the cloud, each cell keeping its water. 0 off' });
      P.slider('fxshade', 'lobe shading', { min: 0, max: 3, step: 0.05, def: SHADE_DEFAULT.shade, value: cv0.shade.shade, onChange: v => cloudVol.setShade({ shade: v }), title: 'A lit side and a shaded side on every lobe of the volume, from taps toward the sun that reach as far as the pixels do; 0 off' });
      P.slider('gbase', 'base light', { min: 0, max: 1, step: 0.05, def: 0.35, value: cv0.grad?.base ?? 0.35, onChange: v => cloudVol.setGrad({ base: v }), title: 'Share of the light left at a cloud\'s base: 1 turns the height cue off' });
      P.slider('gcurve', 'base curve', { min: 0.2, max: 2, step: 0.05, def: 0.7, value: cv0.grad?.curve ?? 0.7, onChange: v => cloudVol.setGrad({ curve: v }) });
      P.slider('gamb', 'ambient', { min: 0, max: 1, step: 0.05, def: 0.5, value: cv0.grad?.amb ?? 0.5, onChange: v => cloudVol.setGrad({ amb: v }) });
      P.section('Distance');
      P.slider('maxd', 'horizon (km)', { min: 5, max: 250, step: 1, def: FAR_DEFAULT.maxD / 1000, value: cv0.far.maxD / 1000, onChange: v => cloudVol.setFar({ maxD: v * 1000 }), title: 'How far the low cloud is followed from the ground; it grows with the eye\'s height above the cloud, up to half the world\'s period' });
      P.slider('maxa', 'horizon aloft (km)', { min: 20, max: 500, step: 10, def: FAR_DEFAULT.maxA / 1000, value: cv0.far.maxA / 1000, onChange: v => cloudVol.setFar({ maxA: v * 1000 }), title: 'How far the layers aloft (cirrus and the like) are followed' });
      P.slider('lod', 'far smoothing', { min: 0, max: 12, step: 0.5, def: FAR_DEFAULT.lod, value: cv0.far.lod, onChange: v => cloudVol.setFar({ lod: v }), title: 'How fast the march\'s steps grow with range (the share of the range, per 0.0016); 0 keeps them at the pixel footprint' });
      P.slider('fade', 'far thinning starts', { min: 0, max: 1, step: 0.05, def: FAR_DEFAULT.fade, value: cv0.far.fade, onChange: v => cloudVol.setFar({ fade: v }), title: 'Where the cloud starts to thin toward the horizon, as a share of it: 1 is no thinning, 0 thins from the eye' });
      P.slider('haze', 'air in front', { min: 0, max: 3, step: 0.05, def: 1, value: cv0.far.haze, onChange: v => cloudVol.setFar({ haze: v }) });
      P.section('Planet', 'The world is drawn on a sphere (render-only: the data stays a flat tile). ?radius=km sets it at load, ?radius=flat draws it flat.');
      P.slider('planetr', 'radius (km)', { min: RADIUS_MIN_M / 1000, max: RADIUS_MAX_M / 1000, step: 10, def: EARTH_RADIUS_M / 1000, value: planet.wanted > 0 ? planet.wanted / 1000 : RADIUS_MAX_M / 1000, onChange: v => planet.setRadius(v * 1000), title: 'How far the ground falls away round the eye: the horizon is at sqrt(2 R h). The Earth is 6371. The sky\'s own colours keep the Earth\'s radius' });
      P.readout('horizon (eye over y = 0)', () => planet.radius > 0 ? `${(horizonDistance(camera.position.y, planet.radius) / 1000).toFixed(1)} km` : 'flat');
      P.section('March', 'How the volume is sampled and drawn: what decides the softness and the stair steps. The adaptive quality controller resets these when it changes level (pin one with ?quality=N).');
      const cost = { fine: 0.6, erStep: 40, taps: 3, maxSteps: 220 };
      const applyCost = () => cloudVol.setCost({ fine: cost.fine === 0.6 ? null : cost.fine, erStep: cost.erStep === 40 ? null : cost.erStep, taps: cost.taps === 3 ? null : cost.taps, maxSteps: cost.maxSteps });
      P.select('mtier', 'volume tier', [['0', '0 plain volume'], ['1', '1 plus shading taps and detail'], ['2', '2 as 1, three taps']], { def: String(cv0.quality), title: 'Tier 0 takes 1 cell steps and has no taps and no detail', onChange: v => { cloudVol.setQuality(parseInt(v, 10) as VolumeQuality); applyCost(); } });
      P.slider('mscale', 'march size', { min: 0.35, max: 1, step: 0.05, def: cloudVol.scaleValue, title: 'The march is drawn at this share of the screen each way and stretched back; 1 draws it straight to the screen at full size (the temporal resolve is then off)', onChange: v => { cloudVol.setScale(v); } });
      P.toggle('mtemporal', 'temporal resolve', { def: cloudVol.temporalOn, title: 'One pixel in four is marched a frame and the rest carried from the last frames; off, the whole target is marched every frame (only at a march size under 1)', onChange: v => { cloudVol.setTemporal(v); } });
      P.slider('mfine', 'step (voxel share)', { min: 0.1, max: 1.2, step: 0.05, def: 0.6, title: 'A step as a share of a voxel (the window\'s own voxel inside it). Smaller is slower and smoother', onChange: v => { cost.fine = v; applyCost(); } });
      P.slider('mstep', 'longest step (m)', { min: 10, max: 200, step: 5, def: 40, title: 'The longest step inside cloud (tiers 1 and 2)', onChange: v => { cost.erStep = v; applyCost(); } });
      P.slider('mtaps', 'shading taps', { min: 0, max: 3, step: 1, def: 3, title: 'Taps toward the sun per lit sample (the lobe shading); 0 is none', onChange: v => { cost.taps = v; applyCost(); } });
      P.slider('mbudget', 'step budget', { min: 40, max: 220, step: 10, def: 220, title: 'The most steps a ray may take; a ray that runs out stops where it is', onChange: v => { cost.maxSteps = v; applyCost(); } });
      P.readout('march target', () => cloudVol.describe().target);
      P.section('Layers', 'The height clip draws only the cloud between two heights: 3000 to 6500 is the mid deck, 7000 up the upper deck.');
      P.toggle('vol', 'volume clouds', { def: true, value: cloudVol.enabled, onChange: v => { cloudVol.setEnabled(v); sky.setVolumeActive(cloudVol.active); }, title: 'Off hands the sky back to the dome\'s own clouds' });
      P.toggle('blend', 'cross-fade updates', { def: true, value: cv0.blend.on, onChange: v => cloudVol.setBlend(v) });
      const clip = { lo: 0, hi: 13000 };
      const applyClip = () => cloudVol.setClip(clip.lo <= 0 ? -1e9 : clip.lo, clip.hi >= 13000 ? 1e9 : clip.hi);
      P.slider('cliplo', 'only above (m)', { min: 0, max: 13000, step: 250, def: 0, onChange: v => { clip.lo = v; applyClip(); } });
      P.slider('cliphi', 'only below (m)', { min: 0, max: 13000, step: 250, def: 13000, onChange: v => { clip.hi = v; applyClip(); } });
      const gStars = layerGroup('stars', [starField.object]), gGalaxy = layerGroup('galaxy', [galaxyGlow.mesh]), gMoon = layerGroup('moon', [space.shell.mesh, moonView.halo]), gSun = layerGroup('sun', [sunOrb, sunGlare.mesh]);
      P.toggle('sunshow', 'sun disc and glare', { def: true, onChange: so(gSun) });
      P.toggle('moonshow', 'moon', { def: true, onChange: so(gMoon) });
      P.readout('observer', () => space.describe());
      P.toggle('starshow', 'stars', { def: true, onChange: so(gStars) });
      P.toggle('galshow', 'galaxy glow', { def: true, onChange: so(gGalaxy) });
      P.section('Light from the cloud', 'The beam, the shadows and the ambient light take the cloud from the volume that is drawn.');
      P.readout('sun ray tau', () => skyLight.ready ? skyLight.tau.toFixed(2) : '-');
      P.readout('dome factor', () => skyLight.ready ? skyLight.dome.toFixed(3) : '-');
      P.select('ambmode', 'ambient light', [['dir', 'with a direction'], ['flat', 'flat (one colour)']], { def: lightTune.amb, onChange: v => { lightTune.amb = v as 'dir' | 'flat'; } });
      P.slider('ambground', 'ground bounce', { min: 0, max: 0.6, step: 0.01, def: lightTune.ground, title: 'the albedo of the ground that lights faces looking down; 0 leaves them dark', onChange: v => { lightTune.ground = v; } });
      P.slider('ambamt', 'direction amount', { min: 0, max: 1, step: 0.05, def: lightTune.amt, title: '0 is the flat ambient, 1 all of the directional light', onChange: v => { lightTune.amt = v; } });
      P.slider('ambcloud', 'clouds follow', { min: 0, max: 1, step: 0.05, def: lightTune.cloud, title: 'how far the clouds\' ambient light takes the direction (a base looks down at the ground, a top up at the sky); 0 is the clouds\' own flat ambient and height cue', onChange: v => { lightTune.cloud = v; } });
      P.readout('ambient now', () => {
        if (!ambDir) return 'flat' + (lightTune.amb === 'dir' ? ' (needs the volume\'s light)' : '');
        const o = [0, 0, 0], up = _ambE[1] || 1, r = (x: number, y: number, z: number) => { SkyLight.faceLight(_ambSh, x, y, z, o); return (o[1]! / up).toFixed(2); };
        return `light on faces, 1 = a face turned up: down ${r(0, -1, 0)}, sides ${r(1, 0, 0)} ${r(-1, 0, 0)} ${r(0, 0, 1)} ${r(0, 0, -1)}`;
      });
      P.toggle('moonbeam', 'moon casts', { def: lightTune.moon, title: 'the moon takes the shadow cascades and its light moves from the ambient into the beam when it is the stronger; off: the sun\'s only (the moon stays in the ambient, as the server has it)', onChange: v => { lightTune.moon = v; } });
      P.readout('light that casts', () => moonInfo);
      P.readout('moon beam', () => moonVol ? `through the volume: tau ${skyLight.moonTau.toFixed(2)}` : 'the air only (no volume march)');
      P.readout('light step', () => `${skyLight.costMean.toFixed(2)} ms mean, ${skyLight.costMax.toFixed(2)} max, every 100 ms`);
      P.section('Sun disc', 'What hides the disc: both is the app; probe is the cloud as drawn, per pixel; server is the beam that lights the ground.');
      P.slider('sunsize', 'disc size', { min: 0.5, max: 6, step: 0.25, def: 2, onChange: v => { tune.sunSize = v; } });
      P.slider('glare', 'glare', { min: 0, max: 2, step: 0.05, def: 1, onChange: v => { tune.glare = v; } });
      P.select('seen', 'hidden by', [['both', 'both (app)'], ['probe', 'probe only'], ['server', 'server beam only'], ['always', 'never hidden'], ['never', 'always hidden']], { def: 'both', onChange: v => { tune.seen = v as typeof tune.seen; } });
      P.readout('server sees sun', () => discDbg.server === null ? 'unknown' : discDbg.server.toFixed(2));
      P.readout('probe sees sun', () => discDbg.probe < 0 ? 'no probe' : discDbg.probe.toFixed(2));
      P.readout('disc shows', () => discDbg.final.toFixed(2) + (discDbg.note ? '  (' + discDbg.note + ')' : ''));
      P.readout('aloft layers', () => cloudVol.hasAloft ? 'in the volume' : 'none sent');
      P.prune();
    }
    const perf = installPerfProfiler({
      renderer, scene, camera, sky,
      groups: [
        { name: 'grass', off: () => grass.setEnabled(false), on: () => grass.setEnabled(show.grass) },
        { name: 'cloud cells', note: 'off hands the near low cloud back to the dome, so this is the cost of cells against the dome\'s own drawing', warmFrames: 4,
          off: () => cells.setMode('off'), on: () => cells.setMode(cellsMode) },
        { name: 'trees (wood, sprigs, palm casters)', off: () => { frozen.setEnabled(false); palmCasters.setEnabled(false); }, on: () => { frozen.setEnabled(show.trees); palmCasters.setEnabled(show.trees); } },
        { name: 'sun shadows (cascades)', note: 'also removes the shadow lookups from every lit material', warmFrames: 12, off: () => sunShadows.setEnabled(false), on: () => sunShadows.setEnabled(show.shadows) },
        { name: 'rocks', off: () => rocks.setEnabled(false), on: () => rocks.setEnabled(show.rocks) },
        layerGroup('cloud volume', [cloudVol.mesh], 'the server\'s cloud volume: its march (into the small target) and the composite; off shows the sky without the low cloud (the dome\'s own low layer stays off)'),
        layerGroup('sea', [water.mesh]),
        layerGroup('stars', [starField.object]),
        layerGroup('galaxy glow', [galaxyGlow.mesh]),
        layerGroup('moon, planets (far shell) and moon glare', [space.shell.mesh, moonView.halo], 'the bodies too far to be geometry, painted on the sky from where the observer is (farShell.ts); the glare is moon.ts'),
        layerGroup('sun disc and glare', [sunOrb, sunGlare.mesh]),
        layerGroup('bodies, fruit, fronds', [fixtures, fruitMesh, frondMesh]),
        layerGroup('ground plane', [ground], 'the flat fallback plane under the ground rings (lit, shadow-receiving)'),
      ],
      names: [
        ['sky pass', sky.mesh], ['stars', starField.object], ['galaxy glow', galaxyGlow.mesh], ['far shell', space.shell.mesh], ['world cloud', space.cloud.mesh], ['moon halo', moonView.halo],
        ['sun disc', sunOrb], ['sun glare', sunGlare.mesh], ['sea', water.mesh], ['ground plane', ground], ['grid', gridHelper], ['horizon cutter', horizonMesh],
        ['bodies', fixtures], ['fruit', fruitMesh], ['fronds', frondMesh], ['cloud cells', cells.group],
      ],
      hold: () => { const was = autoQuality; autoQuality = false; return () => { autoQuality = was; }; },
      variants: [{
        title: 'Ground and tree detail',
        metrics: [
          { name: 'ground rings, colour pass', prefixes: ['render > ground ring'] },
          { name: 'ground rings, shadow passes', prefixes: ['render > shadow map > ground ring'] },
          { name: 'tree wood and needles, colour pass', prefixes: ['render > ~uGirth'] },
        ],
        list: [
          { name: 'no fine bump', apply: () => terrain.setDetail(0) },
          { name: 'only the rings that cast shadows (2 to 6 cast)', apply: () => terrain.setShadowRings(2) },
          { name: 'ground drawn BEFORE the trees (negative = drawing it after them now saves this)', apply: () => terrain.setGroundOrder(-1) },
          { name: 'needle cards: alpha cutoff 0.75 (fewer pixels kept)', apply: () => { for (const m of treeMats().sprigs) m.alphaTest = 0.75; } },
          { name: 'needle cards: front faces only', apply: () => { for (const m of treeMats().sprigs) { m.side = THREE.FrontSide; m.needsUpdate = true; } } },
        ],
        reset: () => {
          applyGround(); terrain.setGroundOrder(1);
          const t = treeMats();
          for (const m of t.sprigs) { m.alphaTest = 0.42; if (m.side !== THREE.DoubleSide) { m.side = THREE.DoubleSide; m.needsUpdate = true; } }
        },
      }, (() => {
        // The cloud volume's cost: each change applied to the live scene, one at a time, and measured against the WHOLE frame's GPU time and against the march's own section
        // ('cloud volume (small)'; at scale 1 the march is inside 'render' and only the whole-frame column says anything). Not per draw: see the note on PerfVariants.
        // A value a change overwrites is read when the change is applied and put back by reset(), so a setting made in the Clouds panel survives the run.
        const keep: { far?: Record<string, number> } = {};
        const withFar = (f: Record<string, number>) => () => { keep.far ??= { ...cloudVol.describe().far } as Record<string, number>; cloudVol.setFar(f); };
        const scaleNow = () => cloudBaseScale * QUALITY_LEVELS[qLevel].skyK;
        const temporalBase = cloudVol.temporalOn;
        return {
          title: 'Cloud volume',
          trace: false,
          metrics: [{ name: 'whole frame', prefixes: ['=frame'] }, { name: 'cloud march (small target)', prefixes: ['cloud volume (small)'] }],
          list: [
            { name: 'cloud volume not drawn at all', apply: () => { cloudVol.mesh.layers.mask = 0; } },
            { name: 'drawn at full size, straight to the screen (as before the small target)', apply: () => cloudVol.setScale(1) },
            { name: 'small target x0.75 each way', apply: () => cloudVol.setScale(0.75) },
            { name: 'temporal march off: the whole small target marched every frame', apply: () => cloudVol.setTemporal(false) },
            { name: 'small target x0.5 each way (the default)', apply: () => cloudVol.setScale(0.5) },
            { name: 'small target x0.35 each way', apply: () => cloudVol.setScale(0.35) },
            { name: 'quality 0: plain volume (no taps, no window, 1 voxel steps)', apply: () => cloudVol.setQuality(0) },
            { name: 'quality 1: 2 taps, no window', apply: () => cloudVol.setQuality(1) },
            { name: 'no shading taps (lobe lit and shaded sides off)', apply: () => cloudVol.setCost({ taps: 0 }) },
            { name: 'one shading tap', apply: () => cloudVol.setCost({ taps: 1 }) },
            { name: 'longest step in cloud 80 m (from 40)', apply: () => cloudVol.setCost({ erStep: 80 }) },
            { name: 'step budget 120 (from 220)', apply: () => cloudVol.setCost({ maxSteps: 120 }) },
            { name: 'step budget 80', apply: () => cloudVol.setCost({ maxSteps: 80 }) },
            { name: 'cloud horizon 15 km (from the default)', apply: withFar({ maxD: 15000 }) },
          ],
          reset: () => {
            cloudVol.mesh.layers.mask = 1;
            cloudVol.setTemporal(temporalBase);
            cloudVol.setQuality(QUALITY_LEVELS[qLevel].sky as VolumeQuality);          // also puts the cost knobs back
            cloudVol.setScale(scaleNow());
            if (keep.far) { cloudVol.setFar(keep.far); keep.far = undefined; }
          },
        };
      })()],
      extra: () => {
        const lv = QUALITY_LEVELS[qLevel];
        const cv = cloudVol.describe();
        return [
          `cloud volume: ${cv.active ? 'on' : 'off'}, march ${cv.target} (scale x${cv.scale.toFixed(2)}), quality ${cv.quality}, shading taps ${cv.taps}, step ${cv.fine.toFixed(2)} cell, longest step in cloud ${cv.erStep >= 1e8 ? 'a cell' : cv.erStep + ' m'}, step budget ${cv.maxSteps}, horizon ${(cv.far.maxD / 1000).toFixed(0)} km`,
          `quality level L${qLevel} of ${QUALITY_LEVELS.length - 1} (resolution x${lv.res.toFixed(2)}, grass ${(lv.grass * 100).toFixed(0)}%, sky q${lv.sky}, fine ground layer x${lv.fine.toFixed(2)}), adaptive controller ${autoQuality ? 'on' : 'off'}`,
          `ground: rings ${clip.have().join(',') || 'none'} received, ${terrain.ringsDrawn} drawn (finest ${clip.kmin < 16 ? clip.kmin : '-'}, coarsest ${clip.top}), ${clip.taken} windows taken, lowest ${terrain.minHeight.toFixed(1)} m`,
          sky.describe(),
          sceneWbInfo,
          volLight ? skyLight.describe() : `sky light: ${atmosphereRef.current ? 'the tables\' (no volume yet)' : 'the server\'s (no atmosphere tables yet)'}`,
          `trees: ${frozen.count} cells, wood ${frozen.woodCount}, needle cards ${frozen.sprigCount} (${frozen.sprigsDrawn} drawn after distance thinning), ${frozen.binCount} bins, ${frozen.chunkCount} draw chunks`,
        ];
      },
    });

    let dbgOn = new URLSearchParams(window.location.search).has('simdebug');
    const dbg = document.createElement('div');
    dbg.style.cssText = 'position:absolute;top:30px;left:10px;padding:6px 8px;background:rgba(0,0,0,0.6);' +
      'color:#9fe8c8;font:11px/1.45 "Space Mono",monospace;white-space:pre;pointer-events:none;z-index:5;';
    dbg.style.display = dbgOn ? 'block' : 'none';
    mount.appendChild(dbg);

    // ── Input: raycasting + FPS control handlers ─────────────────────────────
    const raycaster = new THREE.Raycaster();
    let prevHighlightedId: string | undefined;

    // Movement scratch vectors + fixed screen-center pick point.
    const _right  = new THREE.Vector3();
    const _up     = new THREE.Vector3();
    const _move   = new THREE.Vector3();
    const _lqY    = new THREE.Quaternion();
    const _lqX    = new THREE.Quaternion();
    const _axisY  = new THREE.Vector3(0, 1, 0);
    const _axisX  = new THREE.Vector3(1, 0, 0);
    const _center = new THREE.Vector2(0, 0);
    const clock   = new THREE.Clock();

    const findClosestEntityOnScreen = (px: number, py: number, rect: DOMRect, threshold: number): string | undefined => {
      let best: string | undefined;
      let bestDist = threshold;
      for (const [eid, pos] of entityPositionsRef.current) {
        _pickVec.copy(pos).project(camera);
        if (_pickVec.z > 1) continue; // behind camera
        const sx = (_pickVec.x + 1) / 2 * rect.width;
        const sy = (-_pickVec.y + 1) / 2 * rect.height;
        const d = Math.hypot(sx - px, sy - py);
        if (d < bestDist) { bestDist = d; best = eid; }
      }
      return best;
    };
    const _pickVec = new THREE.Vector3();

    // Raycast from the screen center (the crosshair) and return the entity under it,
    // if any. Same three-tier resolution as the old mouse pick: schema meshes, then
    // the sphere-fallback InstancedMesh, then a screen-space nearest fallback.
    const pickFromCenter = (): string | undefined => {
      raycaster.setFromCamera(_center, camera);
      const schemaTargets = raycasterTargetsRef.current;
      if (schemaTargets.length > 0) {
        const hits = raycaster.intersectObjects(schemaTargets, false);
        if (hits.length > 0) return (hits[0].object as THREE.Mesh).userData.entityId;
      }
      const fhits = raycaster.intersectObject(fixtures);
      const instanceId = fhits.length > 0 ? fhits[0].instanceId : undefined;
      const orgIdx = instanceId !== undefined ? (instanceIdToOrgIdxRef.current.get(instanceId) ?? -1) : -1;
      if (orgIdx !== -1) return rosterRef.current.find(r => r.idx === orgIdx)?.entity_id;
      const rect = canvas.getBoundingClientRect();
      return findClosestEntityOnScreen(rect.width / 2, rect.height / 2, rect, PICK_PX);
    };

    const selectFromCenter = () => {
      const id = pickFromCenter();
      onOrganismClickRef.current?.(
        id ? (id === selectedEntityIdRef.current ? undefined : id) : undefined
      );
    };

    // A click on the canvas engages pointer lock; once locked, a click selects
    // whatever is under the crosshair. The lock-engaging click fires while still
    // unlocked, so it never doubles as a selection.
    const onCanvasMouseDown = () => {
      if (isTouch) return;   // fingers drive the touch handlers below; pointer lock is for mouse and keyboard
      if (!locked) { canvas.requestPointerLock(); return; }
      selectFromCenter();
    };

    const onPointerLockChange = () => {
      locked = document.pointerLockElement === canvas;
      setIsLocked(locked);
      if (!locked) keys.clear();  // drop held keys so movement stops cleanly on Esc
    };

    // Turn the view by `dYaw` (left) and `dPitch` (up) radians. On the tile that is yaw about the vertical and pitch up to the poles, as a walker looks. Out in space no direction is up:
    // the camera turns about its own axes (left-right about its own up, up-down about its own right), and nothing keeps it level.
    const lookBy = (dYaw: number, dPitch: number) => {
      if (space.flight.mode === 'free') {
        camera.rotation.set(pitch, yaw, roll, 'YXZ');   // (the camera as the angles hold it: the frame loop adds the tilt offsets on top, and they must not be read back into the angles)
        _lqY.setFromAxisAngle(_axisY, dYaw);
        _lqX.setFromAxisAngle(_axisX, dPitch);
        camera.quaternion.multiply(_lqY).multiply(_lqX);
        camera.rotation.setFromQuaternion(camera.quaternion, 'YXZ');
        yaw = camera.rotation.y; pitch = camera.rotation.x; roll = camera.rotation.z;
        return;
      }
      yaw += dYaw;
      pitch = clampPitch(pitch + dPitch);
      camera.rotation.set(pitch, yaw, roll, 'YXZ');
    };
    const onMouseMove = (e: MouseEvent) => {
      if (!locked) return;
      lookBy(-e.movementX * LOOK_SENSITIVITY, -e.movementY * LOOK_SENSITIVITY);
    };

    // Hide or show the page chrome (header and left pane) so the viewer fills the window. Shared by the
    // H key and the touch fullscreen button.
    const setUiHidden = (hidden: boolean) => {
      panelHiddenRef.current = hidden;
      if (hidden) document.documentElement.setAttribute('data-sim-ui-hidden', ''); else document.documentElement.removeAttribute('data-sim-ui-hidden');
      window.dispatchEvent(new CustomEvent('sim-ui-hidden', { detail: { hidden } }));
    };

    // The wheel scales the free-roam speed (a notch is about 15 %), while the view is locked and flying.
    const onWheel = (e: WheelEvent) => {
      if (!locked || !freeRoam) return;
      e.preventDefault();
      flyK = Math.min(FLY_K_MAX, Math.max(FLY_K_MIN, flyK * Math.exp(-Math.sign(e.deltaY) * 0.14)));
      setFlyScale(flyK);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      keys.add(e.code);
      if (freeRoam && locked && e.code === 'Space') e.preventDefault();   // Space climbs; it must not scroll the page
      if (e.repeat) return;
      if (e.code === 'KeyV' && !e.ctrlKey && !e.metaKey && !e.altKey && !isTouch) {
        const t = e.target as HTMLElement | null;
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
        setFreeRoam(!freeRoam);
        return;
      }
      if (e.code === 'KeyG' && !e.ctrlKey && !e.metaKey && !e.altKey && !isTouch) {
        const t = e.target as HTMLElement | null;
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
        gravityMode = !gravityMode;
        setIsGravity(gravityMode);
        return;
      }
      if ((e.code === 'KeyN' || e.code === 'KeyL') && !e.ctrlKey && !e.metaKey && !e.altKey && !isTouch) {
        const t = e.target as HTMLElement | null;
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
        if (e.code === 'KeyN') { navOn = !navOn; setIsNav(navOn); } else { latticeOn = !latticeOn; setIsLattice(latticeOn); }
        return;
      }
      if (e.code === 'KeyH' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        // H hides or shows the page chrome (header and left pane) so the viewer fills the window. The
        // viewer's own overlays (crosshair, hints, tick readout, debug panel) are untouched. The page
        // hides through an attribute on <html> (a class on <body> would be wiped by the theme switcher,
        // which assigns body.className); the same state also goes out as a window event.
        const t = e.target as HTMLElement | null;
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
        setUiHidden(!panelHiddenRef.current);
        return;
      }
      if (e.code === 'KeyM' && !e.ctrlKey && !e.metaKey && !e.altKey && !e.repeat) {
        // M switches the ambient sound on or off. The key press is the user gesture a browser needs to start audio.
        const t = e.target as HTMLElement | null;
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
        toggleSound();
        return;
      }
      if (e.code === 'Backquote') { dbgOn = !dbgOn; dbg.style.display = dbgOn ? 'block' : 'none'; return; }
      // [ and ] step the quality by hand at any time (overlay or not) and switch auto quality off, so
      // the level you pick is the level you get. 5 (overlay open) turns auto back on.
      if (e.code === 'BracketLeft' || e.code === 'BracketRight') {
        autoQuality = false;
        qLevel = e.code === 'BracketLeft' ? Math.min(QUALITY_LEVELS.length - 1, qLevel + 1) : Math.max(0, qLevel - 1);
        applyQuality(); fpsClock = 0; calmFor = 0; probing = false;
        return;
      }
      if (e.code === 'KeyF') { flashWanted = !flashWanted; return; }
      if (!dbgOn) return;
      switch (e.code) {
        case 'Digit1': show.grass = !show.grass; grassRef.current?.setEnabled(show.grass); break;
        case 'Digit2': show.trees = !show.trees; frozenRef.current?.setEnabled(show.trees); palmCastersRef.current?.setEnabled(show.trees); break;
        case 'Digit3': show.shadows = !show.shadows; sunShadows.setEnabled(show.shadows); break;   // recompiles lit materials once
        case 'Digit4': show.rocks = !show.rocks; rocksRef.current?.setEnabled(show.rocks); break;
        case 'Digit5': autoQuality = !autoQuality; break;
        case 'Digit7': { const modes: CellMode[] = ['off', 'puffs', 'volume', 'hybrid']; cellsMode = modes[(modes.indexOf(cellsMode) + 1) % modes.length]; cells.setMode(cellsMode); break; }
      }
    };
    const onKeyUp   = (e: KeyboardEvent) => { keys.delete(e.code); };

    canvas.addEventListener('mousedown', onCanvasMouseDown);
    canvas.addEventListener('wheel', onWheel, { passive: false });
    document.addEventListener('pointerlockchange', onPointerLockChange);
    document.addEventListener('mousemove', onMouseMove);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup',   onKeyUp);

    // ── Touch: floating walk stick, drag to turn, tilt to aim, fullscreen ─────
    // Landscape, three zones by where a finger lands. A touch in the left third plants a floating stick
    // under that thumb. It is analog: how far the thumb is pushed sets the walking speed, up to
    // TOUCH_WALK_SPEED, and there is no sprint. A touch in the right third turns the view as it drags, and
    // two fingers in the middle third pinch to zoom (a double tap there resets the zoom).
    // Tilt (when switched on with its button) adds the phone's own rotation on top of the dragged angles,
    // measured from the pose it had when switched on, so the thumb does the big turns and the wrist the
    // fine aim. The frame loop reads the state below; the handlers only write it.
    let stickVX = 0, stickVY = 0;          // stick deflection, -1 to 1 (x right, y forward)
    let walkR = 0, walkF = 0;              // eased walking velocity, world units / second (right, forward)
    let gyroYawT = 0, gyroPitchT = 0;      // tilt offsets the sensors ask for
    let gyroYaw = 0, gyroPitch = 0;        // tilt offsets drawn (eased)
    let zoomT = 1, zoom = 1;               // pinch zoom: the target and the drawn (eased) magnification, 1 to TOUCH_ZOOM_MAX
    const baseFov = camera.fov;            // the unzoomed vertical field of view, degrees
    const touchUiCleanup: Array<() => void> = [];

    if (isTouch) {
      canvas.style.setProperty('-webkit-touch-callout', 'none');
      canvas.style.userSelect = 'none';
      const mkEl = (css: string) => { const d = document.createElement('div'); d.style.cssText = css; mount.appendChild(d); return d; };
      const R = TOUCH_STICK_R;
      const stickBase = mkEl(`position:absolute;display:none;width:${R * 2}px;height:${R * 2}px;border-radius:50%;` +
        'border:1px solid rgba(150,210,255,0.45);background:rgba(150,210,255,0.08);pointer-events:none;z-index:4;');
      const stickKnob = mkEl('position:absolute;display:none;width:44px;height:44px;border-radius:50%;' +
        'border:1px solid rgba(150,210,255,0.7);background:rgba(150,210,255,0.3);pointer-events:none;z-index:4;');
      const place = (el: HTMLElement, x: number, y: number, half: number) => {
        el.style.left = `${x - half}px`; el.style.top = `${y - half}px`;
      };

      // Three zones, by where a finger lands: left walks, the middle pinches to zoom, the right turns. A
      // finger keeps its job for as long as it stays down, wherever it wanders.
      let moveId = -1, lookId = -1;
      let zoomAId = -1, zoomBId = -1;                  // the two fingers of a pinch, in the middle zone
      let stickX0 = 0, stickY0 = 0, lookX = 0, lookY = 0;
      let zoomAX = 0, zoomAY = 0, zoomBX = 0, zoomBY = 0;
      let zoomD0 = 1, zoomZ0 = 1;                      // finger spacing and zoom when the pinch began
      let midT0 = 0, midTravel = 0;                    // when the first middle finger went down, and how far it has moved (tap versus pinch)
      let midPinched = false, lastTapT = 0;            // that finger took part in a pinch; time of the last tap
      const endStick = () => { moveId = -1; stickVX = 0; stickVY = 0; stickBase.style.display = 'none'; stickKnob.style.display = 'none'; };
      let closeMenu = () => { /* replaced below, once the menu exists */ };

      const onTouchStart = (e: TouchEvent) => {
        e.preventDefault();   // also stops the browser's synthesized mouse events, scrolling and zooming
        if (touchHintRef.current) touchHintRef.current.style.display = 'none';
        closeMenu();   // a touch on the view puts the menu away
        const rect = canvas.getBoundingClientRect();
        const leftEdge = rect.width * TOUCH_ZONE_LEFT, rightEdge = rect.width * TOUCH_ZONE_RIGHT;
        for (const t of Array.from(e.changedTouches)) {
          const lx = t.clientX - rect.left, ly = t.clientY - rect.top;
          if (lx < leftEdge) {
            if (moveId !== -1) continue;
            moveId = t.identifier; stickX0 = lx; stickY0 = ly; stickVX = 0; stickVY = 0;
            place(stickBase, lx, ly, R); place(stickKnob, lx, ly, 22);
            stickBase.style.display = 'block'; stickKnob.style.display = 'block';
          } else if (lx >= rightEdge) {
            if (lookId !== -1) continue;
            lookId = t.identifier; lookX = t.clientX; lookY = t.clientY;
          } else if (zoomAId === -1) {
            zoomAId = t.identifier; zoomAX = t.clientX; zoomAY = t.clientY;
            midT0 = performance.now(); midTravel = 0; midPinched = false;
          } else if (zoomBId === -1) {
            // The second middle finger: a pinch begins.
            zoomBId = t.identifier; zoomBX = t.clientX; zoomBY = t.clientY;
            zoomD0 = Math.max(30, Math.hypot(zoomBX - zoomAX, zoomBY - zoomAY));
            zoomZ0 = zoomT; midPinched = true;
          }
        }
      };
      const onTouchMove = (e: TouchEvent) => {
        e.preventDefault();
        const rect = canvas.getBoundingClientRect();
        for (const t of Array.from(e.changedTouches)) {
          if (t.identifier === moveId) {
            let dx = t.clientX - rect.left - stickX0, dy = t.clientY - rect.top - stickY0;
            const len = Math.hypot(dx, dy);
            if (len > R) { dx *= R / len; dy *= R / len; }
            stickVX = dx / R; stickVY = -dy / R;
            place(stickKnob, stickX0 + dx, stickY0 + dy, 22);
          } else if (t.identifier === lookId) {
            // The turn per pixel shrinks with zoom, so a drag moves the picture the same distance at any zoom.
            lookBy(-(t.clientX - lookX) * TOUCH_LOOK_SENS / zoom, -(t.clientY - lookY) * TOUCH_LOOK_SENS / zoom);
            lookX = t.clientX; lookY = t.clientY;
          } else if (t.identifier === zoomAId) {
            midTravel += Math.hypot(t.clientX - zoomAX, t.clientY - zoomAY);
            zoomAX = t.clientX; zoomAY = t.clientY;
          } else if (t.identifier === zoomBId) {
            zoomBX = t.clientX; zoomBY = t.clientY;
          }
        }
        if (zoomBId !== -1) {   // fingers apart zooms in, together zooms out
          const d = Math.hypot(zoomBX - zoomAX, zoomBY - zoomAY);
          zoomT = Math.min(TOUCH_ZOOM_MAX, Math.max(1, zoomZ0 * (d / zoomD0)));
        }
      };
      const onTouchEnd = (e: TouchEvent) => {
        e.preventDefault();
        for (const t of Array.from(e.changedTouches)) {
          if (t.identifier === moveId) endStick();
          else if (t.identifier === lookId) lookId = -1;
          else if (t.identifier === zoomAId || t.identifier === zoomBId) {
            const isA = t.identifier === zoomAId;
            const now = performance.now();
            // A short, still single touch in the middle is a tap; two in quick succession put the zoom back to 1.
            if (isA && zoomBId === -1 && !midPinched && now - midT0 < TOUCH_TAP_MS && midTravel < TOUCH_TAP_SLOP) {
              if (now - lastTapT < TOUCH_DOUBLE_TAP_MS) { zoomT = 1; lastTapT = 0; } else lastTapT = now;
            }
            if (!isA) zoomBId = -1;
            else if (zoomBId !== -1) {   // the other finger carries on as the first
              zoomAId = zoomBId; zoomAX = zoomBX; zoomAY = zoomBY; zoomBId = -1;
            } else zoomAId = -1;
          }
        }
      };
      canvas.addEventListener('touchstart',  onTouchStart, { passive: false });
      canvas.addEventListener('touchmove',   onTouchMove,  { passive: false });
      canvas.addEventListener('touchend',    onTouchEnd,   { passive: false });
      canvas.addEventListener('touchcancel', onTouchEnd,   { passive: false });

      // Menu, top right under the tick readout: one hamburger button that opens the rest. The buttons are
      // siblings of the canvas, so touching them never starts the stick or a drag; a touch on the canvas
      // closes the menu (see closeMenu above).
      const bar = mkEl('position:absolute;top:30px;right:10px;display:flex;flex-direction:column;gap:8px;align-items:flex-end;z-index:6;');
      const btnBase = 'padding:6px 10px;background:rgba(0,0,0,0.45);color:rgba(150,210,255,0.85);' +
        'border:1px solid rgba(150,210,255,0.4);border-radius:6px;font:11px "Space Mono",monospace;' +
        'letter-spacing:0.06em;touch-action:manipulation;';
      const menuBtn = document.createElement('button');
      menuBtn.textContent = '☰';
      menuBtn.setAttribute('aria-label', 'Menu');
      menuBtn.style.cssText = btnBase + 'width:40px;height:40px;padding:0;font-size:18px;line-height:1;';
      const panel = document.createElement('div');
      panel.style.cssText = 'display:none;flex-direction:column;gap:8px;align-items:flex-end;';
      bar.appendChild(menuBtn);
      bar.appendChild(panel);
      let menuOpen = false;
      const setMenu = (open: boolean) => {
        menuOpen = open;
        panel.style.display = open ? 'flex' : 'none';
        menuBtn.textContent = open ? '✕' : '☰';
      };
      menuBtn.addEventListener('click', () => setMenu(!menuOpen));
      closeMenu = () => { if (menuOpen) setMenu(false); };
      const mkBtn = (label: string, onTap: () => void) => {
        const b = document.createElement('button');
        b.textContent = label;
        b.style.cssText = btnBase + 'min-width:96px;min-height:36px;';
        b.addEventListener('click', onTap);
        panel.appendChild(b);
        return b;
      };

      // Flashlight: the same toggle as the F key.
      const lightBtn = mkBtn('FLASHLIGHT: OFF', () => {
        flashWanted = !flashWanted;
        lightBtn.textContent = flashWanted ? 'FLASHLIGHT: ON' : 'FLASHLIGHT: OFF';
      });

      // Free roam: the same toggle as the V key. The stick then flies along the view direction (look up and push to climb).
      const flyBtn = mkBtn('FREE ROAM: OFF', () => setFreeRoam(!freeRoam));
      syncFlyBtn = () => { flyBtn.textContent = freeRoam ? 'FREE ROAM: ON' : 'FREE ROAM: OFF'; };

      // Fullscreen. Where the browser has the Fullscreen API (Android, iPad) the button uses it and then
      // hides the page chrome; iPhone Safari has none for pages, so there the button only hides the chrome
      // (adding the page to the home screen is the way to lose the browser bars on iPhone).
      const root = document.documentElement as any;
      const fsRequest = root.requestFullscreen ?? root.webkitRequestFullscreen;
      const fsElement = (): Element | null => (document as any).fullscreenElement ?? (document as any).webkitFullscreenElement ?? null;
      const fsExit = () => { const d = document as any; return (d.exitFullscreen ?? d.webkitExitFullscreen)?.call(document); };
      const fsBtn = mkBtn('', () => {
        if (!fsRequest) { setUiHidden(!panelHiddenRef.current); syncFsBtn(); return; }
        if (fsElement()) { void fsExit(); return; }
        try {
          Promise.resolve(fsRequest.call(root, { navigationUI: 'hide' }))
            .then(() => (screen.orientation as any)?.lock?.('landscape'))   // Android honours this once fullscreen
            .catch(() => { /* refused: the rotate cover still covers portrait */ });
        } catch { /* ignore */ }
      });
      const syncFsBtn = () => {
        fsBtn.textContent = !fsRequest ? (panelHiddenRef.current ? 'SHOW UI' : 'HIDE UI') : (fsElement() ? 'EXIT FULLSCREEN' : 'FULLSCREEN');
      };
      const onFsChange = () => { setUiHidden(!!fsElement()); syncFsBtn(); };
      document.addEventListener('fullscreenchange', onFsChange);
      document.addEventListener('webkitfullscreenchange', onFsChange);
      syncFsBtn();

      // HI-RES: draw at the screen's native pixel ratio instead of the PIXEL_RATIO_CAP, for sharper grass
      // and foliage at several times the pixel work. Only offered when the screen is denser than the cap.
      // The label shows the resulting render size. Off by default.
      if (nativePixelRatio > PIXEL_RATIO_CAP) {
        let hiRes = false;
        const hiResBtn = mkBtn('HI-RES: OFF', () => {
          hiRes = !hiRes;
          basePixelRatio = hiRes ? nativePixelRatio : Math.min(nativePixelRatio, PIXEL_RATIO_CAP);
          applyQuality();
          const sz = renderer.getDrawingBufferSize(_drawSize);
          hiResBtn.textContent = hiRes ? `HI-RES ${sz.x}x${sz.y}` : 'HI-RES: OFF';
        });
      }

      // Tilt to aim. The offsets are relative: the first sensor reading after switching on (or after a
      // rotation or a LEVEL tap) becomes zero, so there is no compass heading to drift against.
      if ('DeviceOrientationEvent' in window) {
        const _gEuler = new THREE.Euler();
        const _gQ = new THREE.Quaternion();
        const _gQ0 = new THREE.Quaternion();
        const _gQ1 = new THREE.Quaternion(-Math.SQRT1_2, 0, 0, Math.SQRT1_2);   // -90 degrees about x
        const _gZ = new THREE.Vector3(0, 0, 1);
        const _gF = new THREE.Vector3();
        let gyroOn = false, gyroGot = false;
        let gyroBase: { yaw: number; pitch: number } | null = null;

        // Fold the current tilt offsets into the dragged angles and restart from the next reading.
        const bakeGyro = () => {
          yaw += gyroYaw;
          pitch = clampPitch(pitch + gyroPitch);
          gyroYaw = gyroPitch = gyroYawT = gyroPitchT = 0;
          gyroBase = null;
        };
        const onOrient = (e: DeviceOrientationEvent) => {
          if (e.alpha === null || e.beta === null || e.gamma === null) return;
          gyroGot = true;
          const D = Math.PI / 180;
          const screenAngle = (screen.orientation && typeof screen.orientation.angle === 'number')
            ? screen.orientation.angle : ((window as any).orientation ?? 0);
          // Device orientation to a camera quaternion (the same mapping three's old DeviceOrientationControls used),
          // then the direction it looks along, as a yaw and a pitch.
          _gEuler.set(e.beta * D, e.alpha * D, -e.gamma * D, 'YXZ');
          _gQ.setFromEuler(_gEuler).multiply(_gQ1).multiply(_gQ0.setFromAxisAngle(_gZ, -screenAngle * D));
          _gF.set(0, 0, -1).applyQuaternion(_gQ);
          const dYaw = Math.atan2(-_gF.x, -_gF.z);
          const dPitch = Math.asin(Math.max(-1, Math.min(1, _gF.y)));
          // Accumulate the change since the previous reading, shrunk by the zoom: a wrist movement turns the
          // view less when zoomed, so aim stays steady under hand tremor. At zoom 1 the sum equals the pose
          // relative to the one at switch-on.
          if (gyroBase) {
            gyroYawT += wrapPi(dYaw - gyroBase.yaw) / zoom;
            gyroPitchT += (dPitch - gyroBase.pitch) / zoom;
            gyroPitchT = clampPitch(pitch + gyroPitchT) - pitch;   // never push the view past straight up or down
          }
          gyroBase = { yaw: dYaw, pitch: dPitch };
        };
        const tiltBtn = mkBtn('TILT: OFF', async () => {
          if (gyroOn) {
            gyroOn = false;
            window.removeEventListener('deviceorientation', onOrient);
            bakeGyro();
            tiltBtn.textContent = 'TILT: OFF';
            levelBtn.style.display = 'none';
            return;
          }
          try {
            const DOE = (window as any).DeviceOrientationEvent;
            if (typeof DOE.requestPermission === 'function') {   // iOS: needs this tap, and the user's yes
              if ((await DOE.requestPermission()) !== 'granted') { tiltBtn.textContent = 'TILT: DENIED'; return; }
            }
          } catch { tiltBtn.textContent = 'TILT: DENIED'; return; }
          gyroOn = true; gyroGot = false; gyroBase = null;
          window.addEventListener('deviceorientation', onOrient);
          tiltBtn.textContent = 'TILT: ON';
          levelBtn.style.display = 'block';
          setTimeout(() => {
            if (gyroOn && !gyroGot) {   // a device with no motion sensor, or a page that is not on https
              gyroOn = false; window.removeEventListener('deviceorientation', onOrient);
              tiltBtn.textContent = 'TILT: NO SENSOR'; levelBtn.style.display = 'none';
            }
          }, 1500);
        });
        // LEVEL: treat the way the phone is held now as straight ahead, and bring the horizon level.
        const levelBtn = mkBtn('LEVEL', () => { bakeGyro(); pitch = 0; });
        levelBtn.style.display = 'none';

        const rebase = () => { if (gyroOn) bakeGyro(); };   // a rotation of the screen changes the sensor frame
        screen.orientation?.addEventListener?.('change', rebase);
        window.addEventListener('orientationchange', rebase);
        touchUiCleanup.push(() => {
          window.removeEventListener('deviceorientation', onOrient);
          screen.orientation?.removeEventListener?.('change', rebase);
          window.removeEventListener('orientationchange', rebase);
        });
      }

      touchUiCleanup.push(() => {
        canvas.removeEventListener('touchstart',  onTouchStart);
        canvas.removeEventListener('touchmove',   onTouchMove);
        canvas.removeEventListener('touchend',    onTouchEnd);
        canvas.removeEventListener('touchcancel', onTouchEnd);
        document.removeEventListener('fullscreenchange', onFsChange);
        document.removeEventListener('webkitfullscreenchange', onFsChange);
        if (fsElement()) void fsExit();
        stickBase.remove(); stickKnob.remove(); bar.remove();
      });
    }

    // ── Frame decoding ───────────────────────────────────────────────────────
    // Each wire frame is decoded exactly once, in arrival order. The streamed sections (particles,
    // frozen cells) are deltas that must all be applied; fixtures and sites are plain state, so
    // only the newest frame of a batch needs them.
    const seenPositions = new Set<string>();
    const _q = new THREE.Quaternion();
    const _sunDir = new THREE.Vector3(0, 1, 0);
    const _moonSkyDir = new THREE.Vector3(0, 1, 0);       // the moon's direction in three's axes, for the clouds' light
    let frozenDirty = false;
    let newFrameApplied = false;
    let decodeMs = 0;

    const applyFixtures = (buf: ArrayBuffer, view: DataView) => {
      const tick = view.getBigUint64(0, true);
      if (tickDisplayRef.current) tickDisplayRef.current.textContent = tick.toString();
      if (clockRef.current) {
        const sp = spinRef.current;
        const sd = sunRef.current?.direction;
        const txt = sp ? ((sd && solarClockText(sd, sp)) || clockText(Number(tick), sp)) : '--:--';
        if (clockRef.current.textContent !== txt) clockRef.current.textContent = txt;
      }
      const fixture_count  = view.getUint16(12, true);
      const site_count     = view.getUint16(14, true);
      let off = 20;  // frame header is 20 bytes (particle_upd_count is u32)
      let n = 0;  // sphere fallback instance counter
      instanceIdToOrgIdxRef.current.clear();

      const slotToEntity = slotToEntityRef.current;
      const entityGroups = entityGroupsRef.current;
      const rosterByIdx = rosterByIdxRef.current;
      seenPositions.clear();

      for (let i = 0; i < fixture_count; i++) {
        if (off + RECORD_BYTES > buf.byteLength) break;
        const slotIdx = view.getUint16(off,      true);
        const orgIdx  = view.getUint16(off + 2,  true);
        const x  = view.getFloat32(off + 4,  true);
        const y  = view.getFloat32(off + 8,  true);
        const z  = view.getFloat32(off + 12, true);
        const r  = view.getFloat32(off + 16, true);
        const qw        = view.getFloat32(off + 23, true);
        const qx        = view.getFloat32(off + 27, true);
        const qy        = view.getFloat32(off + 31, true);
        const qz        = view.getFloat32(off + 35, true);
        const geomScale = view.getFloat32(off + 39, true);
        off += RECORD_BYTES;

        // Track world position for each entity (first slot per entity wins)
        const slotInfo = slotToEntity.get(slotIdx);
        const eidForSlot = slotInfo?.entityId ?? rosterByIdx.get(orgIdx)?.entity_id;
        if (eidForSlot && !seenPositions.has(eidForSlot)) {
          seenPositions.add(eidForSlot);
          let ep = entityPositionsRef.current.get(eidForSlot);
          if (!ep) { ep = new THREE.Vector3(); entityPositionsRef.current.set(eidForSlot, ep); }
          ep.set(x, z, -y);
        }

        // If this slot has a schema-rendered group, update its transform.
        if (slotInfo) {
          const groups = entityGroups.get(slotInfo.entityId);
          const group  = groups?.[slotInfo.segIdx];
          if (group) {
            group.position.set(x, z, -y);
            group.quaternion.copy(simQuatToThreeInto(_q, qw, qx, qy, qz));
            group.scale.setScalar(geomScale > 0 ? geomScale : 1);
          }
          continue; // don't also render a fallback sphere
        }

        // Fallback: render as a sphere instance; skip unowned/dead slots
        if (orgIdx === 0xFFFF) continue;
        const orgEntry = rosterByIdx.get(orgIdx);
        if (!orgEntry || orgEntry.state === 'dead') continue;
        if (n >= MAX_FIXTURES) continue;
        _dummy.position.set(x, z, -y);
        _dummy.scale.setScalar(Math.max(0.5, r));
        _dummy.updateMatrix();
        fixtures.setMatrixAt(n, _dummy.matrix);
        fixtures.setColorAt(n, _color.setRGB(0.90, 0.48, 0.15));
        instanceIdToOrgIdxRef.current.set(n, orgIdx);
        n++;
      }

      fixtures.count = n;
      fixtures.instanceMatrix.needsUpdate = true;
      if (fixtures.instanceColor) fixtures.instanceColor.needsUpdate = true;

      // Parse site records from binary (follow all fixture records)
      const registry = siteRegistryRef.current;
      let si = 0;
      for (let i = 0; i < site_count; i++) {
        if (off + 14 > buf.byteLength) break;
        const siteId = view.getUint16(off,      true);
        const sx     = view.getFloat32(off + 2,  true);
        const sy     = view.getFloat32(off + 6,  true);
        const sz     = view.getFloat32(off + 10, true);
        off += 14;
        if (si >= MAX_SITES) continue;
        sitePosArr[si * 3]     = sx;
        sitePosArr[si * 3 + 1] = sz;
        sitePosArr[si * 3 + 2] = -sy;
        const name = registry[siteId] ?? '';
        const isMouth = name.endsWith('_mouth');
        siteColArr[si * 3]     = isMouth ? 0.98 : 0.38;
        siteColArr[si * 3 + 1] = isMouth ? 0.52 : 0.80;
        siteColArr[si * 3 + 2] = isMouth ? 0.18 : 0.98;
        si++;
      }
      siteGeo.setDrawRange(0, si);
      if (si > 0) {
        (siteGeo.attributes.position as THREE.BufferAttribute).needsUpdate = true;
        (siteGeo.attributes.color    as THREE.BufferAttribute).needsUpdate = true;
      }
    };

    // The streamed sections of a frame: particles, then frozen cells. Fixtures and sites come
    // first on the wire at fixed record sizes, so they are skipped by arithmetic.
    const applyStreams = (buf: ArrayBuffer, view: DataView) => {
      const fixture_count    = view.getUint16(12, true);
      const site_count       = view.getUint16(14, true);
      let off = 20 + fixture_count * RECORD_BYTES + site_count * 14;

      // Particle section (managed-field cells, e.g. plants and fruit): 13-byte sub-header
      // [flags u8][a u32][b u32][state_hash u32] then 27-byte records (slot u32, owner u32, xyz,
      // radius, rgb). Two kinds, told apart by flags bit0 (the frozen section below uses the same
      // layout and rules):
      //  - delta (bit0 clear): a = upserted records, b = removed count, state_hash = hash after
      //    applying; then `a` records, then `b` removed slots (u32). Cells not mentioned persist.
      //    Nothing in it can be lost in transit: the server builds each client's delta from its own
      //    position in a change log when the frame is sent.
      //  - keyframe slice (bit0 set): a = records here, b = slice index, state_hash = hash of the whole
      //    set on the last slice. The server cuts a keyframe into slices, one per frame. Bit1 marks the
      //    first slice (start collecting), bit2 the last (swap the collected set in and verify).
      // state_hash is a running XOR of crc32 over each active record. A mismatch, a slice out of order
      // (we dropped a frame) or a delta with no baseline asks the server for a fresh keyframe ("rk"),
      // which restarts only this client's particle stream.
      if (off + PARTICLE_SUBHEADER_BYTES <= buf.byteLength) {
        const pflags = view.getUint8(off);
        const pA = view.getUint32(off + 1, true);
        const pB = view.getUint32(off + 5, true);
        const frameHash = view.getUint32(off + 9, true);
        off += PARTICLE_SUBHEADER_BYTES;
        const recBase = off;
        const isKeyframe = (pflags & 0x01) !== 0;
        // Always step past the whole section, applied or not, so the frozen section after it parses.
        off += pA * PARTICLE_RECORD_BYTES + (isKeyframe ? 0 : pB * 4);
        const requestParticleKeyframe = () => {
          particleCrcFrameRef.current++;
          if (particleCrcFrameRef.current - lastRkFrameRef.current >= PARTICLE_RK_COOLDOWN) {
            try { wsRef.current?.send("rk"); } catch { /* socket not ready */ }
            lastRkFrameRef.current = particleCrcFrameRef.current;
          }
        };
        const fruitAt = (o: number): FruitRec => [
          view.getFloat32(o + 8, true), view.getFloat32(o + 12, true), view.getFloat32(o + 16, true),
          view.getFloat32(o + 20, true),
          view.getUint8(o + 24), view.getUint8(o + 25), view.getUint8(o + 26),
          view.getUint32(o + 4, true),
        ];
        if (isKeyframe) {
          if ((pflags & 0x02) !== 0) {          // first slice: start collecting
            particleKfRef.current = { shadow: new Map(), fruit: new Map(), hash: 0, next: 0 };
            particleHasKeyframeRef.current = false;
          }
          const kf = particleKfRef.current;
          if (kf && pB === kf.next) {
            let o = recBase;
            for (let i = 0; i < pA; i++) {
              if (o + PARTICLE_RECORD_BYTES > buf.byteLength) break;
              const slot = view.getUint32(o, true);
              if (view.getUint32(o + 4, true) >= PROP_OWNER_MIN) kf.fruit.set(slot, fruitAt(o));
              // Hash over a VIEW into the frame buffer (no copy) and keep only the number.
              const crc = crc32(new Uint8Array(buf, o, PARTICLE_RECORD_BYTES));
              kf.shadow.set(slot, crc);
              kf.hash = (kf.hash ^ crc) >>> 0;
              o += PARTICLE_RECORD_BYTES;
            }
            kf.next++;
            particleCrcFrameRef.current++;      // slices count as frames for the request cooldown
            if ((pflags & 0x04) !== 0) {        // last slice: the keyframe is complete, swap it in
              particleRawRef.current = kf.shadow;
              particleHashRef.current = kf.hash;
              const fruitLive = fruitLiveRef.current;
              fruitLive.clear();
              for (const [k, v] of kf.fruit) fruitLive.set(k, v);
              if (fruitRef.current && frondRef.current) flushProps(fruitRef.current, frondRef.current, fruitLive);
              particleKfRef.current = null;
              particleHasKeyframeRef.current = true;
              if (kf.hash !== frameHash) requestParticleKeyframe();
            }
          } else {
            // A slice we cannot place (we dropped the ones before it): abandon this keyframe and ask
            // for a new one. Slices still in flight from the old one are ignored until its first
            // slice arrives.
            particleKfRef.current = null;
            particleHasKeyframeRef.current = false;
            requestParticleKeyframe();
          }
        } else if (particleHasKeyframeRef.current) {
          const rawShadow = particleRawRef.current;
          const fruitLive = fruitLiveRef.current;
          let fruitChanged = false;
          let o = recBase;
          // Plant cells are not drawn, so a record only feeds the running hash; fruit and frond debris are also kept for drawing.
          for (let i = 0; i < pA; i++) {
            if (o + PARTICLE_RECORD_BYTES > buf.byteLength) break;
            const slot = view.getUint32(o, true);
            if (view.getUint32(o + 4, true) >= PROP_OWNER_MIN) {
              fruitLive.set(slot, fruitAt(o));
              fruitChanged = true;
            } else if (fruitLive.delete(slot)) {
              fruitChanged = true;               // the slot was a fruit and is now something else
            }
            const newCrc = crc32(new Uint8Array(buf, o, PARTICLE_RECORD_BYTES));
            const prev = rawShadow.get(slot);
            if (prev !== undefined) particleHashRef.current = (particleHashRef.current ^ prev) >>> 0;
            particleHashRef.current = (particleHashRef.current ^ newCrc) >>> 0;
            rawShadow.set(slot, newCrc);
            o += PARTICLE_RECORD_BYTES;
          }
          for (let i = 0; i < pB; i++) {
            if (o + 4 > buf.byteLength) break;
            const slot = view.getUint32(o, true);
            o += 4;
            if (fruitLive.delete(slot)) fruitChanged = true;
            const prev = rawShadow.get(slot);
            if (prev !== undefined) particleHashRef.current = (particleHashRef.current ^ prev) >>> 0;
            rawShadow.delete(slot);
          }
          if (fruitChanged && fruitRef.current && frondRef.current) flushProps(fruitRef.current, frondRef.current, fruitLive);
          // O(1) audit: our hash vs the server's. A mismatch means we are not in step (a frame was
          // dropped on our side); the cooldown avoids a storm.
          particleCrcFrameRef.current++;
          if (particleHashRef.current !== frameHash &&
              particleCrcFrameRef.current - lastRkFrameRef.current >= PARTICLE_RK_COOLDOWN) {
            try { wsRef.current?.send("rk"); } catch { /* socket not ready */ }
            lastRkFrameRef.current = particleCrcFrameRef.current;
          }
        } else if (particleKfRef.current === null) {
          // A delta with no baseline and no keyframe in progress: unusable, so ask for a keyframe (throttled).
          requestParticleKeyframe();
        }
      }

      // Frozen section (LOD unit cells): 13-byte sub-header [flags u8][a u32][b u32][hash u32], then
      // 36-byte records (layout in frozenTrees.ts: cid, owner, xyz, parent delta, radii, cell type,
      // progress, rgb, flags). Two kinds, told apart by flags bit0:
      //  - delta (bit0 clear): a = added count, b = removed count, hash = cid-set hash after applying;
      //    then `a` records, then `b` removed cids. Nothing in it can be lost in transit: the server
      //    builds each client's delta from its own position in a change log when the frame is sent.
      //  - keyframe slice (bit0 set): a = records here, b = slice index, hash = whole-set hash on the
      //    last slice. The server cuts a keyframe into slices, one per frame, so a big forest arrives
      //    over many frames instead of as one multi-megabyte message. Bit1 marks the first slice
      //    (start collecting cids), bit2 the last (drop held cells the keyframe lacks, then verify the
      //    hash). Frozen cells are immutable, so a slice only builds cells we do not already hold.
      // The hash is a running XOR of crc32(cid) over the active set; a mismatch, a slice out of
      // order (a dropped frame on our side) or a delta with no baseline asks the server for a fresh
      // keyframe ("rfk"), which restarts only this client's stream.
      const fmesh = frozenRef.current;
      if (fmesh && off + FROZEN_SUBHEADER_BYTES <= buf.byteLength) {
        const fflags = view.getUint8(off);
        const addedCount = view.getUint32(off + 1, true);
        const fB = view.getUint32(off + 5, true);
        const fFrameHash = view.getUint32(off + 9, true);
        off += FROZEN_SUBHEADER_BYTES;
        const isFKeyframe = (fflags & 0x01) !== 0;
        const requestFrozenKeyframe = () => {
          frozenCrcFrameRef.current++;
          if (frozenCrcFrameRef.current - lastFrozenRkFrameRef.current >= FROZEN_RK_COOLDOWN) {
            try { wsRef.current?.send("rfk"); } catch { /* socket not ready */ }
            lastFrozenRkFrameRef.current = frozenCrcFrameRef.current;
          }
        };
        if (isFKeyframe) {
          if ((fflags & 0x02) !== 0) {          // first slice: start collecting
            frozenKfKeepRef.current = new Set<number>();
            frozenKfNextRef.current = 0;
            frozenHashRef.current = 0;          // records below re-accumulate it
            frozenHasKeyframeRef.current = false;
          }
          const keep = frozenKfKeepRef.current;
          if (keep && fB === frozenKfNextRef.current) {
            for (let i = 0; i < addedCount; i++) {
              if (off + FROZEN_RECORD_BYTES > buf.byteLength) break;
              const cid = view.getUint32(off, true);
              keep.add(cid);
              if (fmesh.has(cid)) frozenHashRef.current = (frozenHashRef.current ^ crc32Cid(cid)) >>> 0;
              else if (fmesh.add(view, off)) frozenHashRef.current = (frozenHashRef.current ^ crc32Cid(cid)) >>> 0;
              off += FROZEN_RECORD_BYTES;
            }
            frozenKfNextRef.current++;
            frozenCrcFrameRef.current++;   // slices count as frames for the request cooldown
            frozenDirty = true;     // the store is flushed to the GPU once per batch, after the loop
            if ((fflags & 0x04) !== 0) {        // last slice: the keyframe is complete
              fmesh.retainOnly(keep);
              frozenKfKeepRef.current = null;
              frozenHasKeyframeRef.current = true;
              if (frozenHashRef.current !== fFrameHash) requestFrozenKeyframe();
            }
          } else {
            // A slice we cannot place (we dropped the ones before it): abandon this keyframe and ask
            // for a new one. Slices still in flight from the old one are ignored until its first
            // slice arrives.
            frozenKfKeepRef.current = null;
            frozenHasKeyframeRef.current = false;
            requestFrozenKeyframe();
          }
        } else if (frozenHasKeyframeRef.current) {
          for (let i = 0; i < addedCount; i++) {
            if (off + FROZEN_RECORD_BYTES > buf.byteLength) break;
            const cid = view.getUint32(off, true);
            // xor in only on genuine insert, so a re-sent cid does not double-count.
            if (fmesh.add(view, off)) frozenHashRef.current = (frozenHashRef.current ^ crc32Cid(cid)) >>> 0;
            off += FROZEN_RECORD_BYTES;
          }
          for (let i = 0; i < fB; i++) {
            if (off + 4 > buf.byteLength) break;
            const cid = view.getUint32(off, true);
            off += 4;
            // xor out only when we actually held it, keeping the hash in step.
            if (fmesh.remove(cid)) frozenHashRef.current = (frozenHashRef.current ^ crc32Cid(cid)) >>> 0;
          }
          frozenDirty = true;
          // O(1) audit: our cid-set hash vs the server's. A mismatch means we are not in step
          // (the frame was dropped on our side); the cooldown avoids a storm.
          frozenCrcFrameRef.current++;
          if (frozenHashRef.current !== fFrameHash &&
              frozenCrcFrameRef.current - lastFrozenRkFrameRef.current >= FROZEN_RK_COOLDOWN) {
            try { wsRef.current?.send("rfk"); } catch { /* socket not ready */ }
            lastFrozenRkFrameRef.current = frozenCrcFrameRef.current;
          }
        } else if (frozenKfKeepRef.current === null) {
          // A delta with no baseline and no keyframe in progress (we dropped frames, or missed the
          // start): deltas are unusable, so ask for a keyframe (throttled). Frozen is the last
          // section, so leaving `off` mid-section is fine.
          requestFrozenKeyframe();
        }
      }
    };

    // ── Sun motion: dead reckoning ───────────────────────────────────────────
    // Sun messages arrive at the sim's cadence, a few per second to dozens, and the frame loop runs at
    // its own rate, so drawing "the latest message" or easing between messages moves the sky in fits:
    // some frames carry two steps of motion and some none. Instead the sky moves continuously. Its
    // velocity per sim tick comes from the messages themselves (the newest against one about a second
    // older, so rounding in the message does not show), the sim tick rate from the stats message turns
    // that into velocity per second, and each frame draws the newest message plus the time since it
    // arrived times that velocity. When the next message says the prediction was a little off, the
    // difference is worked off over a fraction of a second instead of jumping, so the motion stays
    // smooth whatever the message cadence. How far ahead it may extrapolate, and how long a correction is
    // worked off, follow the cadence actually seen (sunGapS, a running mean of the gap between messages),
    // so a server that steps the sun every few seconds is still drawn as a steady glide and not as a hop
    // per message. A gap longer than SUN_STALL_S (a hidden tab, a paused or stalled sim, a restart) is not
    // worked off: whatever happened in it is dropped and the sky goes straight to the latest message.
    type SunState = NonNullable<typeof sunRef.current>;
    const SUN_STALL_S = 30, SUN_HIST_S = 1.0;
    let sunGapS = 0;                                              // running mean of the gap between sun messages, seconds (0 = not seen yet)
    const sunLeadMaxS = () => Math.min(10, Math.max(0.5, 1.5 * sunGapS));      // farthest ahead of the newest message the sun is drawn
    const sunBlendS = () => Math.min(2, Math.max(0.3, 0.5 * sunGapS));         // time over which a prediction error is worked off
    const ID9 = [1, 0, 0, 0, 1, 0, 0, 0, 1];
    const SUN_N = 31;
    // Fixed layout: 0-2 direction, 3 elevation, 4 angular diameter, 5-7 direct rgb, 8-10 sky rgb,
    // 11-13 world position, 14-22 frame, 23-25 moon direction, 26 moon size, 27 moon phase, 28-30 moon rgb.
    const packSun = (s: SunState): number[] => [
      ...s.direction, s.elevation, s.angularDiameter, ...s.directRgb, ...s.skyRgb,
      ...(s.worldPos ?? [0, 0, 0]), ...(s.frame && s.frame.length === 9 ? s.frame : ID9),
      ...(s.moon ? [...s.moon.direction, s.moon.angularDiameter, s.moon.phase, ...s.moon.rgb] : [0, 0, 0, 0, 0, 0, 0, 0]),
    ];
    const sunHas = (s: SunState) => (s.worldPos ? 1 : 0) | (s.frame && s.frame.length === 9 ? 2 : 0) | (s.moon ? 4 : 0);
    const sunShown: SunState = { direction: [0, 0, 1], elevation: 0, angularDiameter: 0, directRgb: [0, 0, 0], skyRgb: [0, 0, 0], isDay: true, moon: null };
    interface SunSample { tick: number; wall: number; v: number[]; has: number; isDay: boolean }
    let sunSeen: SunState | null = null;           // the last message taken in
    let sunHist: SunSample[] = [];
    let sunVel: number[] = new Array(SUN_N).fill(0);   // change per sim tick
    let sunErr: number[] = new Array(SUN_N).fill(0);   // correction still to work off
    let sunErrT0 = 0;
    const sunEval = (nowMs: number): number[] | null => {
      const n = sunHist[sunHist.length - 1];
      if (!n) return null;
      const tps = simTpsRef.current;
      const lead = tps > 0 ? Math.min(Math.max(0, (nowMs - n.wall) / 1000), sunLeadMaxS()) * tps : 0;
      const k = Math.exp(-Math.max(0, nowMs - sunErrT0) / 1000 / sunBlendS());
      const out = new Array<number>(SUN_N);
      for (let i = 0; i < SUN_N; i++) out[i] = n.v[i] + sunVel[i] * lead + sunErr[i] * k;
      return out;
    };
    const smoothSun = (nowMs: number): SunState | null => {
      const target = sunRef.current;
      if (!target) return null;
      if (target !== sunSeen) {
        sunSeen = target;
        const was = sunEval(nowMs);                          // where the old model has the sky right now
        const s: SunSample = { tick: target.tick ?? NaN, wall: target.receivedAt ?? nowMs, v: packSun(target), has: sunHas(target), isDay: target.isDay };
        const prev = sunHist[sunHist.length - 1];
        if (!prev || !Number.isFinite(s.tick) || s.tick < prev.tick || (s.wall - prev.wall) / 1000 > SUN_STALL_S || s.has !== prev.has) {
          sunHist = [s]; sunVel.fill(0); sunErr.fill(0); sunGapS = 0;     // first message, a stall or a restart: just take it
        } else {
          { const gap = (s.wall - prev.wall) / 1000; if (gap > 0) sunGapS = sunGapS > 0 ? 0.7 * sunGapS + 0.3 * gap : gap; }
          sunHist.push(s);
          while (sunHist.length > 2 && s.wall - sunHist[1].wall >= SUN_HIST_S * 1000) sunHist.shift();
          const o = sunHist[0], dtk = s.tick - o.tick;
          if (dtk > 0) for (let i = 0; i < SUN_N; i++) sunVel[i] = (s.v[i] - o.v[i]) / dtk;
          const pred = sunEval(nowMs);
          if (was && pred) { for (let i = 0; i < SUN_N; i++) sunErr[i] = was[i] - (pred[i] - sunErr[i] * Math.exp(-Math.max(0, nowMs - sunErrT0) / 1000 / sunBlendS())); sunErrT0 = nowMs; }
        }
      }
      const out = sunEval(nowMs);
      const n = sunHist[sunHist.length - 1];
      if (!out || !n) return null;
      const unit = (o: number): [number, number, number] => {
        const l = Math.hypot(out[o], out[o + 1], out[o + 2]);
        return l > 1e-3 ? [out[o] / l, out[o + 1] / l, out[o + 2] / l] : [out[o], out[o + 1], out[o + 2]];
      };
      const [dx, dy, dz] = unit(0);
      sunShown.direction[0] = dx; sunShown.direction[1] = dy; sunShown.direction[2] = dz;
      sunShown.elevation = out[3];
      sunShown.angularDiameter = Math.max(0, out[4]);
      for (let i = 0; i < 3; i++) { sunShown.directRgb[i] = Math.max(0, out[5 + i]); sunShown.skyRgb[i] = Math.max(0, out[8 + i]); }
      sunShown.isDay = n.isDay;
      sunShown.clearRgb = undefined; sunShown.atm = undefined; sunShown.moonOpenRgb = undefined;     // (the eye's light is the tables': observerSun)
      sunShown.worldPos = n.has & 1 ? [out[11], out[12], out[13]] : undefined;
      sunShown.frame = n.has & 2 ? out.slice(14, 23) : undefined;
      sunShown.moon = n.has & 4 ? {
        direction: unit(23), angularDiameter: Math.max(0, out[26]), phase: out[27],
        rgb: [Math.max(0, out[28]), Math.max(0, out[29]), Math.max(0, out[30])],
      } : null;
      return sunShown;
    };

    // The sun and the moon as the observer has them: the directions and sizes from the registry at the pose, the beams and the sky's light from the atmosphere tables at the pose's height
    // (eyeLight.ts). The server's numbers are the world's own point's; they stand only until the tables have come (and for an eye at another world).
    const eyeSun: SunState = { direction: [0, 0, 1], elevation: 0, angularDiameter: 0, directRgb: [0, 0, 0], skyRgb: [0, 0, 0], isDay: true, moon: null };
    const eyeMoon = { direction: [0, 0, 1] as [number, number, number], angularDiameter: 0, phase: 0, rgb: [0, 0, 0] as [number, number, number] };
    const eyeStarlight = [0, 0, 0] as number[];
    const eyeMoonIn = { dir: [0, 1, 0] as number[], angR: 0, toa: [0, 0, 0] as number[] };
    const observerSun = (server: SunState | null): SunState | null => {
      const A = atmosphereRef.current, pose = space.pose;
      if (!server || !A || !space.active || !pose || pose.body !== 0) return server;
      const star = space.bodies.sunSeen(pose);
      if (!star) return server;
      const flux = sunRef.current?.flux ?? 1;         // (the smoothed state does not carry the flux: it is the message's)
      // the moon as the registry sees it from here: its beam outside the air is the disc's mean brightness times its solid angle
      const ms = server.moon ? space.bodies.moonSeen(pose) : null;
      let moonIn: typeof eyeMoonIn | null = null;
      if (ms) {
        const sin2 = Math.sin(ms.angR) ** 2;
        moonIn = eyeMoonIn; moonIn.dir = ms.dir; moonIn.angR = ms.angR;
        for (let c = 0; c < 3; c++) moonIn.toa[c] = ms.mean[c]! * sin2;
      }
      const sv = starlightRef.current ? space.starView() : null;       // the stars' floor at the up vector the eye has (universe axes: row 2 of the frame)
      const sl = sv ? starlightRef.current!.at([sv.frame[6], sv.frame[7], sv.frame[8]], eyeStarlight) : null;
      const L = eyeLight(A, pose.h, star.dir, star.angR, flux, moonIn, sl);
      const e = eyeSun;
      e.direction[0] = star.dir[0]; e.direction[1] = -star.dir[2]; e.direction[2] = star.dir[1];       // (render axes to the world's: x east, y north, z up)
      e.elevation = L.elevation;
      e.angularDiameter = 2 * star.angR;
      for (let i = 0; i < 3; i++) { e.directRgb[i] = L.direct[i]!; e.skyRgb[i] = L.sky[i]!; }
      e.clearRgb = L.clear; e.atm = L.atm;
      e.isDay = L.elevation > -0.0145;
      e.worldPos = server.worldPos; e.frame = server.frame;
      if (ms && L.moon && server.moon) {
        const m = eyeMoon;
        m.direction[0] = ms.dir[0]; m.direction[1] = -ms.dir[2]; m.direction[2] = ms.dir[1];
        m.angularDiameter = 2 * ms.angR; m.phase = server.moon.phase;
        for (let c = 0; c < 3; c++) m.rgb[c] = L.moon[c]!;
        e.moon = m; e.moonOpenRgb = m.rgb;
      } else { e.moon = server.moon; e.moonOpenRgb = undefined; }
      e.flux = flux; e.tick = server.tick; e.receivedAt = server.receivedAt;
      return e;
    };

    // ── rAF loop ─────────────────────────────────────────────────────────────
    let animId: number;
    let lastPickMs = 0;
    let shadowUpdatesWin = 0;
    let statsAt = 0;
    let glowRmsSm = 1;            // the sky's glow concentration, eased (the sky tables are re-solved in steps)
    // The sun message's light at the eye (direct_rgb, sky_rgb) is worked out through the cloud cells on the ray to the sun, so when a cell crosses the sun's
    // line the beam goes from full to nearly none in one 4 s step of the model (measured: 5 to 13 times per 1600 model s, depending on the sun's height),
    // and sky_rgb rises by the share of the beam the cell scatters down. Exposure and the dome's and clouds' ambient light follow those two numbers, so every
    // cloud changed brightness at once. An eye does not adapt in a frame: exposure follows the light in the log domain, slowly when the light falls (the
    // scene is dark for a moment, as it is under a cloud) and quickly when it rises. The sky's colour scale and the clouds' ambient are held steadier still.
    // ?adapt=off keeps the old behaviour for comparison; ?adapt=dim,bright,sky sets the three time constants in seconds.
    const adaptQ = (new URLSearchParams(window.location.search).get('adapt') ?? '').split(',');
    const ADAPT_OFF = adaptQ[0] === 'off';
    const ADAPT_DIM_S = Number.isFinite(parseFloat(adaptQ[0])) ? parseFloat(adaptQ[0]) : 6;       // exposure rising: the light fell
    const ADAPT_BRIGHT_S = Number.isFinite(parseFloat(adaptQ[1])) ? parseFloat(adaptQ[1]) : 2;    // exposure falling: the light rose
    const ADAPT_SKY_S = Number.isFinite(parseFloat(adaptQ[2])) ? parseFloat(adaptQ[2]) : 15;      // the dome's and the clouds' light level
    let lumSm = -1;                                  // the eye's light level (eyeLum.ts, for lit surfaces), eased
    let lumDarkSm = -1;                              // ... and for things seen against the sky (stars, the galaxy), eased
    let skySm: number[] | null = null;               // sky_rgb as the dome and the clouds see it: the light's colour, its level eased (skyLSm)
    let skyLSm = -1;                                 // the luminance of sky_rgb, eased
    let lumPrevRaw = -1;
    const lightEvents: { t: number; what: string; from: number; to: number }[] = [];   // big steps of the raw light, to see whether they match a flip of the clouds
    const lightDbg = { lum: 0, lumSm: 0, adaptRaw: 0, adapt: 0, sky: [0, 0, 0], skySm: [0, 0, 0] };
    let frameEma = 16;            // smoothed frame time, ms (each sample capped so one hitch does not count as slow)
    let winFrames = 0, winMs = 0; // true frame count and time since the overlay last refreshed
    let fpsFrames = 0, fpsMs = 0; // the same for the on-screen FPS readout, which refreshes twice a second whether or not the stats panel is open
    let fpsClock = 0;             // seconds since the quality level last changed
    let calmFor = 0;              // seconds frames have been fast
    let upWait = 6;               // seconds of calm before stepping quality back up (doubles if that step bounces back)
    let lastRaiseAt = -1e9;
    let tSec = 0;
    // Lowering the resolution only helps when the GPU is fill-bound. When the frame is slow for some
    // other reason (draw calls, JS, stream decoding) it just makes the picture blurry for nothing. So
    // each step down is checked a few seconds later: if frame time did not drop by at least 7 percent
    // the step is undone and further steps down are held off for two minutes.
    let probing = false, probeFromMs = 0, holdDownUntil = 0;

    const updateOverlay = (nowMs: number) => {
      const secs = (nowMs - statsAt) / 1000;
      statsAt = nowMs;
      const info = renderer.info.render;
      const fm = frozenRef.current, gr = grassRef.current;
      const sz = renderer.getDrawingBufferSize(new THREE.Vector2());
      const lv = QUALITY_LEVELS[qLevel];
      const f = (n: number) => n >= 1e6 ? (n / 1e6).toFixed(2) + 'M' : n >= 1e3 ? (n / 1e3).toFixed(1) + 'k' : String(n);
      const on = (b: boolean) => (b ? 'on ' : 'OFF');
      dbg.textContent =
        `FPS ${(winFrames * 1000 / Math.max(winMs, 1)).toFixed(1)}   frame ${(winMs / Math.max(winFrames, 1)).toFixed(1)} ms   decode ${decodeMs.toFixed(1)} ms   queued ${frameQueueRef.current.length}\n` +
        `render ${sz.x}x${sz.y}   quality L${qLevel} (res x${lv.res.toFixed(2)}, grass ${(lv.grass * 100).toFixed(0)}%)   auto ${autoQuality ? 'on' : 'off'}\n` +
        `draw calls ${info.calls}   triangles ${f(info.triangles)}   geometries ${renderer.info.memory.geometries}   textures ${renderer.info.memory.textures}\n` +
        `trees: cells ${f(fm?.count ?? 0)}  wood ${f(fm?.woodCount ?? 0)}  sprigs ${f(fm?.sprigCount ?? 0)} (submitted ${f(fm?.sprigsDrawn ?? 0)})  bins ${fm?.binCount ?? 0}  chunks ${fm?.chunkCount ?? 0}\n` +
        `exposure gain ${exposure.toExponential(1)}   stars ${starFieldRef.current?.count ?? 0}   sun msgs ${(sunMsgCountRef.current / Math.max(secs, 1e-3)).toFixed(1)}/s   sim ${simTpsRef.current.toFixed(1)} tps\n` +
        `ground: rings ${clip.have().join(',') || 'none'}   drawn ${terrain.ringsDrawn}   windows taken ${f(clip.taken)}\n` +
        `${cells.describe()}\n` +
        `grass: ${f(gr?.tufts ?? 0)} tufts   canopy easing ${canopyShadeRef.current?.easing ? 'yes' : 'no '}   shadow refresh ${(shadowUpdatesWin / Math.max(secs, 1e-3)).toFixed(0)}/s\n` +
        `[1] grass ${on(show.grass)}  [2] trees ${on(show.trees)}  [3] shadows ${on(show.shadows)}  [4] rocks ${on(show.rocks)}  [7] cloud cells ${cellsMode}  [5] auto quality  [ ] quality  [F] flashlight`;
      shadowUpdatesWin = 0; winFrames = 0; winMs = 0; sunMsgCountRef.current = 0;
    };

    function animate() {
      animId = requestAnimationFrame(animate);
      perf.begin();
      if (spaceHidden.size) { for (const [o, v] of spaceHidden) o.visible = v; spaceHidden.clear(); }   // what the last frame hid of the near field comes back, so the code below sees the objects as it always did
      const dt = clock.getDelta();
      const nowMs = performance.now();
      tSec += dt;
      windRef.current?.update(dt, simTpsRef.current);
      frozenRef.current?.update(dt);
      perf.mark('wind and tree update');

      // Quality controller: step down when smoothed frame time is slow, back up after a stretch of
      // fast frames (vsync caps a healthy frame at about 16.7 ms, so "fast" means "holding 60").
      // A step back up that bounces straight down again doubles the calm period required next time.
      frameEma += (Math.min(dt * 1000, 60) - frameEma) * 0.08;
      winFrames++; winMs += dt * 1000;
      fpsFrames++; fpsMs += dt * 1000;
      if (fpsMs >= 500) { if (fpsRef.current) fpsRef.current.textContent = (fpsFrames * 1000 / fpsMs).toFixed(0); fpsFrames = 0; fpsMs = 0; }
      fpsClock += dt;
      if (autoQuality) {
        if (probing && fpsClock > 3) {
          probing = false;
          if (frameEma > 0.93 * probeFromMs && qLevel > 0) {
            qLevel--; applyQuality(); fpsClock = 0; calmFor = 0; holdDownUntil = tSec + 120;
          }
        } else if (frameEma > SLOW_FRAME_MS && qLevel < QUALITY_LEVELS.length - 1 && fpsClock > 1.5 && tSec >= holdDownUntil && !probing) {
          if (tSec - lastRaiseAt < 15) upWait = Math.min(60, upWait * 2);
          probing = true; probeFromMs = frameEma;
          qLevel++; applyQuality(); fpsClock = 0; calmFor = 0;
        } else if (frameEma < FAST_FRAME_MS && qLevel > 0) {
          calmFor += dt;
          if (calmFor > upWait && fpsClock > upWait) { qLevel--; applyQuality(); fpsClock = 0; calmFor = 0; lastRaiseAt = tSec; }
        } else {
          calmFor = 0;
        }
      }

      // Decode queued frames in order, within a time budget (a long backlog carries over to the
      // next animation frame rather than freezing this one). Only the newest frame decoded in this
      // batch updates the fixtures and sites; every frame's particle and frozen sections are applied.
      const queue = frameQueueRef.current;
      if (queue.length > 0) {
        const t0 = performance.now();
        let last: { buf: ArrayBuffer; view: DataView } | null = null;
        while (queue.length > 0) {
          const f = queue.shift()!;
          applyStreams(f.buf, f.view);
          last = f;
          if (performance.now() - t0 > FRAME_BUDGET_MS) break;
        }
        if (frozenDirty) { frozenRef.current?.flush(); frozenDirty = false; }
        if (last) { applyFixtures(last.buf, last.view); newFrameApplied = true; }
        decodeMs = performance.now() - t0;
      }
      perf.mark('quality control, frame decode');

      // Hover highlight: swap material color (MeshBasicMaterial has no emissive)
      const NEON_BASE    = new THREE.Color(0.90, 0.48, 0.15);
      const NEON_HOVER   = new THREE.Color(1.0, 0.82, 0.40);
      const currentHighlight = hoveredEntityIdRef.current;
      if (currentHighlight !== prevHighlightedId) {
        if (prevHighlightedId !== undefined) {
          const prevGroups = entityGroupsRef.current.get(prevHighlightedId);
          if (prevGroups) {
            for (const g of prevGroups) {
              g.traverse(obj => {
                if (obj instanceof THREE.Mesh) (obj.material as THREE.MeshStandardMaterial).color.copy(NEON_BASE);
              });
            }
          }
        }
        if (currentHighlight !== undefined) {
          const newGroups = entityGroupsRef.current.get(currentHighlight);
          if (newGroups) {
            for (const g of newGroups) {
              g.traverse(obj => {
                if (obj instanceof THREE.Mesh) (obj.material as THREE.MeshStandardMaterial).color.copy(NEON_HOVER);
              });
            }
          }
        }
        prevHighlightedId = currentHighlight;
      }

      // Hover from the crosshair while locked: report whatever the center ray hits.
      if (locked && nowMs - lastPickMs >= PICK_INTERVAL_MS) {
        lastPickMs = nowMs;
        const hid = pickFromCenter();
        if (hid !== prevHoveredFromCanvas) {
          prevHoveredFromCanvas = hid;
          onOrganismHoverRef.current?.(hid);
        }
      }
      perf.mark('hover highlight, center pick');

      // Zoom: ease toward the pinch target and set the field of view to magnify by that factor (a longer lens
      // scales the tangent of the half angle).
      if (zoom !== zoomT) {
        zoom += (zoomT - zoom) * (1 - Math.exp(-dt / ZOOM_EASE_S));
        if (Math.abs(zoomT - zoom) < 1e-3) zoom = zoomT;
        camera.fov = 2 * Math.atan(Math.tan(baseFov * Math.PI / 360) / zoom) * 180 / Math.PI;
        camera.updateProjectionMatrix();
      }

      // View direction: the dragged or mouse-turned angles plus the eased tilt offsets (zero unless tilt
      // is on, so the mouse path is unchanged).
      {
        const gk = 1 - Math.exp(-dt / GYRO_EASE_S);
        gyroYaw += wrapPi(gyroYawT - gyroYaw) * gk;
        gyroPitch += (gyroPitchT - gyroPitch) * gk;
        camera.rotation.set(space.flight.mode === 'free' ? pitch + gyroPitch : clampPitch(pitch + gyroPitch), yaw + gyroYaw, roll, 'YXZ');
      }

      // The eye's height above the nearest surface (the terrain under it; last frame's position is close enough): the free-roam speed and the shadow cascades' scale follow it.
      // Out in space (above the tile, or near another body: space.flight) it is the height above the followed body, so the speed keeps growing with it out to the moon's distance and falls
      // again on the way in, and there is no walking: the toggle for it holds until the eye is back on the tile.
      if (space.flight.mode === 'free' && !freeRoam) setFreeRoam(true);
      if ((space.flight.mode === 'free') !== inSpaceShown) { inSpaceShown = !inSpaceShown; setInSpaceHud(inSpaceShown); }
      const hSurf = space.flight.mode === 'free'
        ? Math.max(0, space.flight.altitude)
        : Math.max(0, camera.position.y - (terrainRef.current?.heightAtThree(camera.position.x, camera.position.z) ?? 0));
      const flyHeightK = Math.max(1, FLY_ANGULAR * hSurf / FLY_SPEED);
      sunShadows.setReach(Math.min(hSurf, FREE_ABOVE_M));         // (the cascades are for the tile; from above 60 km there is nothing for them to light)
      terrain.setShadowRings(Math.min(12, 7 + Math.round(Math.log2(sunShadows.scale))));     // the ground rings the cascades can reach (a ring's half side is 2.5 m * 2^ring, the cascades reach 300 m * their scale)

      // ── Camera: follow-cam when snapped, else FPS walk at fixed eye height ────
      const snapId = snapToEntityIdRef.current;
      // The simulation's tick now (simclock.ts): the observer's time when the thrusters are on, and where the server's bodies are.
      { const sr = sunRef.current; if (sr && sr.tick !== undefined) space.clock.note(sr.tick, sr.receivedAt ?? nowMs); space.clock.setRate(simTpsRef.current); }
      space.setBodies(bodiesMsgRef.current);
      const tickNow = space.clock.now(nowMs);
      let thrusters = false;                        // the thrusters are on this frame (flight.ts Drive)
      let driveDir: [number, number, number] = [0, 0, 0];
      let driveGain = flyK;
      if (snapId) {
        // Follow-cam: keep camera behind and above the snapped entity.
        const targetPos = entityPositionsRef.current.get(snapId);
        if (targetPos) {
          camera.getWorldDirection(_fwd);
          _fwd.y = 0;
          if (_fwd.lengthSq() > 0.001) _fwd.normalize(); else _fwd.set(0, 0, -1);
          camera.position.set(
            targetPos.x - _fwd.x * 12,
            targetPos.y + 6,
            targetPos.z - _fwd.z * 12,
          );
        }
      } else {
        // Free roam. Out in space the keys have two modes (G switches). INERTIAL: WASD fly along the camera's own axes and Space / C along its own up and down, like the mouse look; the observer
        // stays where the keys put it, with the followed body. THRUSTERS (the server's bodies and the tick are known): the observer is a body with a velocity under the bodies' pulls (flight.ts),
        // and the keys only say which way to push: WASD along the horizontal of the nearest body (the view's, projected on it, or the camera's own up when the view is along the vertical), Space
        // away from the body and C toward it, X brakes against it; Shift and the wheel scale the push. On the tile the keys are as they were, whichever mode is set.
        thrusters = space.flight.mode === 'free' && gravityMode && space.bodies.hasEphemeris && Number.isFinite(tickNow);
        if (freeRoam && locked) {
          const sprint = keys.has('ShiftLeft') || keys.has('ShiftRight') ? SPRINT_MULT : 1;
          camera.getWorldDirection(_fwd);
          const inSpace = space.flight.mode === 'free';
          const fb = (keys.has('KeyW') ? 1 : 0) - (keys.has('KeyS') ? 1 : 0), rl = (keys.has('KeyD') ? 1 : 0) - (keys.has('KeyA') ? 1 : 0), ud = (keys.has('Space') ? 1 : 0) - (keys.has('KeyC') ? 1 : 0);
          if (thrusters) {
            let cx = 0, cz = 0;
            if (fb !== 0 || rl !== 0) {
              _up.set(0, 1, 0).applyQuaternion(camera.quaternion);
              const cp = Math.sqrt(Math.max(0, 1 - _fwd.y * _fwd.y));
              let hx = _fwd.x * cp - _fwd.y * _up.x, hz = _fwd.z * cp - _fwd.y * _up.z;
              const hl = Math.hypot(hx, hz);
              if (hl > 1e-6) { hx /= hl; hz /= hl; cx = hx * fb - hz * rl; cz = hz * fb + hx * rl; }
            }
            const dl = Math.hypot(cx, ud, cz);
            if (dl > 1e-6) driveDir = [cx / dl, ud / dl, cz / dl];
            driveGain = flyK * sprint;
          } else {
            const stepRaw = FLY_SPEED * flyK * flyHeightK * sprint * dt;
            const step = inSpace ? Math.min(stepRaw, 0.5 * Math.max(hSurf, 1)) : stepRaw;   // in space, never more than half the height in one frame (a fast wheel setting would otherwise step through a body)
            _move.set(0, 0, 0);
            if (inSpace) { _right.set(1, 0, 0).applyQuaternion(camera.quaternion); _up.set(0, 1, 0).applyQuaternion(camera.quaternion); }
            else _right.crossVectors(_fwd, camera.up).normalize();
            _move.addScaledVector(_fwd, fb).addScaledVector(_right, rl);
            if (inSpace) _move.addScaledVector(_up, ud); else _move.y += ud;
            const total = _move.length();   // (the keys together make one unit direction, as they always did)
            if (total > 1e-6) camera.position.addScaledVector(_move, step / total);
          }
        }
        // FPS walk: translate along the flattened look direction, pinned to eye height.
        if (locked && !freeRoam) {
          const sprint = keys.has('ShiftLeft') || keys.has('ShiftRight') ? SPRINT_MULT : 1;
          const step = MOVE_SPEED * sprint * dt;
          camera.getWorldDirection(_fwd);
          _fwd.y = 0;
          if (_fwd.lengthSq() > 1e-6) _fwd.normalize();
          _right.crossVectors(_fwd, camera.up).normalize();  // right of the look direction
          _move.set(0, 0, 0);
          if (keys.has('KeyW')) _move.add(_fwd);
          if (keys.has('KeyS')) _move.sub(_fwd);
          if (keys.has('KeyD')) _move.add(_right);
          if (keys.has('KeyA')) _move.sub(_right);
          if (_move.lengthSq() > 1e-6) {
            _move.normalize().multiplyScalar(step);
            camera.position.x += _move.x;
            camera.position.z += _move.z;
          }
        }
        // Touch walk: the stick sets a target velocity (analog, no sprint) that the walking speed eases
        // toward: a gentle ramp up, a quick stop. The speed is divided by the zoom, because a magnified view
        // makes the same walk look that much faster, and that mismatch is a motion-sickness trigger.
        // Direction follows where the view points, tilt included. In free roam the stick flies along the full view
        // direction, so looking up and pushing forward climbs, and the speed is the flying speed.
        if (isTouch) {
          camera.getWorldDirection(_fwd);
          if (!freeRoam) _fwd.y = 0;
          if (_fwd.lengthSq() > 1e-6) _fwd.normalize();
          if (freeRoam && space.flight.mode === 'free') _right.set(1, 0, 0).applyQuaternion(camera.quaternion);
          else _right.crossVectors(_fwd, camera.up).normalize();
          const mag = Math.hypot(stickVX, stickVY);
          const touchSpeed = freeRoam ? (space.flight.mode === 'free' ? Math.min(FLY_SPEED * flyK * flyHeightK, 0.5 * Math.max(hSurf, 1) / Math.max(dt, 1e-3)) : FLY_SPEED * flyK * flyHeightK) : TOUCH_WALK_SPEED;
          let tR = 0, tF = 0;
          if (mag > TOUCH_DEADZONE) {
            const s = Math.min(1, (mag - TOUCH_DEADZONE) / (1 - TOUCH_DEADZONE));
            tR = (stickVX / mag) * s * touchSpeed / zoom;
            tF = (stickVY / mag) * s * touchSpeed / zoom;
          }
          // The easing is short on any axis whose speed is falling.
          const ekR = 1 - Math.exp(-dt / (Math.abs(tR) < Math.abs(walkR) ? TOUCH_STOP_TAU : TOUCH_ACCEL_TAU));
          const ekF = 1 - Math.exp(-dt / (Math.abs(tF) < Math.abs(walkF) ? TOUCH_STOP_TAU : TOUCH_ACCEL_TAU));
          walkR += (tR - walkR) * ekR;
          walkF += (tF - walkF) * ekF;
          if (Math.abs(walkR) + Math.abs(walkF) > 1e-3) {
            camera.position.x += (_fwd.x * walkF + _right.x * walkR) * dt;
            camera.position.z += (_fwd.z * walkF + _right.z * walkR) * dt;
            if (freeRoam) camera.position.y += _fwd.y * walkF * dt;
          }
        }
        const groundY = terrainRef.current?.heightAtThree(camera.position.x, camera.position.z) ?? 0;
        if (freeRoam) {
          // flying: stay out of the ground
          camera.position.y = Math.max(groundY + FLY_CLEARANCE, camera.position.y);
        } else if (landing) {
          // just left free roam: settle to walking height, then pin again
          const target = groundY + EYE_HEIGHT;
          camera.position.y += (target - camera.position.y) * (1 - Math.exp(-dt / LAND_TAU_S));
          if (Math.abs(target - camera.position.y) < 0.05) landing = false;
        } else {
          // pin eye height above the ground every frame (the ground is the terrain surface)
          camera.position.y = groundY + EYE_HEIGHT;
        }
      }
      // The observer among the bodies (space.ts). The sun message (smoothed) goes into the registry first; then the camera the controls have just moved is read as a place among the bodies.
      // On the tile that is a reading and nothing is written back; above FREE_ABOVE_M, or near another body, the place is the master, the controls' step is added to it, and the camera
      // (and the heading, when the axes it is measured in changed) is written from it.
      const sunServer = smoothSun(nowMs);
      space.feed(sunServer, sunRef.current?.flux ?? 1, planet.wanted > 0 ? planet.wanted : EARTH_RADIUS_M, spinRef.current?.latitudeDeg ?? NaN, starsMsgRef.current?.extinction, tickNow);
      if (snapId) { space.flight.reset(); roll = 0; }
      else {
        const drive: Drive | null = thrusters ? { tick: tickNow, dir: driveDir, gain: driveGain, brake: keys.has('KeyX') } : null;
        const fo = space.step(camera.position.x, camera.position.y, camera.position.z, yaw, pitch, roll, dt, drive);
        roll = fo.roll;
        if (fo.changed) {
          camera.position.set(fo.x, fo.y, fo.z);
          yaw = fo.yaw; pitch = fo.pitch;
          camera.rotation.set(space.flight.mode === 'free' ? pitch + gyroPitch : clampPitch(pitch + gyroPitch), yaw + gyroYaw, roll, 'YXZ');
        }
      }
      space.prepare();
      // The light at the eye. The sun message's numbers are the world's own point's; with the atmosphere's tables the star is where the registry sees it from the observer's place
      // (any height, any point of the planet) and the beam and the sky's light are read from the tables at that height and elevation (eyeLight.ts). Without them: the server's.
      const sun = observerSun(sunServer);
      water.setNearFade(space.nf);          // the sea grows transparent as the eye leaves the planet (the far shell's planet shows through)
      cloudVol.setNearFade(space.nfc);      // the cloud volume is whole while the tile's camera frame can draw it; where it gives way to the world painter's cloud is the composite's own (handover.ts)
      {
        // The ambient sound is a field in the world's frame; the player rotates it to where the camera faces (ambientAudio.ts). East is three's +x, north is -z.
        const ea = ambientRef.current;
        if (ea?.enabled) { camera.getWorldDirection(_earDir); ea.setHeading(Math.atan2(-_earDir.z, _earDir.x)); }
      }
      {
        // Tell the server where we stand, so it can place the cloud windows round us (windowrelay.py). The world's local frame is
        // x east, y north, z up: three's x, -z, y. At most twice a second, and only after moving 2 m, up or down as well (NaN, the first time, compares false and sends).
        const wsL = wsRef.current, L = locSentRef.current, nowMs = performance.now();
        if (wsL && wsL.readyState === WebSocket.OPEN && nowMs - L.t >= 500) {
          // On the tile the camera's x and z are a place on it. Out in space they are not: the place is the registry's reading of the observer on the home planet's tile chart (space.chartPos:
          // east and north along the ground from the world's point, valid round the whole planet), which is also what the world painter's tiles are chosen by (worldwin.py).
          let lx = NaN, ly = NaN, lz = NaN, still = 2.0;
          if (space.flight.mode === 'tile') { lx = camera.position.x; ly = -camera.position.z; lz = camera.position.y; }
          else { const cp = space.chartPos(); if (cp) { lx = cp.east; ly = cp.north; lz = cp.h; still = 200.0; } }
          if (Number.isFinite(lx + ly + lz) && !(Math.hypot(lx - L.x, ly - L.y, lz - L.z) < still)) {
            try { wsL.send(JSON.stringify({ type: 'loc', pos: [+lx.toFixed(2), +ly.toFixed(2), +lz.toFixed(2)] })); } catch { /* socket not ready */ }
            locSentRef.current = { t: nowMs, x: lx, y: ly, z: lz };
          }
        }
      }
      {
        // Tell the server where we stand for the ground's height windows (groundwin.py): the tile metres and the eye's height above the ground, about every 0.25 m of movement. Only a viewer that says so is sent windows.
        const wsT = wsRef.current, T = terrSentRef.current, nowT = performance.now();
        if (wsT && wsT.readyState === WebSocket.OPEN && nowT - T.t >= TERR_EVERY_MS) {
          let tx = NaN, ty = NaN, th = NaN;
          if (space.flight.mode === 'tile') { tx = camera.position.x; ty = -camera.position.z; th = camera.position.y - (terrainRef.current?.heightAtThree(camera.position.x, camera.position.z) ?? 0); }
          else { const cp = space.chartPos(); if (cp) { tx = cp.east; ty = cp.north; th = cp.h; } }
          if (Number.isFinite(tx + ty + th) && (!Number.isFinite(T.x) || Math.hypot(tx - T.x, ty - T.y) >= TERR_STEP_M || Math.abs(th - T.h) >= Math.max(TERR_STEP_M, 0.1 * T.h))) {
            try { wsT.send(JSON.stringify({ type: 'terr', pos: [+tx.toFixed(2), +ty.toFixed(2), +Math.max(th, 0).toFixed(2)] })); } catch { /* socket not ready */ }
            terrSentRef.current = { t: nowT, x: tx, y: ty, h: th };
          }
        }
      }
      perf.mark('camera, walk');
      origin.update(camera.position.x, camera.position.y, camera.position.z);                   // before anything that draws counts from it (the water, the clouds, the air)
      // The camera's planes follow the eye. The far plane has to reach the farthest thing drawn about it: the sea, the horizon disc and the dome follow the eye in x and z, so that is the eye's
      // height above them and their radius; at 2.5 km up the old fixed plane (worldMax * 20) cut the sea and the ground away. It grows in doublings, so the shadow cascades (csmShadows.ts refits
      // when the planes change) are redone only now and then, and it is the old plane for any eye below about a kilometre. The near plane keeps the depth buffer's ratio of near to far.
      // The reach also grows with the height itself (REACH_PER_HEIGHT): the sea is a flat plane to the far plane, and its edge must sit at the horizon, not at a fixed dip below it.
      // The sea, the ground apron and the horizon cutter then all cover what the camera can see, whatever the height, and have no edge in view. Near the ground nothing changes (the
      // base plane is the larger of the two).
      let depthGap = 0;                                         // how far under the sea the ground apron and the horizon cutter sit (they would flicker through it at the far plane's coarse depth steps)
      {
        // (capped: from 200 km up the near field is on its way out and the far shell, which has no depth, draws the rest, so the planes need not follow the height any further)
        const hAbove = Math.min(Math.abs(camera.position.y - HORIZON_Y), 2e5);
        const reach = Math.max(Math.hypot(hAbove, SKY_R * 1.5) * 1.1 + 50, REACH_PER_HEIGHT * hAbove);
        let farWant = worldMax * 20;
        while (farWant < reach) farWant *= 2;
        // The near plane follows the far one (the depth buffer's ratio), but never reaches past what the ground under the eye allows: the far plane grows with the eye's height above the sea, and on real
        // land that is hundreds of metres or kilometres up, which pushed the near plane out to metres and cut away the ground under the observer. Here it stays within NEAR_PER_GROUND_HEIGHT of the
        // eye's height above the ground (in doublings from CAMERA_NEAR, so the shadow cascades are refitted only now and then). Away from the tile (flying in space) the old rule stands.
        let nearCap = Infinity;
        if (space.flight.mode === 'tile' && terrain.loaded) {
          const hGround = Math.max(0, camera.position.y - terrain.heightAtThree(camera.position.x, camera.position.z));
          nearCap = CAMERA_NEAR;
          while (nearCap * 2 <= NEAR_PER_GROUND_HEIGHT * hGround) nearCap *= 2;
        }
        const nearWant = Math.min(CAMERA_NEAR * farWant / (worldMax * 20), Math.max(CAMERA_NEAR, NEAR_CAP_M), nearCap);
        if (farWant !== camera.far || nearWant !== camera.near) {
          camera.near = nearWant;
          camera.far = farWant; camera.updateProjectionMatrix();
        }
        // Every point the camera can see is within this of the eye on the ground plane, whichever way it looks (the frustum's far corner), so a square of this half side has no visible edge.
        const th = Math.tan(camera.fov * Math.PI / 360);
        const seaR = camera.far * Math.sqrt(1 + th * th * (1 + camera.aspect * camera.aspect));
        water.setReach(seaR);
        horizonMesh.scale.setScalar(Math.max(1, seaR / (SKY_R * 1.5)));
        depthGap = DEPTH_GAP_RATE * Math.max(0, hAbove - 50);
        ground.position.y = -depthGap;
      }
      grassRef.current?.update(camera.position);
      frozenRef.current?.setView(camera.position);
      perf.mark('grass and tree view update');

      // ── Sun: drive light, sky, exposure and the sun disc from sun state ──────
      if (sun) {
        // The server publishes the sun's LOCAL direction (this world's frame). Sim
        // direction (dx, dy, dz), dz = up, maps to three (dx, dz, -dy).
        const [dx, dy, dz] = sun.direction;
        const tdx = dx, tdy = dz, tdz = -dy;
        _sunDir.set(tdx, tdy, tdz);
        // The light at the eye: the tables' (observerSun, eyeLight.ts) taken through the cloud volume that is drawn (skyLight.ts): the cloud's optical depth on the sun's ray and over
        // the dome is the picture's own. Throttled inside (a step every 100 ms, a few tenths of a millisecond), eased between steps. Until the tables have come, the server's numbers.
        let dRgb: [number, number, number] = sun.directRgb;
        let sRgb: [number, number, number] = sun.skyRgb;
        volLight = false; moonVol = false;
        const atmMsg = sun.atm;
        if (atmMsg && sun.clearRgb) {
          _slEye[0] = camera.position.x; _slEye[1] = camera.position.y; _slEye[2] = camera.position.z;
          _slSun[0] = tdx; _slSun[1] = tdy; _slSun[2] = tdz;
          // the moon, for the sky's shape: its glow is its share of the sky's light (the floor of the eye's light has its horizontal irradiance in it). Its beam is marched through the
          // volume (MoonIn.march); `mo.rgb` is the beam through the air and no cloud
          const mo = sun.moon, mSin0 = mo ? Math.max(0, mo.direction[2]) : 0;
          const mGlow = mo && lightTune.moon && mSin0 > 0
            ? Math.min(1, (0.2126 * mo.rgb[0] + 0.7152 * mo.rgb[1] + 0.0722 * mo.rgb[2]) * mSin0 / Math.max(0.2126 * sun.skyRgb[0] + 0.7152 * sun.skyRgb[1] + 0.0722 * sun.skyRgb[2], 1e-30)) : 0;
          if (mo) { _moonDirThree.set(mo.direction[0], mo.direction[2], -mo.direction[1]); _moonDirArr[0] = _moonDirThree.x; _moonDirArr[1] = _moonDirThree.y; _moonDirArr[2] = _moonDirThree.z; }
          if (skyLight.update(nowMs, cloudVol.lightView(nowMs), _slEye, _slSun, sun.elevation > 0, 0.5 * sun.angularDiameter,
              mo ? { dir: _moonDirArr, up: mSin0 > 0, ang: 0.5 * mo.angularDiameter, glow: mGlow, march: lightTune.moon && !!sun.moonOpenRgb } : null)) {
            skyLight.light(atmMsg, sun.clearRgb, Math.max(0, Math.sin(sun.elevation)), lightD, lightS);
            dRgb = lightD; sRgb = lightS; volLight = true;
            // The moon's beam through the picture's cloud: its beam through the air (mo.rgb, from the tables) times the transmittance on the ray to the moon. The eye's light has the moon's
            // horizontal irradiance in it with no cloud (eyeLight.ts), and the picture's cloud takes the same filter off it that the sun's gets (the sun's form: e + (1 - e) ftr).
            if (mo && mSin0 > 0 && sun.moonOpenRgb && lightTune.moon && skyLight.moonMarched) {
              const oR = sun.moonOpenRgb, eV = skyLight.moonBeam, tV = skyLight.moonTau;
              cloudOptics.beamToDiffuse(LIQUID, tV * mSin0, Math.max(mSin0, 0.08), _moonBtr);       // the beam the cloud scatters down, a colour
              for (let c = 0; c < 3; c++) { _moonRgb[c] = oR[c] * eV; lightS[c] = Math.max(0, lightS[c] + oR[c] * mSin0 * (eV + _moonBtr[c]! - 1)); }
              moonVol = true;
            }
          }
        } else skyLight.reset();

        // Exposure: the luminance of the eye's light sets the gain (eyeLum.ts): the sky's light plus the sun's beam, weighted by the cosine of a horizontal surface for the share of the
        // view that is planet and as a surface facing the star for the rest. On the ground that is the open-ground illumination (the sky plus the beam on a horizontal surface);
        // climbing it goes to the light of a surface facing the star, and in the planet's shadow (no beam in the tables) to the night's. The stars and the galaxy are seen against the
        // sky, so their gain is set from the sky's own light (`dark`).
        const sinEl = Math.max(0, Math.sin(sun.elevation));
        const eyeL = space.eyeLum(0.2126 * sRgb[0] + 0.7152 * sRgb[1] + 0.0722 * sRgb[2], 0.2126 * dRgb[0] + 0.7152 * dRgb[1] + 0.0722 * dRgb[2], Math.sin(sun.elevation));
        const lum = eyeL.surface;
        // Nautical twilight: nearly all the sky's light is a glow at the horizon on the sun's side, many times the mean the exposure is set
        // from, so it comes out far too bright and the dome with it. Give back some exposure by how concentrated the sky is (see sky.ts).
        glowRmsSm += (sky.glowRms - glowRmsSm) * Math.min(1, dt * 2);
        // the eye's light level, eased (see the note at ADAPT_DIM_S); a stalled frame (hidden tab) or the first one takes the light as it is
        const lumRaw = Math.max(lum, 1e-30);
        const lumDarkRaw = Math.max(eyeL.dark, 1e-30);
        if (lumPrevRaw > 0 && Math.abs(Math.log(lumRaw / lumPrevRaw)) > 0.3 && sun.elevation > 0) lightEvents.push({ t: Math.round(nowMs) / 1000, what: 'light', from: lumPrevRaw, to: lumRaw });
        if (lightEvents.length > 40) lightEvents.shift();
        lumPrevRaw = lumRaw;
        const easeLum = (sm: number, raw: number): number => {
          if (ADAPT_OFF || !(sm > 0) || !(dt < 2)) return raw;
          const tau = raw < sm ? ADAPT_DIM_S : ADAPT_BRIGHT_S;
          return Math.exp(Math.log(sm) + (Math.log(raw) - Math.log(sm)) * (tau > 0 ? 1 - Math.exp(-dt / tau) : 1));
        };
        lumSm = easeLum(lumSm, lumRaw);
        lumDarkSm = easeLum(lumDarkSm, lumDarkRaw);
        // The sky's light as the dome and the clouds see it: its LEVEL is eased, its colour is the light's own at every frame. Easing the three channels apart lets a change of the eye's
        // height (the blue thins first) leave the old colour behind in the clouds' ambient, whose field is divided by the light as it is now (ambientSh.ts): they stop cancelling and the
        // faces that do not look up come out tinted until the easing has caught up.
        const skyLRaw = 0.2126 * sRgb[0] + 0.7152 * sRgb[1] + 0.0722 * sRgb[2];
        if (ADAPT_OFF || skySm === null || !(skyLSm > 0) || !(dt < 2)) skyLSm = skyLRaw;
        else skyLSm += (skyLRaw - skyLSm) * (ADAPT_SKY_S > 0 ? 1 - Math.exp(-dt / ADAPT_SKY_S) : 1);
        const skyKeep = skyLRaw > 1e-30 ? skyLSm / skyLRaw : 1;
        skySm = [sRgb[0] * skyKeep, sRgb[1] * skyKeep, sRgb[2] * skyKeep];
        const twG = twilightGlowGain(glowRmsSm, sun.elevation);     // (the sky table's own glow: from a height it is the limb's)
        const adaptRaw = exposureGain(lumRaw) * twG;
        const adapt = exposureGain(lumSm) * twG;
        const adaptDark = exposureGain(lumDarkSm) * twG;
        lightDbg.lum = lum; lightDbg.lumSm = lumSm; lightDbg.adaptRaw = adaptRaw; lightDbg.adapt = adapt;
        lightDbg.sky = [sRgb[0], sRgb[1], sRgb[2]]; lightDbg.skySm = [skySm[0], skySm[1], skySm[2]];
        exposure = adapt;
        renderer.toneMappingExposure = BASE_EXPOSURE * adapt;

        // Light for shading: the beam is a directional light just outside the patch
        // (shadow-map placement only; rays are parallel), skylight is the ambient.
        // Three applies the cosine term itself, so the colour is the beam-normal value.
        const wbT = Math.min(1, Math.max(0, (sinEl - 0.02) / 0.2));
        const wbAmt = SCENE_WB * wbT * wbT * (3 - 2 * wbT);
        const wbE = [sRgb[0] + dRgb[0] * sinEl, sRgb[1] + dRgb[1] * sinEl, sRgb[2] + dRgb[2] * sinEl];
        const wbG = [0, 1, 2].map(i => 1 + wbAmt * (lum / Math.max(wbE[i], 1e-9) - 1));
        sceneWbInfo = `scene light: sun on ground (${dRgb.map((v: number) => v.toExponential(2)).join(', ')}), sky_rgb (${sRgb.map((v: number) => v.toExponential(2)).join(', ')}), mean on a horizontal surface blue/red ${(wbE[2] / Math.max(wbE[0], 1e-12)).toFixed(3)}, white balance ${wbAmt.toFixed(2)}, gains (${wbG.map(v => v.toFixed(3)).join(', ')})`;
        // Which light the cascades follow: the stronger beam on level ground, the sun or the moon (see moonLead). The moon's is its beam through the picture's cloud where the server sends moon_open_rgb (bodies) and the volume is on, else the server's (moon.rgb: through its cells, at the ground).
        const mo = sun.moon, mSin = mo && lightTune.moon ? Math.max(0, mo.direction[2]) : 0;
        if (!moonVol) for (let i = 0; i < 3; i++) _moonRgb[i] = mo ? mo.rgb[i] : 0;
        const mLum = mo && mSin > 0 ? (0.2126 * _moonRgb[0] + 0.7152 * _moonRgb[1] + 0.0722 * _moonRgb[2]) * mSin : 0;
        const sLum = sun.elevation > 0 ? (0.2126 * dRgb[0] + 0.7152 * dRgb[1] + 0.0722 * dRgb[2]) * sinEl : 0;
        moonLead = mLum > 0 && mLum > sLum * (moonLead ? 0.708 : 1.413);                      // +-0.15 in log10: a moon and a sun of about the same strength do not flip back and forth
        if (moonLead !== moonShown) { handK -= dt / 0.25; if (handK <= 0) { handK = 0; moonShown = moonLead; } }
        else handK = Math.min(1, handK + dt / 0.25);
        const sunI = !moonShown && sun.elevation > 0 ? LIGHT_GAIN * handK : 0;
        const moonI = moonShown && mo && mSin > 0 ? MOON_GAIN * handK : 0;
        if (moonShown && mo) {
          _moonDirThree.set(mo.direction[0], mo.direction[2], -mo.direction[1]);
          sunShadows.setSun(_moonDirThree, _moonRgb[0] * wbG[0], _moonRgb[1] * wbG[1], _moonRgb[2] * wbG[2], moonI);
        } else sunShadows.setSun(_sunDir, dRgb[0] * wbG[0], dRgb[1] * wbG[1], dRgb[2] * wbG[2], sunI);
        // The cloud's shadow of the body that casts: the rings of beam transmittance are drawn for its direction (the moon's when it leads), and every lit fragment scales the beam by its own (cloudShadow.ts)
        cloudShadow.update(renderer, camera, moonShown && mo ? _moonDirThree : _sunDir, (moonShown ? moonI : sunI) > 0 && space.atHome && !space.volGone, nowMs,
          moonShown && mo ? (moonVol ? skyLight.moonBeam : 1) : (volLight ? skyLight.beam : 1),        // (the transmittance at the eye the beam's colour already has in it)
          volLight ? skyLight.tauUp : 0,                                                                // (the cloud's optical depth straight up from the eye, for the sky's light)
          moonShown && mo ? [_moonRgb[0] * wbG[0] * moonI * mSin, _moonRgb[1] * wbG[1] * moonI * mSin, _moonRgb[2] * wbG[2] * moonI * mSin]
                          : [dRgb[0] * wbG[0] * sunI * sinEl, dRgb[1] * wbG[1] * sunI * sinEl, dRgb[2] * wbG[2] * sunI * sinEl]);   // (the beam's irradiance on level ground, in the lights' units)
        // while the moon is the beam, its direct light is not also in the ambient (the share of level ground's light it gives, never more than the sky had)
        for (let i = 0; i < 3; i++) _moonSub[i] = moonI > 0 && mo ? Math.min(_moonRgb[i] * mSin * handK, 0.98 * sRgb[i]) : 0;
        moonLift = moonShown ? handK : 0;
        moonInfo = `light that casts: ${moonShown ? 'moon' : 'sun'}${moonLead !== moonShown ? ' (handing over)' : ''}, level ground sun ${sLum.toExponential(2)} | moon ${mLum.toExponential(2)}`;
        ambient.color.setRGB((sRgb[0] - _moonSub[0]) * wbG[0], (sRgb[1] - _moonSub[1]) * wbG[1], (sRgb[2] - _moonSub[2]) * wbG[2]);
        ambient.intensity = AMBIENT_GAIN;
        // The same light with a direction: the sky's share from each way (the clear sky's shape through the cloud's gaps, the cloud's own scattered light where it is thick) and the
        // ground's below. A face turned up gets exactly what the flat ambient gave it; the others are lit by what they see. Needs the volume's light (the dome's rays).
        ambDir = false;
        if (volLight && lightTune.amb === 'dir' && atmMsg && sun.clearRgb) {
          // E: the ambient that is left; H: the irradiance the open ground gets, from that and from the beam that casts (the sun's, or the moon's)
          for (let i = 0; i < 3; i++) { _ambE[i] = (sRgb[i] - _moonSub[i]) * wbG[i] * AMBIENT_GAIN; _ambH[i] = _ambE[i] + (dRgb[i] * wbG[i] * sunI * sinEl) + (mo ? _moonRgb[i] * wbG[i] * moonI * mSin : 0); }
          skyLight.field(atmMsg, sun.clearRgb, sinEl, _ambE, _ambH, lightTune.ground, lightTune.amt, _ambSh);
          const co = ambProbe.sh.coefficients;
          for (let k = 0; k < 9; k++) co[k].set(_ambSh[3 * k], _ambSh[3 * k + 1], _ambSh[3 * k + 2]);
          ambProbe.intensity = 1; ambient.intensity = 0; ambDir = true;
          // the same light for the clouds, relative to a face turned up (their ambient colour keeps its level; see ambientSh.ts)
          for (let k = 0; k < 9; k++) for (let c = 0; c < 3; c++) _ambRel[3 * k + c] = _ambSh[3 * k + c] / Math.max(_ambE[c], 1e-30);
          sky.setAmbientField(_ambRel, lightTune.cloud);
        } else { ambProbe.intensity = 0; sky.setAmbientField(null, 0); }

        // Sky and fog from skylight, compressed to display range. At night the colour first goes
        // through the same scotopic shift as the lit surfaces, so sky and ground agree.
        // The sky works out the sky-view table for the observer (its height, the star's elevation at its eye) and returns what the horizon fades to (the sky just above the true
        // horizon, round the compass) in the same units as sky_rgb, so fog, background and distant land agree with the sky behind them. The star is where the registry puts it for
        // this eye (space.sunPlacement): the server's on the ground, the real one away from it. Nothing in it fades with the air: the table is the sky from wherever the eye is.
        const sp = space.sunPlacement([tdx, tdy, tdz], sun.angularDiameter * 0.5);
        _sunDirThree.set(sp.dir[0], sp.dir[1], sp.dir[2]);
        sky.enabled = space.atHome;
        const horizon = sky.update({
          sunDir: _sunDirThree, elevation: Math.asin(Math.min(1, Math.max(-1, sp.dir[1]))), skyRgb: skySm,
          flux: sunRef.current?.flux ?? 1, adapt, baseExposure: BASE_EXPOSURE, skyExposure: SKY_EXPOSURE, eyeH: space.pose && space.pose.body === 0 ? space.pose.h : 0,
          dt,
          moon: sun.moon ? { dir: _moonSkyDir.set(sun.moon.direction[0], sun.moon.direction[2], -sun.moon.direction[1]), rgb: sun.moon.rgb } : null,
        });
        perf.mark('sky.update');
        if (!space.nearOff) cells.update(camera, dt);          // after the dome: the cells share its uniforms (the light tables, the sun, the exposure)
        perf.mark('cloud cells update');
        if (space.nfc > 0 && !space.volGone) cloudVol.update();   // the volume (and its detail noise) drift with the cloud wind between messages
        cloudVol.mesh.visible = cloudVol.active && !space.volGone;   // (where the shell has every pixel of it (handover.ts) there is nothing to march or composite)
        perf.mark('cloud volume update');    // the cross-fade between volumes is redone on the CPU a few times a second (cloudVolume.ts, blendStep): a spike here is that
        {
          const skyView = scotopicShift([horizon.r, horizon.g, horizon.b], BASE_EXPOSURE * adapt);
          _skyColor.setRGB(
            1 - Math.exp(-SKY_EXPOSURE * adapt * skyView[0]),
            1 - Math.exp(-SKY_EXPOSURE * adapt * skyView[1]),
            1 - Math.exp(-SKY_EXPOSURE * adapt * skyView[2]),
          );
        }
        (scene.background as THREE.Color).copy(_skyColor);
        scene.fog!.color.copy(_skyColor);
        // The sky drawn into a small cube map from the eye (sky.ts): the sea reflects and fogs into it, and the stars and the
        // Milky Way read the cloud in front of them from its alpha, so cloud hides them and the gaps between show them.
        perf.mark('sun, light, fog colours');
        // The probe holds the sky seen from the eye, which changes slowly and does not depend on where the camera points: it is redrawn about once a second (PROBE_PERIOD_S), or when the eye has moved far.
        // The probe is what the sea fogs into and reflects, and what the stars, the galaxy and the disc read the cloud in front of them from. The sky pass is on its layer, so it holds the
        // air's light from wherever the eye is, and the cloud volume when that is drawn.
        const probeUsed = space.atHome;
        if (probeUsed) sky.updateProbe(renderer, scene, camera.position, PROBE_PERIOD_S);
        perf.mark('sky probe');
        if (space.nfc > 0 && !space.volGone) cloudVol.renderView(renderer, camera);   // the cloud volume's march, into its small target (the composite is drawn in the main render); not while it is gone
        perf.mark('cloud volume (small)');
        // (out of the air the cloud is below the eye: nothing is behind it, and the probe is a picture from a place the eye has left)
        const probeTex = sky.probeReady && probeUsed ? sky.probe.texture : null;
        const cloudTex = probeTex;
        starField.setCloudProbe(cloudTex);
        galaxyGlow.setCloudProbe(cloudTex);
        water.setSkyProbe(probeTex);
        moonView.setCloudProbe(cloudTex);
        // The stars and the Milky Way, drawn again into a small cube of their own (after the dome's probe, whose alpha hides them behind cloud) when the dome's probe was
        // redrawn: at night only, and only while the sea is shown. The sea and the pools reflect it.
        starProbe.update(renderer, scene, camera.position, sky.probeSerial, water.mesh.visible && !space.nearOff);
        water.setStarProbe(starProbe.active ? starProbe.target.texture : null);
        perf.mark('star probe');

        // Sun disc at infinity: camera-locked, angular radius from the server. Colour is
        // the beam's hue (normalised); held while the beam is 0 so the last sliver of
        // limb at the horizon keeps its colour. Visible while the upper limb is up.
        const dMax = Math.max(dRgb[0], dRgb[1], dRgb[2]);
        if (dMax > 1e-12) lastDisc.setRGB(dRgb[0] / dMax, dRgb[1] / dMax, dRgb[2] / dMax);
        // Where the star is and how big: the server's direction and size on the ground, the registry's away from it (space.ts), so flying to the star it grows as it should; a planet's limb can hide part of it.
        const angR = sp.angR;
        // (the disc is a sphere at SKY_R: its radius stays under SKY_R, which is about 42 degrees across the star's half-angle, so one that fills the sky still draws)
        const orbR = SKY_R * Math.min(Math.tan(angR) * tune.sunSize, 0.9);
        const ox = camera.position.x + sp.dir[0] * SKY_R;
        const oy = camera.position.y + sp.dir[1] * SKY_R;
        const oz = camera.position.z + sp.dir[2] * SKY_R;
        sunOrb.position.set(ox, oy, oz);
        sunOrb.scale.setScalar(orbR);
        // The disc is nearly white even in a warm beam (it is far brighter than the screen can show);
        // the glow keeps more of the hue.
        // The cloud in the sun's own direction decides how much of the disc and glare shows (sky.sunCloudT); squared for the disc, because the sRGB output curve lifts small values and a disc behind thick cloud should vanish.
        // How much of the sun shows through the cloud in front of it. When the server worked the beam out through the cloud cells over this place, that
        // is the beam over the clear beam (the cells' optical depth on the ray and the high layer), the same cloud that lights the ground, with no
        // read-back from the picture and no lag; the curve is the one the picture's alpha went through. Otherwise (no cells) it is what the dome draws.
        // The disc and the glare read the cloud in front of them themselves, per pixel, from the sky probe (which holds the volume). Without the volume the cells' beam says how much
        // of the sun shows (the beam over the clear beam: the cloud that lights the ground); with neither, what the dome estimated.
        // The cells' beam over the clear beam is the cloud that lights the ground over this very place: when it is dim the sun cannot be seen, whatever the picture holds. It is the
        // ceiling on the disc and the glare; the probe (the cloud as drawn, per pixel) can only take more off.
        const cellsTr = volLight && sun.clearRgb      // (the volume's march starts at the eye: cloud below it is not on the ray to the star, so there is nothing to guard against from above the cloud)
          // (with the volume's light, dRgb is the beam through the picture's own cloud: the disc and the ground then share one cloud)
          ? (() => {
              const cl = 0.2126 * sun.clearRgb![0] + 0.7152 * sun.clearRgb![1] + 0.0722 * sun.clearRgb![2];
              const tr = cl > 1e-9 ? Math.min(1, Math.max(0, (0.2126 * dRgb[0] + 0.7152 * dRgb[1] + 0.0722 * dRgb[2]) / cl)) : 1;
              return THREE.MathUtils.smoothstep(tr, 0.3, 0.9);
            })()
          : null;
        const cellsOnly = !cloudVol.active && cellsTr !== null;
        let seenCpu = cellsTr ?? (sky.probeReady && cloudVol.active ? 1 : sky.sunTransmit);
        let sunProbe = !cellsOnly && sky.probeReady ? sky.probe.texture : null;
        // The panel's switch for what hides the disc (to find which of the two is wrong): both (the app), the probe alone, the server's beam alone, never hidden, always hidden.
        switch (tune.seen) {
          case 'probe': seenCpu = sky.probeReady && cloudVol.active ? 1 : (cellsTr ?? sky.sunTransmit); break;
          case 'server': sunProbe = null; seenCpu = cellsTr ?? sky.sunTransmit; break;
          case 'always': sunProbe = null; seenCpu = 1; break;
          case 'never': sunProbe = null; seenCpu = 0; break;
        }
        if (!space.atHome) { sunProbe = null; seenCpu = 1; }          // away from the home planet no cloud is between the eye and the star: only a planet can hide it (sp.hidden)
        if (tunePanel.isOpen) {
          discDbg.server = cellsTr;
          discDbg.probe = sky.probeReady ? sky.sunCloudT(renderer, _sunDirThree) : -1;
          discDbg.final = Math.min(seenCpu, sunProbe ? Math.max(0, discDbg.probe) : 1);
          discDbg.note = cellsOnly ? 'cells only (no volume)' : sunProbe ? 'probe' : 'no probe';
        }
        sunOrbMat.uniforms.uColor.value.copy(lastDisc).lerp(_white, 0.5);
        sunOrbMat.uniforms.uSrgbOut.value = srgbOut ? 1 : 0;
        sunOrbMat.uniforms.uCloudT.value = sunProbe; sunOrbMat.uniforms.uCloudOn.value = sunProbe ? 1 : 0; sunOrbMat.uniforms.uSeen.value = seenCpu * (1 - sp.hidden);
        sunGlare.setCloud(sunProbe, seenCpu * (1 - sp.hidden));
        glareColor.copy(lastDisc).lerp(_white, 0.25);
        // Glare grows with how bright the beam is (a reddened, dim low sun glows less).
        const dLum = 0.2126 * dRgb[0] + 0.7152 * dRgb[1] + 0.0722 * dRgb[2];
        // The glare is the sun's own strength seen through the cloud we draw in front of it (taken off per pixel in the shader). dRgb already has the cloud over this
        // place in it, so using it here would take the cloud off twice: the glare is set from the clear beam when the server sends one.
        const cRgb = sun.clearRgb ?? dRgb;
        // (the clear beam at the eye is the star's strength seen through the air above the eye, from the tables: out of the air it is the star's own)
        const cLum = 0.2126 * cRgb[0] + 0.7152 * cRgb[1] + 0.0722 * cRgb[2];
        sunBeamLum = dLum * sinEl;
        const vis = sp.away ? sp.hidden < 1 : sun.elevation > -angR;      // (away from the ground the planet's own disc decides, not the ground's horizon)
        sunOrb.visible = vis;
        sunGlare.update(camera.position, ox, oy, oz, orbR, glareColor, 0.8 * (cLum / (cLum + 0.02)) * tune.glare * (1 - sp.hidden), vis, srgbOut);   // (the cloud in front of it is taken off per pixel, in the shader)
        horizonMesh.position.set(camera.position.x, HORIZON_Y - depthGap, camera.position.z);

        // The moon's glare, the catalog stars and the far shell (the moon, other planets), placed from where the observer is (space.ts). The stars and the galaxy are hidden where the planet or
        // the moon covers them and dimmed by the air along the ray out of the eye (farShell.ts AIR_COLUMN_GLSL), the way the elevation test and the airmass did on the ground.
        // With the probe the cloud in each star's own direction does the dimming; the coverage-wide factor is the fallback until it is drawn.
        const cloudK = !space.atHome || sky.probeReady ? 1 : sky.starVisibility;       // (with the probe the cloud in each star's own direction does it; the coverage-wide factor is the fallback)
        const starGain = adapt * cloudK;                          // lit surfaces (the moon, the far bodies)
        const skyStarGain = adaptDark * cloudK;                   // things seen against the sky (stars, the galaxy): set from the sky's own light
        moonView.update(camera.position, space.moonGlare(sun.moon ? sun.moon.rgb : null), starGain, srgbOut);
        const drawH = renderer.getDrawingBufferSize(_drawSize).y;
        const sv = space.starView(), ss = space.starSpace();
        const bodyTr = space.atHome && sky.mesh.visible ? sky.transTexture : null;          // the air between the eye and a star: the sky-view table's transmittance (bodyAir.ts)
        starField.setSpace(ss.home, ss.occ);
        galaxyGlow.setSpace(ss.home, ss.occ);
        starField.setAir(bodyTr, sky.horizonElevation);
        galaxyGlow.setAir(bodyTr, sky.horizonElevation);
        starField.update(camera,
          sv ?? (sun.worldPos && sun.frame ? { worldPos: sun.worldPos, frame: sun.frame } : null),
          skyStarGain, tSec, drawH, srgbOut, renderer.getPixelRatio());
        galaxyGlow.update(camera,
          sv ? { frame: sv.frame } : sun.worldPos && sun.frame ? { frame: sun.frame } : null,
          skyStarGain, srgbOut, renderer);       // renderer: the galaxy's baked structure is made on the first draw (the band is invisible below GALAXY_DARK_LO)
        space.paint(SKY_EXPOSURE * starGain, srgbOut, space.atHome && sky.probeReady ? sky.probe.texture : null, 2 * Math.tan(camera.fov * Math.PI / 360) / Math.max(drawH, 1), sky.mesh.visible ? sky.transTexture : null, sky.horizonElevation);
        perf.mark('sun disc, moon, stars, galaxy update');
      }

      // ── Sea: tide, wet sand, and the underwater look ──────────────────────────
      // Everything here is cosmetic and client-side. The tide comes from the sun and moon directions in
      // the sun message (tide.ts); the wet band on the sand follows the tide up at once and down slowly;
      // with the eye under the surface the fog and sky become the colour of the water.
      {
        const tv = terrainRef.current;
        if (tv && tv.hasSea) {
          // The tide is the server's (from its own sun and moon; every viewer and the physics see the same water). A server that sends no water
          // message leaves the local formula (tide.ts) in charge.
          tideState.update(dt);
          const tide = tideState.hasState ? tideState.tide : (sun ? tideOffset(sun.direction, sun.moon ? sun.moon.direction : null, tv.tideAmplitude) : 0);
          const seaY = tv.seaLevel + tide;
          wetY = Math.max(seaY, wetY + (seaY - wetY) * (1 - Math.exp(-dt / WET_DRY_TAU_S)));
          tv.setWetLevel(wetY + 0.6 * water.runup);       // the swash wets the sand a little above the still level
          grass.setDryLine(tv.seaLevel + tv.tideAmplitude + SEA_DRY_MARGIN);
          const eyeDepth = seaY - camera.position.y;
          const uw = THREE.MathUtils.clamp(eyeDepth / UNDER_FADE_M, 0, 1);
          _underCol.copy(_skyColor).multiply(_underTint);
          water.setEnabled(true);
          water.setShore(clip.shore(), tv.seaLevel);       // the beach waves' phase, solved over the shore window the server sent (and again when a new one comes)
          // Sea state from the simulated wind: speed (the server's m/s at a push of 1, times the live push and a 10 m mean factor) and heading,
          // relaxed over tens of seconds so a gust front or a storm builds the swell up and lets it die away instead of snapping it.
          {
            const wu = wind.uniforms;
            // Swell: the server's trains (water message; waves made by far storms, from the global wind field), as headings in the three x-z plane (x east, z south).
            const wm = tideRef.current.state;
            const swell = wm ? (wm.swell ?? []).map((t) => ({ hs: t.hs, tp: t.tp, dir: Math.atan2(-t.dir[1], t.dir[0]) })) : undefined;
            const target: Partial<SeaState> = { speed: (windMsgRef.current?.speed_ms ?? 10) * wu.uWindAmp.value * 0.65, dir: Math.atan2(wu.uWindDir.value.y, wu.uWindDir.value.x), ...(swell ? { swell } : {}) };
            if (!seaSnapped) { water.setSeaState(target); seaSnapped = true; } else water.relaxSea(target, dt);
            water.simulate(renderer, tSec, dt);                        // the wave FFT, before the frame is drawn
            water.setRain(_rainDiscs, cells.rainDiscs(_rainDiscs, 6)); // raining discs near the eye ring the water
          }
          // Glitter: each light's strength in the sky curve's units, 1 - exp(-K * ...) in the shader.
          if (sun) {
            const kg = SKY_EXPOSURE * exposure;
            const gb = volLight ? lightD : sun.directRgb;
            _glitSunK[0] = kg * gb[0]; _glitSunK[1] = kg * gb[1]; _glitSunK[2] = kg * gb[2];
            const m = sun.moon;
            if (m) {
              _glitMoonDir.set(m.direction[0], m.direction[2], -m.direction[1]);     // sim (x, y, z) -> three (x, z, -y)
              _glitMoonK[0] = kg * m.rgb[0]; _glitMoonK[1] = kg * m.rgb[1]; _glitMoonK[2] = kg * m.rgb[2];
            }
            water.update(camera.position, seaY, tSec, _skyColor, eyeDepth > 0, _underCol,
              { sunDir: _sunDir, sunK: _glitSunK, moonDir: m ? _glitMoonDir : null, moonK: _glitMoonK });
          } else {
            water.update(camera.position, seaY, tSec, _skyColor, eyeDepth > 0, _underCol);
          }
          air.update(dt, camera.position, seaY, uw);                                  // the air in front of everything: off under the sea, where the fog below is the medium
          const fog = scene.fog as THREE.FogExp2;
          fog.density = THREE.MathUtils.lerp(FOG_AIR_DENSITY, FOG_UNDER_DENSITY, uw);
          if (uw > 0) {
            _fogMix.copy(_skyColor).lerp(_underCol, uw);
            fog.color.copy(_fogMix);
            (scene.background as THREE.Color).copy(_fogMix);
          }
          sky.setUnderwater(_underCol, uw);
        } else {
          water.setEnabled(false);
          grass.setDryLine(-1e6);
          air.update(dt, camera.position, 0, 0);
        }
      }
      perf.mark('sea');

      // Flashlight: fade, follow the camera, and lift the canopy's sun filter off the direct term once the
      // sun's beam is gone (so the beam is not dimmed by shade it is not subject to).
      {
        const goal = flashWanted ? 1 : 0;
        flashLevel += (goal - flashLevel) * (1 - Math.exp(-dt / FLASH_FADE_S));
        if (Math.abs(goal - flashLevel) < 0.002) flashLevel = goal;
        if (flashLevel > 0 && !flashInScene) { scene.add(flash); scene.add(flash.target); flashInScene = true; }
        else if (flashLevel === 0 && flashInScene) { scene.remove(flash); scene.remove(flash.target); flashInScene = false; }
        if (flashInScene) {
          camera.updateMatrixWorld();
          camera.getWorldDirection(_flashFwd);
          flash.position.set(0.18, -0.14, -0.05).applyMatrix4(camera.matrixWorld);
          flash.target.position.copy(camera.position).addScaledVector(_flashFwd, 6);
          flash.target.updateMatrixWorld();
          flash.intensity = FLASH_INTENSITY * flashLevel;
          if (!perLightShadows) renderer.shadowMap.needsUpdate = true;
        }
        canopyShade.uniforms.uCanopyFlash.value = Math.max(flashLevel * (1 - THREE.MathUtils.smoothstep(sunBeamLum, 2e-5, 2e-3)), moonLift);   // (plants do not shade moonlight: their light volume is the sun's)
      }
      perf.mark('flashlight');

      // Shadow maps: a cascade is redrawn when something that casts changed (a new server frame moved the body, a message changed
      // the terrain or roster), when the sun has moved enough to matter, or when its window has slid a texel with the camera; the
      // far cascade is also refreshed every SHADOW_REFRESH_MS. Otherwise the last maps are reused. The call goes after the
      // camera has its final pose and before the render.
      if (newFrameApplied || shadowDirtyRef.current) { sunShadows.markDirty(); newFrameApplied = false; shadowDirtyRef.current = false; }
      if (!space.nearOff) shadowUpdatesWin += sunShadows.update(nowMs, SHADOW_REFRESH_MS);     // the overlay's "shadow refresh" is now cascade redraws per second (nothing of the tile is drawn from high up)
      perf.mark('shadow cascade update');
      canopyShadeRef.current?.update(renderer, dt);     // eases the drawn canopy volume toward the newest one
      perf.mark('canopy volume update');
      terrainRef.current?.update(camera);   // the ground windows that arrived since the last frame, into the textures and the rings' index buffers; the rings out of view are left out
      ecoViewRef.current?.update(camera.position);   // the plant windows that arrived, into meshes on that ground (after the ground has taken its windows)
      perf.mark('ground rings');
      // The near field out of sight (space.ts). The terrain, the props, the grass, the trees, the cells and the rest of the tile are hidden from NEAR_HIDE_M up and at any other body; the sea and the
      // cloud composite stay until they have faded out (they are transparent by then); the sky pass is never hidden. The sky shell (stars, galaxy, sun, glare, the far shell) is never hidden. What
      // is hidden here is put back at the start of the next frame, so everything above sees the objects as it always did.
      if (space.nearOff) {
        for (const o of scene.children) {
          if (!o.visible || (o as THREE.Light).isLight) continue;
          let hide: boolean;
          if (o === sky.mesh || o === starField.object || o === galaxyGlow.mesh || o === moonView.halo || o === sunOrb || o === sunGlare.mesh || o === space.shell.mesh || o === space.cloud.mesh || o === lattice.group) hide = false;
          else if (o === water.mesh) hide = space.nearOff && space.nf <= 0;
          else if (o === cloudVol.mesh) hide = space.nearOff && space.nfc <= 0;
          else hide = space.nearOff;
          if (hide) { spaceHidden.set(o, true); o.visible = false; }
        }
      }
      // The navigation overlay and the gravitational lattice (nav.ts, lattice.ts): out in space only, and only when asked for. Both read the pose and the camera as they now stand.
      {
        const inFree = space.flight.mode === 'free' && !snapId;
        camera.updateMatrixWorld();
        const li = latticeOn && inFree ? latticeInputs(space.bodies, space.flight, tickNow) : null;
        if (li) lattice.update(true, camera, li.axes, li.obs, li.wells, li.home, li.scale); else lattice.hide();
        const hud = navHudRef.current, marks = navMarksRef.current;
        if (hud && marks) {
          if (!navOn || !inFree) { if (hud.style.display !== 'none') { hud.style.display = 'none'; marks.style.display = 'none'; } }
          else {
            const info = navInfo(space.bodies, space.flight, tickNow, true);
            if (!info) { hud.style.display = 'none'; marks.style.display = 'none'; }
            else {
              hud.style.display = ''; marks.style.display = '';
              if (nowMs - navAt >= 100) {
                navAt = nowMs;
                const o = info.orbit;
                const orbitLine = !o ? 'orbit    (thrusters off: the keys move the camera)'
                  : !o.bound ? `orbit    escape (e ${o.e.toFixed(2)}), closest ${fmtDist(o.periAlt)}`
                  : `orbit    ${fmtDist(o.periAlt)} x ${fmtDist(o.apoAlt)}   e ${o.e.toFixed(3)}   T ${fmtTime(o.periodS)}`;
                hud.textContent =
                  `NAV   ${info.dynamic ? 'THRUSTERS' : 'INERTIAL'}   pull ${info.pullBody} ${info.pull >= 0.1 ? info.pull.toFixed(2) : info.pull.toExponential(1)} m/s2\n` +
                  `alt      ${fmtDist(info.alt)}${info.speed !== null ? `   speed ${fmtSpeed(info.speed)}` : ''}\n` + orbitLine + '\n' +
                  (info.dynamic ? `thrust   ${info.thrust >= 0.1 ? info.thrust.toFixed(1) : info.thrust.toExponential(1)} m/s2   (wheel scales, X brakes)\n` : '') +
                  (info.nearest ? `nearest  ${info.nearest.name}  ${fmtDist(info.nearest.dist)}` : '');
              }
              // Lagrange points: a ring on the sky where each is, with its name and distance (a direction and a size, as the far bodies are)
              const w = renderer.domElement.clientWidth, h = renderer.domElement.clientHeight;
              while (marks.childElementCount < info.marks.length) {
                const d = document.createElement('div');
                d.style.cssText = 'position:absolute;left:0;top:0;white-space:nowrap;font:10px "Space Mono",monospace;letter-spacing:0.05em;color:rgba(255,200,110,0.85);will-change:transform;';
                d.innerHTML = '<span style="display:inline-block;width:9px;height:9px;margin:-5px 4px 0 -5px;border:1px solid rgba(255,200,110,0.9);border-radius:50%;vertical-align:middle"></span><span></span>';
                marks.appendChild(d);
              }
              for (let i = 0; i < marks.childElementCount; i++) {
                const el = marks.children[i] as HTMLElement;
                const m = info.marks[i];
                if (!m) { el.style.display = 'none'; continue; }
                _navV.set(m.dir[0], m.dir[1], m.dir[2]).multiplyScalar(1000).add(camera.position).project(camera);
                if (_navV.z >= 1 || Math.abs(_navV.x) > 1.05 || Math.abs(_navV.y) > 1.05) { el.style.display = 'none'; continue; }
                el.style.display = '';
                el.style.transform = `translate(${((_navV.x * 0.5 + 0.5) * w).toFixed(1)}px,${((-_navV.y * 0.5 + 0.5) * h).toFixed(1)}px)`;
                if (nowMs - navAt < 1) (el.lastChild as HTMLElement).textContent = `${m.name}  ${fmtDist(m.dist)}`;
              }
            }
          }
        }
      }
      renderer.info.reset();                            // so the overlay's counts are this render alone
      renderer.render(scene, camera);
      perf.mark('render');

      if (dbgOn && nowMs - statsAt >= 500) updateOverlay(nowMs);
      perf.end();
    }
    animate();

    // ── WebGL context loss ───────────────────────────────────────────────────
    const handleContextLost = (e: Event) => {
      e.preventDefault(); // required to allow restoration
      cancelAnimationFrame(animId);
    };
    const handleContextRestored = () => {
      renderer.resetState();
      animate();
    };
    renderer.domElement.addEventListener('webglcontextlost',     handleContextLost);
    renderer.domElement.addEventListener('webglcontextrestored', handleContextRestored);

    // ── Resize ───────────────────────────────────────────────────────────────
    const resizeObs = new ResizeObserver(() => {
      camera.aspect = mount.clientWidth / mount.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mount.clientWidth, mount.clientHeight);
    });
    resizeObs.observe(mount);

    return () => {
      cancelAnimationFrame(animId);
      ambientRef.current?.dispose();
      ambientRef.current = null;
      setSoundOn(false);
      perf.dispose();
      tunePanel.dispose();
      resizeObs.disconnect();
      if (document.pointerLockElement === canvas) document.exitPointerLock();
      canvas.removeEventListener('mousedown', onCanvasMouseDown);
      canvas.removeEventListener('wheel', onWheel);
      document.removeEventListener('pointerlockchange', onPointerLockChange);
      document.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('keydown', onKeyDown);
      document.documentElement.removeAttribute('data-sim-ui-hidden');   // never leave the page hidden behind us
      window.removeEventListener('keyup',   onKeyUp);
      for (const fn of touchUiCleanup) fn();
      renderer.domElement.removeEventListener('webglcontextlost',     handleContextLost);
      renderer.domElement.removeEventListener('webglcontextrestored', handleContextRestored);
      // Clean up all entity groups
      for (const groups of entityGroupsRef.current.values()) {
        for (const g of groups) {
          g.traverse(obj => {
            if (obj instanceof THREE.Mesh) {
              obj.geometry.dispose();
              (obj.material as THREE.Material).dispose();
            }
          });
        }
      }
      entityGroupsRef.current.clear();
      slotToEntityRef.current.clear();
      sceneRef.current = null;
      fixturesRef.current = null;
      if (fruitRef.current) {
        scene.remove(fruitRef.current);
        fruitRef.current.geometry.dispose();
        (fruitRef.current.material as THREE.Material).dispose();
        fruitRef.current = null;
      }
      if (frondRef.current) {
        scene.remove(frondRef.current);
        frondRef.current.geometry.dispose();
        (frondRef.current.material as THREE.Material).dispose();
        frondRef.current = null;
      }
      _frondState.clear();
      fruitLiveRef.current.clear();
      cloudShadow.dispose();
      sunShadows.dispose();
      palmCastersRef.current?.dispose();
      palmCastersRef.current = null;
      frozenRef.current?.dispose();
      frozenRef.current = null;
      terrain.dispose();
      terrainRef.current = null;
      rocks.dispose();
      rocksRef.current = null;
      grass.dispose();
      grassRef.current = null;
      eco.dispose();
      ecoViewRef.current = null;
      water.dispose();
      waterRef.current = null;
      canopyShade.dispose();
      canopyShadeRef.current = null;
      windRef.current = null;
      scene.remove(cells.group);
      cells.dispose();
      cloudCellsRef.current = null;
      delete (window as any).cloudCells;
      scene.remove(cloudVol.mesh);
      cloudVol.dispose();
      cloudVolRef.current = null;
      delete (window as any).cloudVolume;
      sky.dispose();
      skyRef.current = null;
      fixGeo.dispose();
      fixMat.dispose();
      siteGeo.dispose();
      groundGeo.dispose();
      groundMat.dispose();
      sunOrbGeo.dispose();
      sunOrbMat.dispose();
      sunGlare.dispose();
      starProbe.dispose();
      starField.dispose();
      starFieldRef.current = null;
      galaxyGlow.dispose();
      galaxyRef.current = null;
      moonView.dispose();
      space.dispose();
      lattice.dispose();
      horizonGeo.dispose();
      horizonMat.dispose();
      dbg.remove();
      renderer.dispose();
      if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
    };
  }, []);

  if (webglUnavailable) {
    return (
      <div className="broadcast-offline">
        <p>WEBGL UNAVAILABLE</p>
        <p className="broadcast-offline-hint">Your browser or GPU does not support WebGL</p>
      </div>
    );
  }

  if (broadcastOffline) {
    return (
      <div className="broadcast-offline">
        <p>SIM OFFLINE</p>
        <p className="broadcast-offline-hint">The simulation tunnel is currently down</p>
        <button onClick={() => { setBroadcastOffline(false); connectBroadcast(); }}>RETRY</button>
      </div>
    );
  }

  return (
    <div ref={mountRef} style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
      {/* No crosshair is drawn: the pick still comes from the screen centre while locked (see the hover raycast), it is just not marked. */}
      {/* Touch hint (touch devices; hidden at the first touch). */}
      {isTouchDevice && (
        <div ref={touchHintRef} style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          fontFamily: '"Space Mono", monospace', fontSize: 11,
          color: 'rgba(150,210,255,0.6)', letterSpacing: '0.06em',
        }}>
          {([['WALK', 0, TOUCH_ZONE_LEFT], ['PINCH TO ZOOM', TOUCH_ZONE_LEFT, TOUCH_ZONE_RIGHT], ['TURN', TOUCH_ZONE_RIGHT, 1]] as const).map(([label, a, b]) => (
            <div key={label} style={{ position: 'absolute', bottom: 30, left: `${a * 100}%`, width: `${(b - a) * 100}%`, textAlign: 'center' }}>{label}</div>
          ))}
          <div style={{ position: 'absolute', bottom: 10, left: 10, right: 10, textAlign: 'center', opacity: 0.8 }}>
            ☰ MENU FOR FLASHLIGHT, TILT AND MORE
          </div>
        </div>
      )}
      {/* Landscape only on touch devices: cover the view while in portrait. */}
      {isTouchDevice && isPortrait && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 20, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(5,7,15,0.94)', textAlign: 'center', padding: 24,
          fontFamily: '"Space Mono", monospace', fontSize: 13,
          color: 'rgba(150,210,255,0.85)', letterSpacing: '0.08em',
        }}>
          ROTATE YOUR DEVICE TO LANDSCAPE
        </div>
      )}
      {/* Control hint (only while unlocked, mouse and keyboard). */}
      {!isLocked && !isTouchDevice && (
        <div style={{
          position: 'absolute', bottom: 10, left: 10, pointerEvents: 'none',
          fontFamily: '"Space Mono", monospace', fontSize: 11,
          color: 'rgba(150,210,255,0.55)', letterSpacing: '0.06em',
        }}>
          CLICK TO LOOK · WASD MOVE · V FREE ROAM{isFreeRoam ? ' (ON)' : ''} · SHIFT SPRINT · H HIDE UI · M SOUND{soundOn ? ' (ON)' : ''} · ESC RELEASE
        </div>
      )}
      {/* Navigation overlay (N, out in space): the readout, and the Lagrange points on the sky. Both are written by the frame loop. */}
      <pre ref={navHudRef} style={{
        position: 'absolute', top: 40, left: 10, margin: 0, display: 'none', pointerEvents: 'none',
        fontFamily: '"Space Mono", monospace', fontSize: 11, lineHeight: 1.45, color: 'rgba(150,210,255,0.8)', letterSpacing: '0.04em', textShadow: '0 0 4px rgba(0,0,0,0.9)',
      }} />
      <div ref={navMarksRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', display: 'none' }} />
      {/* Free roam readout (locked, mouse and keyboard): the keys that only matter while flying, and the wheel's speed scale. */}
      {isFreeRoam && isLocked && !isTouchDevice && (
        <div style={{
          position: 'absolute', bottom: 10, left: 10, pointerEvents: 'none',
          fontFamily: '"Space Mono", monospace', fontSize: 11,
          color: 'rgba(150,210,255,0.55)', letterSpacing: '0.06em',
        }}>
          FREE ROAM{inSpaceHud ? (isGravity ? ' · G THRUSTERS: WASD ALONG THE SURFACE · SPACE AWAY · C TOWARD · X BRAKE' : ' · G INERTIAL: WASD + SPACE / C ALONG THE CAMERA') + ` · N NAV${isNav ? ' (ON)' : ''} · L LATTICE${isLattice ? ' (ON)' : ''}` : ' · SPACE UP · C DOWN'} · WHEEL SPEED x{flyScale >= 10 ? flyScale.toFixed(0) : flyScale.toFixed(flyScale < 1 ? 2 : 1)} · V LAND
        </div>
      )}
      <div style={{
        position: 'absolute', top: 8, right: 10, pointerEvents: 'none',
        fontFamily: '"Space Mono", monospace', fontSize: 11,
        color: 'rgba(150,210,255,0.6)', letterSpacing: '0.08em',
      }}>
        <span ref={clockRef} style={{ marginRight: 12 }}>--:--</span>
        TICK <span ref={tickDisplayRef}>—</span>
        <span style={{ marginLeft: 10, opacity: 0.7 }}>TPS <span ref={tickRateRef} style={{ display: 'inline-block', width: '4.5ch', textAlign: 'right' }}>—</span></span>
        <span style={{ marginLeft: 10, opacity: 0.7 }}>FPS <span ref={fpsRef} style={{ display: 'inline-block', width: '3ch', textAlign: 'right' }}>—</span></span>
      </div>
      <button
        type="button"
        onClick={toggleSound}
        title="Ambient sound (M)"
        style={{
          position: 'absolute', top: 30, right: 10, padding: '1px 8px', cursor: 'pointer', background: 'transparent',
          border: '1px solid rgba(150,210,255,0.35)', borderRadius: 3,
          fontFamily: '"Space Mono", monospace', fontSize: 11, letterSpacing: '0.08em',
          color: soundOn ? 'rgba(150,210,255,0.95)' : 'rgba(150,210,255,0.55)',
        }}
      >
        SOUND {soundOn ? 'ON' : 'OFF'}
      </button>
      {soundWhy && (
        <div style={{
          position: 'absolute', top: 54, right: 10, maxWidth: 320, textAlign: 'right', pointerEvents: 'none',
          fontFamily: '"Space Mono", monospace', fontSize: 10, color: 'rgba(255,190,150,0.9)',
        }}>
          NO SOUND: {soundWhy}
        </div>
      )}
    </div>
  );
}
